import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { recordDataOutEvent } from '../../services/rlsAudit';
import { useToast, Button as ChakraButton, IconButton, Textarea, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Box, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faTrash } from '@fortawesome/free-solid-svg-icons';
import { exportEditorToDocx } from '../../documentComponents/exportToDocx';
import DoctorPortalBar from '../../components/DoctorPortalBar';
import { useTodaysSchedule } from '../../hooks/medstudent/useTodaysSchedule';
import {
  usePatientDocument,
  resolveTemplateDoc,
  buildDocPath,
  effectiveTemplateCode,
  rowMatchesVisitParam,
  FOLLOW_UP_TYPES,
} from '../../hooks/medstudent/usePatientDocument';
import { listTemplateOptions, withStaleOption } from './templateCatalog';
import { TEMPLATE_FIELD } from '../../components/EditPatientModal';
import {
  firstEntryPromptKey,
  priorDocPromptDecision,
  isUntouchedDoc,
  isEmptyOps,
  withTimeout,
} from './priorDocPrompt';
import { fetchPriorVisitCandidates, formatPatientLabel } from '../../utils/priorVisitMatch';
import { saveCustomTemplate, normalizeTypeCode } from '../../hooks/medstudent/customTemplates';
import { useUserInfo } from '../../hooks/medstudent/useUserInfo';
import { useDocumentPresence } from '../../hooks/medstudent/useDocumentPresence';
import { useVersionCount } from '../../hooks/medstudent/useVersionCount';
import { useSignedStatus } from '../../hooks/medstudent/useSignedStatus';
import { createVersioningSystem, SIGNED_VERSION_PREFIX, POST_SIGN_EDIT_PREFIX } from '../../documentComponents/versioningSystem';
import SignAndSaveModal from './SignAndSaveModal';
import UnlockNoteModal from './UnlockNoteModal';
import { generateLockPassword } from '../../utils/generateLockPassword';
import { writeSignatureRecord } from '../../services/documentSignatures';
import { getSupabase, supabaseSchema, getSupabaseAdmin } from '../../components/supabaseClient';
import { mirrorWriteToRds } from '../../utils/rdsCutover';
import { uniqueChannelTopic } from '../../utils/realtimeChannel';
import { bucketName_files, documentBackupBucketName, documentBackupTableName, getBackendURL, loadConfig } from '../../components/env';
import * as envModule from '../../components/env';
import { MEDSTUDENT_LIVE_COLLAB } from '../../config/collab';
import { isFixtureUuid, fixtureEhrIdForUuid } from '../../utils/testPatients';
import { useDocumentCollab } from '../../hooks/medstudent/useDocumentCollab';
import { useRosPositivesBolding } from '../../hooks/medstudent/useRosPositivesBolding';
import CollabStatusBadge from './CollabStatusBadge';
import IconRail from './IconRail';
import PatientListPanel from './PatientListPanel';
import PatientHeaderBar from './PatientHeaderBar';
import DocumentToolbar from './DocumentToolbar';
import MedStudentRightPanel from './MedStudentRightPanel';
import { useAgents } from './agents/useAgents';
import PatientRightPanel from '../../components/patient/PatientRightPanel';
import ResizeSplitter from './ResizeSplitter';
import { isVisitCompleted } from './utils';
import { chartTable, chartSchema, isSchemaAllowed } from './chartSource';
import { switchSectionFormat, detectSectionFormat } from './switchSectionFormat';
import { resolveTenantRowAccess } from '../../utils/shareAccess';
import { useRecordVisit } from '../../utils/visitHistory';
import { capitalizeDictatedSegment } from '../../utils/voiceDictationFormatting';
import DocumentBody from './DocumentBody';
import RecordingBubble from '../../components/RecordingBubble';
// MedStudentAIAssistant is now rendered inside MedStudentRightPanel as the
// "AI Chat" tab; we don't import it here directly anymore.
import FormsModal from '../../components/FormsModal';
import VersionHistoryModal from '../../documentComponents/VersionHistoryModal';
import MedStudentReportsModal from './MedStudentReportsModal';
import PriorVisitsModal from './PriorVisitsModal';
import AudioManagerModal from '../../components/AudioManagerModal';
import ClinicAudiosModal from './ClinicAudiosModal';
import PatientProfileDrawer from './PatientProfileDrawer';
import ClinicPatientMedsModal from './ClinicPatientMedsModal';
import ClinicPatientOrdersModal from './ClinicPatientOrdersModal';
import useOrdersDeepLink from './useOrdersDeepLink';
import PaperworkLinkEditor from './PaperworkLinkEditor';
import AssignModal from '../../components/messaging/AssignModal';
import TaskLinkCard from './TaskLinkCard';
import { buildTaskPrefill } from './taskPrefill';
import { isTaskId, attachTaskLinkClicks, applyTaskLink } from './taskLinkBlot';
import { trackRange } from './taskLinkRange';
import PatientSummaryPanel from '../../components/patient/PatientSummaryPanel';
import { usePatientSummaryComments } from '../../hooks/patient/usePatientSummaryComments';
import SectionsPanel from './SectionsPanel';
import { useCombineDocuments } from '../../documentComponents/MedStudentCombineButton';
import SendToPcpModal from './SendToPcpModal';
import { mergeLedgerIntoEnvelope } from './process/mergeLedgerIntoEnvelope';
import { readLedger } from './process/processedLedger';
import { downloadFreshJson } from '../../utils/freshDownload';
import ProcessModal, { computeDefaultSourceSelection } from './process/ProcessModal';
import { useVisitSources } from './process/useVisitSources';
import useSavedMasterDiff from './process/useSavedMasterDiff';
import { mergeLedger, withLedgerAddedElsewhere } from './process/processedLedger';
import { applyOpsToEditor } from './process/applyProcessEdits';
import { composeOps, expandMarkdownInsertToOps, locateEdits, locateSectionEdits } from './process/applyEditsToDoc';
import { ICD_PROBLEM_BLOT_NAME } from './process/icdProblemBlot';
import { isReadOnlyRole, canAccessPage, canAccessComponent } from '../../config/roles';
import {
  loadAiChatSettings,
  buildAiRequestSettings,
  readLocalAiSettings,
  normalizeAiChatSettings,
  writeLocalAiSettings,
  saveAiChatSettings,
} from '../../utils/aiChatSettings';

// Local-date ISO (YYYY-MM-DD). new Date().toISOString() is UTC and lands on the
// wrong day for users west of UTC after their local midnight.
function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// Tailwind lg — must match breakpoint used for clinic layout (`lg:` utilities).
const CLINIC_DESKTOP_MEDIA = '(min-width: 1024px)';
// Session-dismiss small-viewport gate so 11–13" laptops (physical width under lg) can work.
const NARROW_GATE_DISMISS_KEY = 'medstudent-clinic-narrow-dismissed';
const PROCESS_SOURCE_MODE_KEY = 'medstudent-process-source-mode';

function opsArrayToPlainText(ops) {
  if (!Array.isArray(ops)) return null;
  // For embed ops (block-level blots like VitalsGridBlot, RosGridBlot, the
  // bleeding/clotting summary boxes) op.insert is an object, not a string.
  // Each embed takes 1 position in Quill's index space. Without a placeholder
  // here, the plain text would be SHORTER than Quill's index space by the
  // number of embeds preceding any given offset — which causes
  // locateSectionEdits's `retain N` to under-shoot, and the subsequent
  // delete then chops the last char(s) of the heading line (the
  // "PAST MEDICAL HISTOR" symptom). Using ￼ (Unicode OBJECT REPLACEMENT
  // CHARACTER) keeps plain-text positions in sync with Quill positions.
  const t = ops.map((op) => {
    if (typeof op.insert === 'string') return op.insert;
    if (op.insert != null) return '￼';
    return '';
  }).join('');
  return t.length ? t : null;
}

// AIAssistant.documentContent is consumed as a plain string (.trim() etc.).
// Storage envelope: { delta: { ops: [...] }, ... }. Quill change callbacks emit { ops }.
function documentToText(doc) {
  return opsArrayToPlainText(doc?.delta?.ops);
}

function quillDeltaToPlain(deltaLike) {
  return opsArrayToPlainText(deltaLike?.ops);
}

// Name prefix for Master Note snapshots saved into the version history. The
// trailing space matters: the saved version is `${PREFIX}${N}` = "Master Note
// Version 3", and the count LIKE query matches `${PREFIX}%`.
const MASTER_NOTE_VERSION_PREFIX = 'Master Note Version ';

export default function MedStudentClinicPage({
  setStateData,
  setRefId,
  setShowPersistentComponent,
  source = 'visit',
}) {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dateParam = searchParams.get('date');
  // PatientPage.buildEditDocumentLocationState stamps the visit's date_ onto
  // location.state when redirecting to /clinic, so we can render the sidebar
  // for the right day even when the patient row lookup (below) fails or is
  // slow. Survives only the in-app nav — a page refresh loses this and we
  // fall back to the visit fetch / today.
  const stateDate = location.state?.date_ || null;
  // Which Postgres schema this chart's patient row lives in, resolved BEFORE
  // the row loads (usePatientDocument's first fetch needs it): an explicit
  // `?schema=` URL param wins (deep-links / cross-schema nav), then nav-state
  // `_schema` (PatientListPanel-style redirects carrying a linked-provider
  // row), then the viewer's own active schema — byte-identical to today when
  // none of those are present. Once the row itself loads, `chartSchema(visit.
  // patient) || initialChartSchema` is used everywhere downstream so a stale
  // initial guess can't outlive the real fetched `_schema`.
  const urlSchema = searchParams.get('schema');
  const stateSchema = location.state?.rowData?._schema || location.state?._schema || null;
  // The schema this chart was ASKED to open (deep-link / cross-schema nav), or
  // null for a same-schema open. A plain const (recomputed every render) so an
  // in-page nav to a different ?schema= is never frozen to the mount value.
  const requestedSchema = (urlSchema || stateSchema || '').trim() || null;
  const initialChartSchema = requestedSchema || supabaseSchema;
  // Allow-list guardrail (defense-in-depth — NOT a hard security boundary:
  // tenant-table RLS is USING(true) by design, so authoritative enforcement is
  // server-side, e.g. the view-as RLS guard). A hand-crafted `?schema=` could
  // aim this chart at any exposed schema; we OPTIMISTICALLY honor the request so
  // an honest cross-schema open's first read is correct with zero added latency,
  // then — once the viewer's own linked set (public.users.schema) loads — revoke
  // it if the requested schema isn't theirs, collapsing every downstream
  // read/write back to home. Same-schema opens (requestedSchema == null) never
  // gate and stay byte-identical to before.
  const [schemaAllowed, setSchemaAllowed] = useState(true);
  useEffect(() => {
    if (!requestedSchema) { setSchemaAllowed(true); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const sb = await getSupabase();
        const { data: { session } = {} } = await sb.auth.getSession();
        const uid = session?.user?.id;
        if (!uid) return;
        const { data, error } = await sb
          .schema('public').from('users').select('schema, patient_verified_schema').eq('id', uid).single();
        if (cancelled) return;
        if (error) {
          // Couldn't determine the viewer's linked set (transient error / missing
          // row) — stay OPTIMISTIC rather than bump an honest cross-schema user to
          // home. Server-side RLS is the real boundary, so failing open is safe.
          console.warn('[MedStudentClinicPage] users.schema fetch failed; keeping requested schema optimistically:', error.message);
          return;
        }
        // A patient's verified tenant lives in `patient_verified_schema` (a
        // separate column from their `users.schema`, which only tracks the
        // multi-tenant clinical link). Without folding it in here, a verified
        // patient whose linked tenant wasn't promoted into `users.schema`
        // (e.g. freshly approved) would get bounced to `supabaseSchema` and
        // the visit-summary call (which uses `patient_verified_schema` directly)
        // would 403 — the doc loads from one schema while the summary hits
        // another. CSV-merge the two so this gate matches what the rest of
        // patient-portal calls actually use.
        const linkedSchemas = [
          ...String(data?.schema || '').split(','),
          ...String(data?.patient_verified_schema || '').split(','),
        ].map((s) => s.trim()).filter(Boolean).join(',');
        const ok = isSchemaAllowed(requestedSchema, linkedSchemas, supabaseSchema);
        if (!ok) {
          console.warn(`[MedStudentClinicPage] requested schema "${requestedSchema}" not in the viewer's linked set — falling back to "${supabaseSchema}".`);
        }
        setSchemaAllowed(ok);
      } catch (_) { /* keep the optimistic default */ }
    })();
    return () => { cancelled = true; };
  }, [requestedSchema]);
  // The initial schema actually honored: the request, gated by the allow-list.
  const toast = useToast();

  const userInfo = useUserInfo();
  const patientReadOnly = isReadOnlyRole(userInfo.role);
  // Clinic agents (pages/medstudent/agents): loaded here, where the badges live, so they stay
  // live while the right panel is collapsed or on another tab.
  const agentsAllowed = !patientReadOnly && canAccessComponent(userInfo.role, 'clinicPage.agentsTab');
  const agentsState = useAgents({ enabled: agentsAllowed });
  // The count for the AI bubble's and the collapsed rail's badges. Those sit inside buttons with
  // their own labels, which screen readers read alone, so the count goes into the labels as well.
  const agentsUnseen = agentsAllowed && Number.isFinite(agentsState.unseen) && agentsState.unseen > 0
    ? agentsState.unseen
    : 0;
  const agentsUnseenLabel = agentsUnseen > 0
    ? `, ${agentsUnseen} unseen agent ${agentsUnseen === 1 ? 'update' : 'updates'}`
    : '';
  // The badges themselves stop at 99+; the labels keep the exact count.
  const agentsUnseenText = agentsUnseen > 99 ? '99+' : String(agentsUnseen);
  // For verified patients, prefer their `patient_verified_schema` over the
  // viewer's `supabaseSchema` whenever the latter is the catch-all 'public'.
  // Without this pin, a freshly-approved patient (whose `users.schema` is
  // still 'public' / blank) would load the chart from public while the
  // visit-summary call uses the verified tenant — two different schemas,
  // same appointment id, the second one 403s. Pinning here keeps every read
  // in this page consistent with `PatientSummaryPanel`'s schema choice.
  // Clinical viewers keep the original behavior (supabaseSchema / request
  // wins), so non-patient charts are byte-identical to before.
  const patientVerifiedSchema = userInfo?.patientUserRow?.patient_verified_schema || null;
  const schemaForPatient = patientReadOnly && patientVerifiedSchema
    ? patientVerifiedSchema
    : initialChartSchema;
  const effectiveInitialSchema = schemaAllowed
    ? schemaForPatient
    : (patientVerifiedSchema || supabaseSchema);
  // Clinician-only header actions. Hidden for the non-clinical tenant roles
  // (student / socialworker / other / medstudent) via roles.json component
  // gates — only clinical decision-makers (doctor / superadmin / admin) sign,
  // generate master notes, or attach forms.
  const canSignAction = canAccessComponent(userInfo.role, 'clinicPage.signButton');
  const canMasterNoteAction = canAccessComponent(userInfo.role, 'clinicPage.masterNoteButton');
  const canFormsAction = canAccessComponent(userInfo.role, 'clinicPage.formsButton');
  // Orders is gated like Sign rather than like the read-only viewers: it is the
  // entry point for filling and e-signing order paperwork, not just for reading
  // the list, and placing orders is a physician responsibility.
  const canOrdersAction = canAccessComponent(userInfo.role, 'clinicPage.ordersButton');
  const rosterMode = source === 'roster';
  // Live collab now covers roster charts too. The collab room hydrates from the
  // document `.json` at its storage path (schema/path-based — see
  // rooms_registry._hydrate step 2), which EXISTS for any roster patient opened
  // before, so multi-user sync works WITHOUT the notes-c seed the server can't
  // resolve for patients_with_ehrid ids. Only limitation: a brand-new roster
  // patient with no `.json` yet seeds empty server-side (the notes-c seed is a
  // last resort and skips roster paths) — it becomes functional on first edit.
  const collabActive = MEDSTUDENT_LIVE_COLLAB;
  // Clinical-only gate for /patients/:uuid — doctor/admin/superadmin.
  useEffect(() => {
    if (!rosterMode) return;
    if (!userInfo?.role) return; // still loading
    if (!canAccessPage(userInfo.role, 'patientChart')) {
      navigate('/forbidden');
    }
  }, [rosterMode, userInfo?.role, navigate]);
  // Unseen patient comments on this visit — drives the notification badge on the
  // doctor's "Patient summary" panel. Opening that panel marks them all seen.
  const { unseenCount: patientCommentsUnseen } = usePatientSummaryComments(uuid, userInfo.role);
  const showPatientReadOnlyToast = useCallback(() => {
    toast({ title: 'Read-only patient portal', status: 'info', duration: 2500 });
  }, [toast]);
  const [aiChatSettings, setAiChatSettings] = useState(() =>
    normalizeAiChatSettings(readLocalAiSettings()),
  );
  const aiChatSettingsRevisionRef = useRef(0);
  // One-shot `{ activeSkill, skillIntensity, nonce }` handed to the mounted AI
  // chat when the Process Sources modal changes the shared skill preference.
  const [skillSync, setSkillSync] = useState(null);
  const skillSyncNonceRef = useRef(0);

  // Patient fetch must run first — the sidebar date follows the visit being
  // viewed when no explicit ?date= is in the URL. (Navigation from places like
  // ResultsTable always passes ?date=, but deep-links to /clinic/<uuid> from
  // PatientPage.navigateToEditDocument don't.)
  const visit = usePatientDocument(uuid, {
    readOnly: patientReadOnly,
    viewer: userInfo.patientUserRow,
    // For the "Combine Documents" day-view (uuid === COMBINED_UUID) there is
    // no notes-c row; the hook synthesizes one from this date so the collab
    // room resolves to `<date>-combined.json`. Ignored for normal visits.
    combinedDate: dateParam,
    source,
    schema: effectiveInitialSchema,
    // The row the sidebar card carried over. Lets the header switch to the new
    // patient the instant it is clicked rather than blanking while the fetch
    // runs — the hook checks it belongs to this uuid before using it.
    seed: location.state?.rowData || null,
  });

  // Sync patient context to the global AudioRecorder component props in App.js
  useEffect(() => {
    if (visit.patient) {
      if (setStateData) {
        console.log("🔄 [MedStudentClinicPage] Syncing patient data to global stateData:", visit.patient);
        setStateData(visit.patient);
      }
      if (setRefId) {
        console.log("🔄 [MedStudentClinicPage] Syncing patient ID to global refId:", visit.patient.id || uuid);
        setRefId(visit.patient.id || uuid);
      }
    }
  }, [visit.patient, uuid, setStateData, setRefId]);

  // Log this visit to localStorage so the ⌘K command palette can surface
  // recently-viewed patients as a quick rebound list. No-ops while the
  // patient row is still loading; fires once name resolves.
  useRecordVisit(uuid, visit?.patient?.patient_name, visit?.patient?.time_);

  // Latest Med-A-doc plain text for AI when live collab is ON (`visit.document`
  // stays null — usePatientDocument skips storage fetch). Read at chat send-time
  // via ref so typing does not rerender the whole clinic page each keystroke.
  const aiDocumentPlainRef = useRef(null);
  useEffect(() => {
    aiDocumentPlainRef.current = null;
  }, [uuid]);
  const processedSourcesRef = useRef([]);
  // The ledger as this page last read it from, or wrote it to, storage: persistDelta
  // keeps entries other writers (mobile Accept, another tab) added since then.
  const ledgerSyncedRef = useRef([]);
  const [processedLedger, setProcessedLedger] = useState([]);


  // Schedule date precedence:
  //   1. explicit ?date= URL param   (ResultsTable passes this)
  //   2. location.state.date_        (PatientPage redirect carries this)
  //   3. the loaded visit's date_    (covers deep-links / refreshes)
  //   4. today                       (only once we know the visit has no date_)
  //
  // While the visit row is still in flight AND we have neither ?date= nor
  // state.date_, we leave scheduleDate null. useTodaysSchedule bails on a
  // null date and stays in loading state, so the sidebar doesn't flash
  // "TODAY'S SCHEDULE · <today>" + "No visits scheduled" before the real
  // date arrives. The displayed `date` prop falls back to today for the
  // empty-state header text only — the actual schedule query waits on
  // `scheduleDate`.
  const scheduleDate = dateParam
    || stateDate
    || visit.patient?.date_
    || (visit.isLoading ? null : todayIso());
  const date = scheduleDate || todayIso();

  const schedule = useTodaysSchedule(scheduleDate);

  // ── Theme state ──────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('medstudent-theme');
      if (saved === 'dark' || saved === 'light') return saved;
      return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch { return 'light'; }
  });
  const toggleTheme = useCallback(() => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('medstudent-theme', next); } catch {}
      return next;
    });
  }, []);

  // Patients always get the bright theme on this page.
  //
  // `theme` seeds from localStorage or the OS `prefers-color-scheme`, so a
  // patient on a device set to dark mode landed on a dark chart they had no way
  // to change — the theme toggle lives in the clinician-only IconRail, which is
  // hidden for them. The portal's own surfaces (visit summary, the patient
  // sidebar) are designed light and only go dark via the `.dark` class this
  // sets on #clinic-root, so pinning it here is enough for all of them.
  //
  // Only the RENDER is pinned: `theme` and toggleTheme are left alone, so a
  // clinician's stored preference survives untouched.
  const effectiveTheme = patientReadOnly ? 'light' : theme;

  // Viewport tracking — show desktop-only notice below Tailwind lg (1024px).
  // Prefer matchMedia over raw innerWidth: aligns with CSS `lg:` and avoids a
  // transient false‑narrow when width is 0 before first layout.
  const computeIsNarrow = useCallback(() => {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia) return !window.matchMedia(CLINIC_DESKTOP_MEDIA).matches;
    const w =
      window.visualViewport?.width ??
      (window.innerWidth || document.documentElement?.clientWidth || 1024);
    return w < 1024;
  }, []);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' ? computeIsNarrow() : false,
  );
  useEffect(() => {
    const update = () => setIsNarrow(computeIsNarrow());
    update();
    const rafId = window.requestAnimationFrame(() => update());

    const mq = window.matchMedia?.(CLINIC_DESKTOP_MEDIA) ?? null;
    if (mq) mq.addEventListener('change', update);
    else window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      window.cancelAnimationFrame(rafId);
      if (mq) mq.removeEventListener('change', update);
      else window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, [computeIsNarrow]);

  const [narrowGateDismissed, setNarrowGateDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(NARROW_GATE_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const dismissNarrowGate = useCallback(() => {
    try {
      sessionStorage.setItem(NARROW_GATE_DISMISS_KEY, '1');
    } catch {
      /* fine */
    }
    setNarrowGateDismissed(true);
  }, []);
  const showNarrowGate = isNarrow && !narrowGateDismissed;

  // ── Modal open/close state ────────────────────────────────────────────────
  const [isFormsOpen, setIsFormsOpen] = useState(false);
  const [isVersionsOpen, setIsVersionsOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isPriorVisitsOpen, setIsPriorVisitsOpen] = useState(false);
  const [isRecorderOpen, setIsRecorderOpen] = useState(false);
  const [isAudioManagerOpen, setIsAudioManagerOpen] = useState(false);
  // Header "🎧 Audios" — the light play-and-read view. The Audio Manager
  // above stays where it is (Tools menu) for backups / rename / delete.
  const [isAudiosOpen, setIsAudiosOpen] = useState(false);
  const [isPatientProfileOpen, setIsPatientProfileOpen] = useState(false);
  const [isPcpModalOpen, setIsPcpModalOpen] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [signModalMode, setSignModalMode] = useState('sign'); // 'sign' | 'reveal'
  const [revealPassword, setRevealPassword] = useState('');
  const [revealSignatureText, setRevealSignatureText] = useState('');
  const justSignedRef = useRef(false); // reveal-close opens PCP only after a fresh sign
  const [isUnlockOpen, setIsUnlockOpen] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  // Transient per-session grant: true only after the viewer enters the password
  // for a signed/locked note. Never persisted, so reopening always re-locks.
  const [signedEditGranted, setSignedEditGranted] = useState(false);
  const [visitInProgress, setVisitInProgress] = useState(false);
  const [isProcessOpen, setIsProcessOpen] = useState(false);
  // Doctor-only collapsible "Patient summary" panel (clinician view). Lazy:
  // the summary only fetches/generates once the doctor expands it.
  const [patientSummaryOpen, setPatientSummaryOpen] = useState(false);
  const [patientSummaryModalOpen, setPatientSummaryModalOpen] = useState(false);
  const [injectPrompt, setInjectPrompt] = useState(null);
  const injectNonceRef = useRef(0);
  const { groups: sourceGroups, unprocessedCount, loading: sourcesLoading } =
    useVisitSources(visit.patient, processedLedger);

  useEffect(() => {
    let cancelled = false;
    const startedRevision = aiChatSettingsRevisionRef.current;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        const loaded = await loadAiChatSettings(supabase, userId, {
          mirrorToLocal: false,
          backfillToDb: false,
        });
        if (!cancelled && aiChatSettingsRevisionRef.current === startedRevision) {
          writeLocalAiSettings(loaded);
          setAiChatSettings(loaded);
        }
      } catch (error) {
        const loaded = await loadAiChatSettings(null, null);
        if (!cancelled && aiChatSettingsRevisionRef.current === startedRevision) {
          setAiChatSettings(loaded);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAiSettingsSaved = useCallback((settings) => {
    const normalized = normalizeAiChatSettings(settings);
    aiChatSettingsRevisionRef.current += 1;
    writeLocalAiSettings(normalized);
    setAiChatSettings(normalized);
  }, []);

  // Skill edited from the Process Sources modal. Same per-user preference the
  // AI chat's Settings › Skills tab owns, so it is written straight back to
  // users.ai_chat_settings — pick it in either place, it follows the user
  // everywhere. Local state updates first so the very next generate — Master
  // Note or suggest-edits, both of which derive from processAiRequestSettings
  // — carries the new skill without waiting on the round-trip.
  const handleAiSkillChange = useCallback((nextSkill, nextIntensity) => {
    const normalized = normalizeAiChatSettings({
      ...aiChatSettings,
      activeSkill: nextSkill,
      skillIntensity: nextIntensity,
    });
    // Bump the revision so an in-flight settings hydrate can't land on top of
    // the choice that was just made (same guard the assistant's save uses).
    aiChatSettingsRevisionRef.current += 1;
    writeLocalAiSettings(normalized);
    setAiChatSettings(normalized);
    // One-shot signal so the already-mounted AI chat adopts the same choice
    // (its skill badge and next query) without waiting for a remount.
    skillSyncNonceRef.current += 1;
    setSkillSync({
      activeSkill: normalized.activeSkill,
      skillIntensity: normalized.skillIntensity,
      nonce: skillSyncNonceRef.current,
    });
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) return;
        await saveAiChatSettings(supabase, userId, normalized);
      } catch (_) { /* localStorage mirror already written — non-fatal */ }
    })();
  }, [aiChatSettings]);

  // Concise mode (Ellen 2026-07-23 — NP stopped using the app: notes too long).
  // Self-service per-clinician note style, persisted in localStorage so the
  // "click once, stays concise" promise survives reloads. Rides into every
  // Master Note request via processAiRequestSettings.note_style below.
  // (MVP persistence is device-local; the follow-up moves it into the per-user
  // ai_chat_settings DB store so it follows the clinician across machines.)
  const [conciseNote, setConciseNote] = useState(() => {
    try { return localStorage.getItem('vmai:conciseNote') === '1'; } catch { return false; }
  });
  const toggleConciseNote = useCallback(() => {
    setConciseNote((v) => {
      const next = !v;
      try { localStorage.setItem('vmai:conciseNote', next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const processAiRequestSettings = useMemo(
    () => ({
      ...buildAiRequestSettings(aiChatSettings, { chatModelAllowed: false }),
      note_style: conciseNote ? 'concise' : 'full',
    }),
    [aiChatSettings, conciseNote],
  );

  const handleDeleteAudio = async (audioToDelete) => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    const patientUuid = visit?.patient?.id;
    if (!patientUuid) return;
    try {
      const supabase = await getSupabase();
      const { data: existing } = await supabase
        .schema(chartDocSchema)
        .from(chartTable(visit.patient))
        .select('more_audios, transcripts')
        .eq('id', patientUuid)
        .single();
      if (!existing) throw new Error('Patient not found');
      const audioUrl = audioToDelete?.url;
      const updatedAudios = (existing.more_audios || []).filter((a) => a.url !== audioUrl);
      const updatedTranscripts = (existing.transcripts || []).filter(
        (t) => t.audio_url !== audioUrl && t.audio_file_name !== audioToDelete?.name,
      );
      const { BASE_URLS } = await import('../../components/env');
      const base = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? BASE_URLS.local.backend : BASE_URLS.render.backend;
      await fetch(`${base}/api/transcription-backup/delete-audio/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientUuid,
          audio_url: audioUrl,
          session_id: audioToDelete?.session_id || null,
          schema_name: chartDocSchema,
        }),
      });
      await supabase.schema(chartDocSchema).from(chartTable(visit.patient)).update({ more_audios: updatedAudios, transcripts: updatedTranscripts }).eq('id', patientUuid);
      toast({ title: 'Audio deleted', status: 'success', duration: 3000, isClosable: true });
      visit.refetch?.();
    } catch (err) {
      console.error('[AudioDelete]', err);
      toast({ title: 'Delete failed', description: err?.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleRenameAudio = async (audioRow, newFilenameStem) => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    const patientUuid = visit?.patient?.id;
    if (!patientUuid || !audioRow?.url) return;
    try {
      const { BASE_URLS } = await import('../../components/env');
      const base = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? BASE_URLS.local.backend : BASE_URLS.render.backend;
      const resp = await fetch(`${base}/api/transcription-backup/rename-audio/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: patientUuid,
          old_audio_url: audioRow.url.trim(),
          new_filename: newFilenameStem,
          schema_name: chartDocSchema,
        }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.success) throw new Error(result.error || 'Rename failed');
      toast({ title: 'Audio renamed', description: result.display_name ? `Updated to "${result.display_name}".` : 'Done.', status: 'success', duration: 4000, isClosable: true });
      visit.refetch?.();
    } catch (err) {
      console.error('[AudioRename]', err);
      toast({ title: 'Rename failed', description: err?.message, status: 'error', duration: 5000, isClosable: true });
      throw err;
    }
  };

  // Read / edit mode for the document. Persists across refreshes via
  // sessionStorage keyed by uuid — if the user was in edit mode and hits
  // F5, they land back in edit mode for the same patient. A fresh patient
  // (different uuid) starts in read mode by default so opening a new visit
  // can't be edited by accident. Selection still works in read mode
  // (highlight + copy is preserved).
  const editModeKey = (id) => `medstudent-edit-mode:${id}`;
  const [isReadOnly, setIsReadOnly] = useState(true);
  const effectiveReadOnly = patientReadOnly || isReadOnly;
  useEffect(() => {
    setSignedEditGranted(false); // a freshly opened doc never carries an unlock grant
    if (!uuid) return;
    try {
      const stored = sessionStorage.getItem(editModeKey(uuid));
      setIsReadOnly(stored !== 'edit');
    } catch {
      setIsReadOnly(true);
    }
  }, [uuid]);
  // Only the user-initiated toggle writes to storage — programmatic
  // setIsReadOnly(true) on uuid change (above) must not overwrite the
  // newly-loaded preference.
  const toggleReadOnly = useCallback(() => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    setIsReadOnly((r) => {
      const next = !r;
      try {
        if (uuid) sessionStorage.setItem(editModeKey(uuid), next ? 'read' : 'edit');
      } catch {}
      return next;
    });
  }, [uuid, patientReadOnly, showPatientReadOnlyToast]);
  // Collapse state for the left (patient list) and right (AI / Reports /
  // Transcript) sidebars. When collapsed, the panel is replaced by a thin
  // rail with an expand chevron so the document gets the full width.
  //
  // The left (patient list) defaults COLLAPSED — once a doctor is in a
  // specific chart, the list of other patients is redundant and just
  // crowds the canvas. Preference persists so a doctor who prefers the
  // list expanded only has to toggle once.
  const LEFT_COLLAPSED_STORAGE_KEY = 'medstudent-clinic-left-collapsed';
  const [leftCollapsed, setLeftCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(LEFT_COLLAPSED_STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch (_) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(LEFT_COLLAPSED_STORAGE_KEY, String(leftCollapsed)); }
    catch (_) {}
  }, [leftCollapsed]);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  // Collapse state for the sections outline that sits directly left of the
  // document. Same persistence pattern as the patient list; defaults
  // collapsed so it doesn't crowd the canvas for users who never open it.
  const SECTIONS_COLLAPSED_STORAGE_KEY = 'medstudent-clinic-sections-collapsed';
  const [sectionsCollapsed, setSectionsCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem(SECTIONS_COLLAPSED_STORAGE_KEY);
      return v === null ? true : v === 'true';
    } catch (_) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(SECTIONS_COLLAPSED_STORAGE_KEY, String(sectionsCollapsed)); }
    catch (_) {}
  }, [sectionsCollapsed]);
  // Combine documents moved from the DoctorPortalBar green button into the
  // Tools menu (2026-07-05) — same merge logic, triggered via PatientHeaderBar.
  const { combine: combineDocuments } = useCombineDocuments();
  // Right-panel width in px; persisted so user-resizes survive reloads. The
  // 416px default matches the prior `w-[26rem]` literal. Lower bound only —
  // the panel can grow as wide as the user wants. The splitter still keeps
  // a small minimum of doc-area width so the doc never disappears entirely.
  const RIGHT_WIDTH_STORAGE_KEY = 'medstudent-clinic-right-width';
  const [rightWidth, setRightWidth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(RIGHT_WIDTH_STORAGE_KEY), 10);
      if (Number.isFinite(saved) && saved >= 280) return saved;
    } catch (_) { /* fine */ }
    return 416;
  });
  useEffect(() => {
    try { localStorage.setItem(RIGHT_WIDTH_STORAGE_KEY, String(rightWidth)); }
    catch (_) { /* fine */ }
  }, [rightWidth]);

  // Hard cap on the right panel: once a drag pushes the doc-area
  // header/toolbar past its natural single-row width, snap rightWidth back
  // by EXACTLY the overflow amount and lock the cap there. The splitter's
  // `maxWidth` reads `maxRightWidth`, so subsequent drag motion past the
  // cap is rejected at the source — the drag visibly hits a wall instead
  // of bouncing.
  //
  // The cap auto-resets to `Infinity` whenever something changes that
  // could shift the natural limit (patient switch — different name length;
  // window resize — different available space; sidebar collapse toggles).
  // That way the user isn't stuck at a stale cap from a previous layout.
  const [maxRightWidth, setMaxRightWidth] = useState(Infinity);
  // Set true once the user manually drags the splitter. While true, the
  // overflow auto-fit below stops snapping the panel back, so a deliberate
  // resize sticks. Reset on the same layout changes that reset the cap.
  const userResizedRef = useRef(false);
  useEffect(() => {
    setMaxRightWidth(Infinity);
    userResizedRef.current = false;
  }, [uuid, leftCollapsed, rightCollapsed]);

  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      // Once the user has manually resized, respect their width — don't auto-snap.
      if (userResizedRef.current) return;
      const header = document.querySelector('[data-clinic-header]');
      const toolbar = document.querySelector('[data-clinic-toolbar]');
      // Excess = how many px the content overflows by. Measure BOTH the
      // header (whose action cluster is shrink-0 + whitespace-nowrap, so it
      // can leak horizontally) and the toolbar (paragraph/font/size selects
      // + format buttons). `scrollWidth - clientWidth` is the horizontal
      // shortfall.
      const excessOf = (el) => el ? Math.max(0, el.scrollWidth - el.clientWidth) : 0;
      const excess = Math.max(excessOf(header), excessOf(toolbar));
      if (excess > 2) {
        const newWidth = Math.max(280, rightWidth - excess - 4 /* small buffer */);
        setRightWidth(newWidth);
        // Only lock the cap when shrinking actually relieved the overflow
        // (landed above the 280 floor). If we bottomed out at the floor, the
        // overflow is caused by a narrow viewport, not the panel width —
        // locking min==max here is exactly what made the splitter undraggable
        // on laptops. Leave the cap open so the splitter keeps a usable range.
        if (newWidth > 280) {
          setMaxRightWidth(newWidth);
        } else {
          setMaxRightWidth(Infinity);
        }
      }
    };
    const raf = requestAnimationFrame(check);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [rightWidth, leftCollapsed, rightCollapsed, visit.patient]);

  // Window resize clears the cap and re-checks — a wider window may have
  // freed up the right panel; a narrower one may need an immediate snap.
  useEffect(() => {
    const onResize = () => setMaxRightWidth(Infinity);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Active tab in the right panel is owned here so the floating AI bubble
  // (below) can flip to it from outside the panel. 'ai' | 'reports' | 'transcript'.
  const [rightTab, setRightTab] = useState('ai');
  // Task #158 (Option A) — guard against losing a pending diff when user
  // navigates away from the Copilot tab. When the Process review pane is
  // mounted into the Copilot slot (data-process-diff-slot has children),
  // any tab switch away from 'copilot' triggers a confirm. If user cancels,
  // we revert. If they confirm, the diff is intentionally being abandoned.
  // Implemented as a useEffect watching rightTab transitions instead of
  // wrapping every setRightTab call — same outcome, far smaller blast radius.
  const prevRightTabRef = useRef(rightTab);
  useEffect(() => {
    const prev = prevRightTabRef.current;
    prevRightTabRef.current = rightTab;
    if (prev !== 'copilot' || rightTab === 'copilot') return;
    // Was Copilot, now switching away — check for pending diff content.
    const slot = document.querySelector('[data-process-diff-slot]');
    const hasDiffMounted = slot && slot.children && slot.children.length > 0;
    if (!hasDiffMounted) return;
    // Defer the confirm so React finishes the tab swap render first. Without
    // the defer, the confirm fires during the render cycle which can lead to
    // weird state if user picks Cancel.
    setTimeout(() => {
      const ok = window.confirm(
        'You have an unresolved diff in Copilot.\n\n' +
        'Leaving this tab will lose your unsaved review. ' +
        'OK to continue, Cancel to stay on Copilot.'
      );
      if (!ok) setRightTab('copilot');
    }, 0);
  }, [rightTab]);
  // Bubble click rule:
  //   - If panel is collapsed OR a tab other than AI is active → expand the
  //     panel and switch to AI.
  //   - If panel is open AND AI tab is already active → collapse the panel.
  const onAIBubbleClick = useCallback(() => {
    if (rightCollapsed || rightTab !== 'ai') {
      setRightCollapsed(false);
      setRightTab('ai');
    } else {
      setRightCollapsed(true);
    }
  }, [rightCollapsed, rightTab]);
  // Ref to DocumentBody so DocumentToolbar can call editorRef.current.getEditor(0).
  const documentBodyRef = useRef(null);

  const [safeShareMode, setSafeShareMode] = useState(false);
  const [safeShareMethod, setSafeShareMethod] = useState('mask'); // 'mask', 'redact', 'hash'
  const [originalContents, setOriginalContents] = useState(null);
  const [isDeidentifying, setIsDeidentifying] = useState(false);

  // ── Autosave state ───────────────────────────────────────────────────────
  // savedAt: HH:MM string shown in DocumentToolbar; null while no save yet.
  // saving: true during in-flight upload (suppresses concurrent writes).
  const [savedAt, setSavedAt] = useState(null);
  const savingRef = useRef(false);
  const pendingDeltaRef = useRef(null);
  const saveTimerRef = useRef(null);

  // Falls back to initialChartSchema before visit.patient has loaded (first
  // render) / for patients with no `_schema` stamped (chartSchema's own
  // fallback covers that); once the row loads, its own `_schema` wins so a
  // stale initial guess (e.g. nav state that turns out wrong) can't persist.
  // Gated by the allow-list: if the requested schema was revoked (not in the
  // viewer's linked set) EVERYTHING — including a foreign patient row that may
  // already have loaded and stamped its own `_schema` — collapses to home.
  // Patients fall back to their verified tenant before the home schema — see
  // `effectiveInitialSchema` rationale; a row with `_schema === 'public'` is
  // the legacy/staging shape and shouldn't pin a verified patient to it.
  const chartDocSchema = schemaAllowed
    ? (chartSchema(visit.patient) || (patientReadOnly && patientVerifiedSchema) || initialChartSchema)
    : (patientReadOnly && patientVerifiedSchema) || supabaseSchema;
  const documentPath = buildDocPath(visit.patient, chartDocSchema);

  // Mirror of `documentPath` for non-stale comparisons inside async callbacks.
  // persistDelta captures documentPath in its closure; if the user switches
  // patients while a save is in flight, the save's `finally` could re-schedule
  // with the OLD persistDelta and write whatever is now in pendingDeltaRef to
  // the OLD patient's file. The check below aborts the write when the
  // closure's path no longer matches the current one.
  const documentPathRef = useRef(documentPath);
  useEffect(() => { documentPathRef.current = documentPath; }, [documentPath]);

  // Saved Master Note diff for this chart (Omar 2026-08-17) — one slot per
  // user per document, stored beside the document itself so an iPad-generated
  // note can be reviewed and accepted at the desktop. Lives here rather than
  // inside ProcessModal because the header badge has to know about it without
  // the modal ever being opened.
  // The backend keeps every Master Note run in it (api/master_note_runs.py),
  // so a reload or a closed tab no longer loses one, and the phone shares it.
  // Every clinic-page role but a patient can run Master Note (the Process
  // modal opens after a recording or report for all of them), so all of them
  // see it; a patient or other read-only viewer has nothing to read (a null
  // path is the hook's "don't touch storage" signal). `supabaseSchema` is the
  // schema the Master Note request names, so both resolve the same user folder.
  const savedMasterDiff = useSavedMasterDiff({
    documentPath: patientReadOnly ? null : documentPath,
    schema: supabaseSchema,
  });
  const { discard: discardSavedMasterDiff } = savedMasterDiff;
  // Accept put run `requestId` in the note (and Version History): stop offering it.
  const handleAcceptedMasterNote = useCallback((requestId, { restoredSaved = false } = {}) => {
    if (requestId) void discardSavedMasterDiff(requestId);
    else if (restoredSaved) void discardSavedMasterDiff();
  }, [discardSavedMasterDiff]);
  // Hydrate the processed-sources ledger from the stored envelope. visit.document
  // is null under live-collab, so read it directly; persistDelta merges this ref
  // back into every save so autosave doesn't clobber processedSources.
  // Read PAST the storage CDN: it served a twenty-hour-old copy of this file
  // (empty ledger) after three rewrites, so every reload marked every source
  // "new" again and the next autosave wrote that empty ledger over the real one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!documentPath) return;
      try {
        const sb = getSupabaseAdmin();
        const parsed = await downloadFreshJson(sb, bucketName_files, documentPath);
        const ledger = readLedger(parsed);
        if (!cancelled) { processedSourcesRef.current = ledger; ledgerSyncedRef.current = ledger; setProcessedLedger(ledger); }
      } catch (_e) {
        if (!cancelled) { processedSourcesRef.current = []; ledgerSyncedRef.current = []; setProcessedLedger([]); }
      }
      // Post-load checkpoint after a Reset-to-Seed triggered reload.
      // Lift the autosave freeze set before the reset POST.
      try {
        sessionStorage.removeItem('vmai:reset-in-flight');
        window.__vmaiResetInFlight = false;
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [documentPath]);
  // Live viewers of this exact document path (Supabase Realtime presence).
  // Returns [] until the user is authed AND the document path resolves.
  // Stays in use when the live-collab flag is OFF; superseded by the
  // collab hook's awareness output when ON.
  const legacyPresenceUsers = useDocumentPresence(documentPath, userInfo);

  // Track the Quill instance for the collab hook as STATE (not a ref) so
  // the hook's effects re-run when DocumentBody finally mounts its editor.
  // A ref would silently hide the null → instance transition and the
  // hook's selection-change listener (which writes cursor positions into
  // awareness) would never attach.
  const [liveQuill, setLiveQuill] = useState(null);
  useEffect(() => {
    const q = documentBodyRef.current?.getEditor?.(0) || null;
    if (q !== liveQuill) setLiveQuill(q);
  });

  // ── Comments and Highlights States & Handlers ──────────────────────────
  const [comments, setComments] = useState([]);
  const commentsRef = useRef([]); // Ref to always access current comments in callbacks
  const commentIndexUpdateRef = useRef(null); // Ref to hold the comment index update function
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [selectedHighlightColor, setSelectedHighlightColor] = useState('#FEF08A');
  const [commentFloatPosition, setCommentFloatPosition] = useState(null);
  // Where the "📋 Assign task" chip sits next to a highlight (null = hidden).
  const [taskFloatPosition, setTaskFloatPosition] = useState(null);
  
  // Available highlight colors for comments
  const HIGHLIGHT_COLORS = ['#FEF08A', '#BFDBFE', '#BBF7D0', '#FBCFE'];
  const [showCommentsSidebar, setShowCommentsSidebar] = useState(false);
  const [commentBubblePositions, setCommentBubblePositions] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [showResolvedHistory, setShowResolvedHistory] = useState(false);
  // Yellow highlight for commented text (document text is black, so it reads well).
  const COMMENT_HIGHLIGHT_COLOR = '#ffeb3b';
  // Tracks the temporary highlight shown while a comment is being composed, so the
  // selection stays visibly marked after the editor loses focus. Removed on cancel.
  const pendingCommentHighlightRef = useRef(null);

  const fetchComments = useCallback(async () => {
    if (!uuid || uuid === "unknown") return;
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .schema(chartDocSchema)
        .from("document_comments")
        .select("*")
        .eq("appointment_id", uuid)
        .order("created_at", { ascending: true });
      if (!error && data) {
        setComments(data);
      }
    } catch (e) {
      console.error("Error fetching comments:", e);
    }
  }, [uuid, chartDocSchema]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (!uuid || uuid === "unknown") return;
    let subscription;
    (async () => {
      const supabase = await getSupabase();
      // Unique topic per mount — see uniqueChannelTopic. A fixed `comments:<id>`
      // topic is shared with EditableDocumentPage, so navigating between the two
      // for the same appointment returned an already-subscribed channel and
      // .on() threw an uncaught "…after subscribe()" error.
      subscription = supabase
        .channel(uniqueChannelTopic(`comments:${uuid}`))
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: effectiveInitialSchema,
            table: "document_comments",
            filter: `appointment_id=eq.${uuid}`,
          },
          () => {
            fetchComments();
          }
        )
        .subscribe();
    })();
    return () => {
      if (subscription) {
        getSupabase().then(supabase => supabase.removeChannel(subscription));
      }
    };
  }, [uuid, fetchComments, effectiveInitialSchema]);

  // Keep commentsRef in sync with comments state
  useEffect(() => {
    commentsRef.current = comments;
  }, [comments]);

  const updateBubblePositions = useCallback(() => {
    const currentComments = commentsRef.current;
    const newPositions = [];
    if (liveQuill) {
      currentComments.forEach((comment) => {
        if (comment.status !== 'open' || !comment.selection_range) return;
        let { index, length } = comment.selection_range;
        try {
          if (comment.highlight_text) {
            const currentText = liveQuill.getText(index, length);
            if (currentText !== comment.highlight_text) {
              const fullText = liveQuill.getText();
              let searchIdx = fullText.indexOf(comment.highlight_text);
              let closestDistance = Infinity;
              let bestIndex = -1;
              while (searchIdx !== -1) {
                const distance = Math.abs(searchIdx - index);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  bestIndex = searchIdx;
                }
                searchIdx = fullText.indexOf(comment.highlight_text, searchIdx + 1);
              }
              if (bestIndex !== -1) {
                const newRange = { ...comment.selection_range, index: bestIndex, length: comment.highlight_text.length };
                comment.selection_range = newRange;
                index = bestIndex;
                length = comment.highlight_text.length;
                
                // Format the new range to make sure it is highlighted
                liveQuill.formatText(bestIndex, comment.highlight_text.length, 'background', comment.highlight_color || '#FEF08A');
                
                (async () => {
                  try {
                    const supabase = await getSupabase();
                    await supabase
                      .schema(chartDocSchema)
                      .from('document_comments')
                      .update({ selection_range: newRange })
                      .eq('id', comment.id);
                  } catch (err) {
                    console.error("Error updating comment index in DB:", err);
                  }
                })();
              }
            }
          }
          const bounds = liveQuill.getBounds(index, length || 1);
          if (bounds) {
            const editorEl = liveQuill.container;
            const offsetTop = editorEl.offsetTop || 0;
            const offsetLeft = editorEl.offsetLeft || 0;
            
            // Calculate bubble position on top of the text selection
            const bubbleTop = offsetTop + bounds.top - 24;
            const bubbleLeft = Math.max(offsetLeft + 10, Math.min(offsetLeft + editorEl.clientWidth - 30, offsetLeft + bounds.left + bounds.width - 12));
            
            // Calculate active comment card position on top of the text selection (centered)
            const cardWidth = 260;
            const cardLeft = Math.max(offsetLeft + 10, Math.min(offsetLeft + editorEl.clientWidth - cardWidth - 10, offsetLeft + bounds.left + (bounds.width - cardWidth) / 2));
            const cardTop = offsetTop + bounds.top > 130 ? offsetTop + bounds.top - 130 : offsetTop + bounds.top + bounds.height + 8;

            newPositions.push({
              id: comment.id,
              comment: comment,
              top: bubbleTop,
              left: bubbleLeft,
              cardTop: cardTop,
              cardLeft: cardLeft
            });
          }
        } catch (e) {
          // ignore
        }
      });
    }
    // This runs on a 1-second interval (below), and a fresh array every tick
    // re-rendered the entire page even when no bubble moved — which destroyed
    // any text selection elsewhere on the page within a second (AI chat:
    // select an answer → highlight vanishes → nothing to copy; Leejoe
    // 2026-07-17). Keep the previous reference when positions are unchanged
    // so React bails out of the re-render. The `comment` identity check keeps
    // an edited comment's card fresh: an edit produces new objects, which
    // fails the bail and re-renders once.
    setCommentBubblePositions((prev) => {
      const same = prev.length === newPositions.length
        && prev.every((p, i) => {
          const n = newPositions[i];
          return p.id === n.id && p.comment === n.comment
            && p.top === n.top && p.left === n.left
            && p.cardTop === n.cardTop && p.cardLeft === n.cardLeft;
        });
      return same ? prev : newPositions;
    });
    // `chartDocSchema` is read by the comment re-anchor write above (the
    // supabase.schema(...) update when highlight text has moved), so it stays
    // in the deps — dropping it would close over a stale schema and write the
    // re-anchor to the wrong Postgres schema.
  }, [liveQuill, chartDocSchema]);

  useEffect(() => {
    updateBubblePositions();
    const interval = setInterval(updateBubblePositions, 1000);
    
    let combinedTextChangeHandler;
    if (liveQuill) {
      // Combined handler for text-change that updates both bubble positions and comment indices
      combinedTextChangeHandler = (delta, oldDelta, source) => {
        updateBubblePositions();
        if (source === "user" && delta.ops && commentIndexUpdateRef.current) {
          commentIndexUpdateRef.current(delta, 0);
        }
      };
      liveQuill.on('text-change', combinedTextChangeHandler);
      liveQuill.on('selection-change', updateBubblePositions);
    }
    
    return () => {
      clearInterval(interval);
      if (liveQuill && combinedTextChangeHandler) {
        liveQuill.off('text-change', combinedTextChangeHandler);
        liveQuill.off('selection-change', updateBubblePositions);
      }
    };
  }, [comments, liveQuill, updateBubblePositions]);

  useEffect(() => {
    if (!liveQuill) return;
    
    if (liveQuill.container && liveQuill.container.parentNode) {
      liveQuill.container.parentNode.style.position = 'relative';
    }
    
    const handleSelectionChange = (range, oldRange, source) => {
      if (range && range.length > 0) {
        const text = liveQuill.getText(range.index, range.length);
        setSelectedRange({ index: range.index, length: range.length, pageIndex: 0 });
        setSelectedText(text);
        
        try {
          const bounds = liveQuill.getBounds(range.index, range.length);
          const editorEl = liveQuill.container;
          const offsetTop = editorEl.offsetTop || 0;
          const offsetLeft = editorEl.offsetLeft || 0;
          
          const inputWidth = 260;
          const floatLeft = Math.max(offsetLeft + 10, Math.min(offsetLeft + editorEl.clientWidth - inputWidth - 10, offsetLeft + bounds.left + (bounds.width - inputWidth) / 2));
          const floatTop = offsetTop + bounds.top > 130 ? offsetTop + bounds.top - 130 : offsetTop + bounds.top + bounds.height + 8;
          
          setCommentFloatPosition({
            top: floatTop,
            left: floatLeft
          });
          const chipWidth = 130;
          setTaskFloatPosition({
            top: offsetTop + bounds.top > 40 ? offsetTop + bounds.top - 36 : offsetTop + bounds.top + bounds.height + 6,
            left: Math.max(offsetLeft + 4, Math.min(offsetLeft + editorEl.clientWidth - chipWidth - 4, offsetLeft + bounds.left + bounds.width - chipWidth)),
          });
        } catch (e) {
          console.error("Error getting selection bounds:", e);
        }
      } else {
        setTimeout(() => {
          const activeEl = document.activeElement;
          const insideComments = activeEl && (activeEl.closest('.comments-sidebar') || activeEl.closest('.comment-float-btn') || activeEl.closest('.comment-float-input'));
          setTaskFloatPosition(null);
          if (!insideComments) {
            setCommentFloatPosition(null);
            // Exit comment mode on click-away so a stale isAddingComment flag
            // doesn't re-open the New Comment box on every later highlight.
            setIsAddingComment(false);
          }
        }, 200);
      }
    };
    liveQuill.on('selection-change', handleSelectionChange);
    return () => {
      liveQuill.off('selection-change', handleSelectionChange);
    };
  }, [liveQuill]);

  // Intercept image paste (Ctrl+V) and drag-drop into the med-student editor.
  // Quill's default behaviour would embed a base64 data: URL in the delta, which
  // bloats the documents table row and storage file. Instead we upload to the
  // `files` bucket and insert only a public URL (same as EditableDocumentPage).
  useEffect(() => {
    if (!liveQuill) return;

    const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|svg|heic|avif)$/i;
    const isImageFile = (f) =>
      f && (f.type?.startsWith('image/') || IMAGE_EXT_RE.test(f.name || ''));
    const extFrom = (f) => {
      if (f.type?.includes('/')) {
        const p = f.type.split('/')[1];
        return p === 'jpeg' ? 'jpg' : p;
      }
      const m = (f.name || '').match(/\.([a-z0-9]+)$/i);
      return m ? m[1].toLowerCase() : 'png';
    };
    const dir = documentPath
      ? documentPath.substring(0, documentPath.lastIndexOf('/'))
      : 'New/MedADocs/images';

    const uploadAndInsert = async (file, insertIndex) => {
      const ext = extFrom(file);
      const contentType = file.type?.startsWith('image/') ? file.type : `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      const imagePath = `${dir}/img_${Date.now()}_medstud.${ext}`;
      try {
        const supabase = await getSupabase();
        const { error } = await supabase.storage
          .from(bucketName_files)
          .upload(imagePath, file, { upsert: true, contentType });
        if (error) { console.error('🖼️ [MedStud/Image] Upload failed:', error.message); return; }
        const { data: urlData } = supabase.storage.from(bucketName_files).getPublicUrl(imagePath);
        const publicUrl = urlData?.publicUrl;
        if (!publicUrl) return;
        liveQuill.insertEmbed(insertIndex, 'image', publicUrl, 'user');
        liveQuill.setSelection(insertIndex + 1, 0, 'user');
      } catch (err) {
        console.error('🖼️ [MedStud/Image] Error:', err);
      }
    };

    const pasteHandler = async (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItem = items.find((item) => item.type.startsWith('image/'));
      if (!imageItem) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const file = imageItem.getAsFile();
      if (!file) return;
      const range = liveQuill.getSelection(true) || { index: 0 };
      await uploadAndInsert(file, range.index);
    };

    const collectImageFiles = (dt) => {
      if (!dt) return [];
      const out = [];
      const seen = new Set();
      const add = (f) => {
        if (!isImageFile(f)) return;
        const key = `${f.name}-${f.size}`;
        if (!seen.has(key)) { seen.add(key); out.push(f); }
      };
      if (dt.files?.length) { for (let i = 0; i < dt.files.length; i++) add(dt.files[i]); }
      else if (dt.items?.length) {
        for (let i = 0; i < dt.items.length; i++) {
          if (dt.items[i].kind === 'file') add(dt.items[i].getAsFile());
        }
      }
      return out;
    };

    const dragOverHandler = (e) => {
      if (liveQuill.root.contains(e.target) && e.dataTransfer?.types && Array.from(e.dataTransfer.types).includes('Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const dropHandler = async (e) => {
      if (!liveQuill.root.contains(e.target)) return;
      const files = collectImageFiles(e.dataTransfer);
      if (files.length === 0) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const range = liveQuill.getSelection(true) || { index: liveQuill.getLength() - 1 };
      for (const file of files) {
        await uploadAndInsert(file, range.index);
      }
    };

    liveQuill.root.addEventListener('paste', pasteHandler, true);
    document.addEventListener('dragover', dragOverHandler, true);
    document.addEventListener('drop', dropHandler, true);
    return () => {
      liveQuill.root.removeEventListener('paste', pasteHandler, true);
      document.removeEventListener('dragover', dragOverHandler, true);
      document.removeEventListener('drop', dropHandler, true);
    };
  }, [liveQuill, documentPath]);

  // ── Assign task from a note selection ─────────────────────────────────────
  // taskAssign: { range, prefill } while the Assign modal is open.
  // openTaskLink: { taskId, rect } while a task link's card is showing; rect is
  // relative to liveQuill.container.parentNode (the card's portal target).
  const [taskAssign, setTaskAssign] = useState(null);
  const [openTaskLink, setOpenTaskLink] = useState(null);
  // The range to link, kept current while the modal is open (see effect below).
  const taskAssignRangeRef = useRef(null);

  const handleAssignTaskClick = () => {
    // Prefer Quill's live selection: `selectedRange` is not cleared when the
    // selection collapses, so on its own it can be a stale highlight.
    let live = null;
    try { live = liveQuill ? liveQuill.getSelection() : null; } catch (_) { live = null; }
    const range = live || selectedRange;
    if (!liveQuill || !range || range.length === 0) {
      toast({ title: 'Select some text in the note first', status: 'info', duration: 2500 });
      return;
    }
    const text = liveQuill.getText(range.index, range.length);
    taskAssignRangeRef.current = { index: range.index, length: range.length };
    setTaskAssign({
      range: { index: range.index, length: range.length },
      prefill: buildTaskPrefill(text),
    });
  };

  // While the Assign modal is open, remote collab edits and Process writes can
  // land before the selection. Move the range with every delta so the link
  // lands on the text the user picked; null = that text was deleted.
  useEffect(() => {
    if (!taskAssign || !liveQuill) return undefined;
    const onTextChange = (delta) => {
      taskAssignRangeRef.current = trackRange(taskAssignRangeRef.current, delta);
    };
    liveQuill.on('text-change', onTextChange);
    return () => liveQuill.off('text-change', onTextChange);
  }, [taskAssign, liveQuill]);

  const handleTaskCreated = (task) => {
    if (!liveQuill || !isTaskId(task?.id)) return;
    const range = taskAssignRangeRef.current;
    if (!range || range.length === 0) {
      toast({
        title: 'Task created, but the selected text was removed from the note',
        description: 'No link was added to the note.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }
    // As a 'user' change (even in View mode) so y-quill syncs it to peers and
    // autosave persists it.
    applyTaskLink(liveQuill, range, task.id);
  };

  // Task links open a staff card (names, emails, task text). Patients get no
  // click listener, no card, and plain-text styling (inert container class).
  const taskLinksInteractive = !patientReadOnly;
  useEffect(() => {
    if (!liveQuill) return undefined;
    return attachTaskLinkClicks(liveQuill, { interactive: taskLinksInteractive, onOpen: setOpenTaskLink });
  }, [liveQuill, taskLinksInteractive]);

  // A card for one chart must not survive a patient switch.
  useEffect(() => {
    setOpenTaskLink(null);
    setTaskAssign(null);
    taskAssignRangeRef.current = null;
  }, [documentPath]);

  const handleToolbarCommentClick = () => {
    if (selectedRange && selectedRange.length > 0) {
      // Apply the yellow highlight up front so the selected text stays visibly
      // marked while focus moves to the comment input. Applied 'silent' so it
      // doesn't dirty the doc; removed again if the user cancels (see effect below).
      if (liveQuill) {
        liveQuill.formatText(selectedRange.index, selectedRange.length, 'background', selectedHighlightColor, 'silent');
        pendingCommentHighlightRef.current = { ...selectedRange };
      }
      setIsAddingComment(true);
      setShowCommentsSidebar(true);
    } else {
      setShowCommentsSidebar(prev => !prev);
    }
  };

  // Remove the temporary highlight when the user leaves "add comment" without
  // saving (Cancel button, or click-away which clears isAddingComment). On a
  // successful save the ref is nulled first, so the highlight is kept.
  useEffect(() => {
    if (!isAddingComment && pendingCommentHighlightRef.current && liveQuill) {
      const { index, length } = pendingCommentHighlightRef.current;
      liveQuill.formatText(index, length, 'background', false, 'silent');
      pendingCommentHighlightRef.current = null;
    }
  }, [isAddingComment, liveQuill]);

  /**
   * Update comment indices when text is inserted or deleted
   * This ensures comment bubbles stay aligned with their highlighted text
   */
  const updateCommentIndicesOnTextChange = useCallback(async (delta, pageIndex) => {
    const currentComments = commentsRef.current;
    if (!currentComments || currentComments.length === 0) return;
    
    const commentUpdates = [];
    
    currentComments.forEach((comment) => {
      if (!comment.selection_range || comment.selection_range.pageIndex !== pageIndex) {
        return;
      }
      
      const commentIndex = comment.selection_range.index;
      const commentLength = comment.selection_range.length || 0;
      
      let newIndex = commentIndex;
      let newLength = commentLength;
      
      if (delta.ops) {
        let currentPos = 0;
        
        for (const op of delta.ops) {
          if (op.retain !== undefined) {
            if (typeof op.retain === 'number') {
              currentPos += op.retain;
            }
          } else if (op.insert !== undefined && typeof op.insert === 'string') {
            const insertLength = op.insert.length;
            if (currentPos < commentIndex) {
              newIndex += insertLength;
            } else if (currentPos >= commentIndex && currentPos < commentIndex + commentLength) {
              newLength += insertLength;
            }
            currentPos += insertLength;
          } else if (op.delete !== undefined) {
            const deleteLength = op.delete;
            if (currentPos < commentIndex) {
              newIndex = Math.max(0, newIndex - Math.min(deleteLength, commentIndex - currentPos));
            } else if (currentPos >= commentIndex && currentPos < commentIndex + commentLength) {
              const charsDeletedInHighlight = Math.min(deleteLength, commentIndex + commentLength - currentPos);
              newLength = Math.max(0, newLength - charsDeletedInHighlight);
            }
          }
        }
      }
      
      if (newIndex !== commentIndex || newLength !== commentLength) {
        commentUpdates.push({
          id: comment.id,
          selection_range: {
            ...comment.selection_range,
            index: newIndex,
            length: newLength
          }
        });
      }
    });
    
    if (commentUpdates.length > 0) {
      try {
        const supabase = await getSupabase();
        for (const update of commentUpdates) {
          await supabase
            .schema(chartDocSchema)
            .from('document_comments')
            .update({ selection_range: update.selection_range })
            .eq('id', update.id);
        }
        console.log(`Updated indices for ${commentUpdates.length} comments`);
        // Directly update local state with new indices to keep commentsRef in sync
        setComments(prevComments => {
          const updatedComments = prevComments.map(comment => {
            const update = commentUpdates.find(u => u.id === comment.id);
            return update ? { ...comment, selection_range: update.selection_range } : comment;
          });
          return updatedComments;
        });
      } catch (error) {
        console.warn('Error updating comment indices:', error);
      }
    }
  }, [chartDocSchema]);

  // Keep the ref updated with the latest function reference
  useEffect(() => {
    commentIndexUpdateRef.current = updateCommentIndicesOnTextChange;
  }, [updateCommentIndicesOnTextChange]);

  const handleSaveComment = async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    if (!newCommentText.trim() || !uuid) return;
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      const { data: userData } = await supabase
        .from('users')
        .select('name')
        .eq('id', user?.id)
        .single();
      
      const userName = userData?.name || user?.email || 'Anonymous';
      
      const commentData = {
        appointment_id: uuid,
        user_id: user?.id,
        user_name: userName,
        user_email: user?.email,
        content: newCommentText,
        highlight_text: selectedText,
        selection_range: selectedRange,
        highlight_color: selectedHighlightColor,
        status: 'open'
      };

      const { data, error } = await supabase
        .schema(chartDocSchema)
        .from('document_comments')
        .insert([commentData])
        .select();

      if (!error) {
        setIsAddingComment(false);
        setNewCommentText("");
        setSelectedHighlightColor('#FEF08A');
        setShowCommentsSidebar(true);
        fetchComments();
        
        // Keep the yellow highlight on the saved comment's text (already applied
        // when comment mode opened). Null the ref so the cleanup effect doesn't strip it.
        pendingCommentHighlightRef.current = null;
        if (selectedRange && liveQuill) {
          liveQuill.formatText(selectedRange.index, selectedRange.length, 'background', selectedHighlightColor);
          if (typeof onSaveDocument === 'function') {
            onSaveDocument();
          }
        }
      } else {
        console.error("Error saving comment:", error);
      }
    } catch (e) {
      console.error("Error saving comment:", e);
    }
  };

  const handleResolveComment = async (comment) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .schema(chartDocSchema)
        .from('document_comments')
        .update({ status: 'resolved' })
        .eq('id', comment.id);
      if (!error) {
        if (comment.selection_range && liveQuill) {
          const { index, length } = comment.selection_range;
          let targetIndex = index;
          let targetLength = length;
          if (comment.highlight_text) {
            const text = liveQuill.getText();
            const sub = text.substring(targetIndex, targetIndex + targetLength);
            if (sub !== comment.highlight_text) {
              let foundIdx = text.indexOf(comment.highlight_text);
              while (foundIdx !== -1) {
                const format = liveQuill.getFormat(foundIdx, 1);
                if (format && format.background) {
                  targetIndex = foundIdx;
                  targetLength = comment.highlight_text.length;
                  break;
                }
                foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
              }
            }
          }
          liveQuill.formatText(targetIndex, targetLength, 'background', false);
          if (typeof onSaveDocument === 'function') {
            onSaveDocument();
          }
        }
        fetchComments();
        if (activeCommentId === comment.id) {
          setActiveCommentId(null);
        }
      }
    } catch (e) {
      console.error("Error resolving comment:", e);
    }
  };

  const handleDeleteComment = async (comment) => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .schema(chartDocSchema)
        .from('document_comments')
        .delete()
        .eq('id', comment.id);
      if (!error) {
        if (comment.selection_range && liveQuill) {
          const { index, length } = comment.selection_range;
          let targetIndex = index;
          let targetLength = length;
          if (comment.highlight_text) {
            const text = liveQuill.getText();
            const sub = text.substring(targetIndex, targetIndex + targetLength);
            if (sub !== comment.highlight_text) {
              let foundIdx = text.indexOf(comment.highlight_text);
              while (foundIdx !== -1) {
                const format = liveQuill.getFormat(foundIdx, 1);
                if (format && format.background) {
                  targetIndex = foundIdx;
                  targetLength = comment.highlight_text.length;
                  break;
                }
                foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
              }
            }
          }
          liveQuill.formatText(targetIndex, targetLength, 'background', false);
          if (typeof onSaveDocument === 'function') {
            onSaveDocument();
          }
        }
        fetchComments();
        if (activeCommentId === comment.id) {
          setActiveCommentId(null);
        }
      }
    } catch (e) {
      console.error("Error deleting comment:", e);
    }
  };
  // ── End Comments and Highlights Handlers ────────────────────────────────


  // Keep AI chart text in sync with Yjs/Collaborative Quill (hydration +
  // remote edits) — onChange alone may miss programmatic updates.
  useEffect(() => {
    if (!collabActive || !liveQuill?.getContents) return undefined;
    const syncPlain = () => {
      try {
        const dc = liveQuill.getContents();
        aiDocumentPlainRef.current = quillDeltaToPlain(dc);
      } catch {
        /* non-fatal */
      }
    };
    syncPlain();
    liveQuill.on('text-change', syncPlain);
    return () => {
      liveQuill.off('text-change', syncPlain);
    };
  }, [liveQuill, collabActive]);

  // Task #228 — Brett ROS cards: bold positive complaints by post-processing
  // the rendered card descriptions. Hook listens to liveQuill text-change,
  // debounces, calls /api/ai/extract-ros-positives/, then DOM-wraps any
  // matching substrings in each .ros-card-desc with <strong>. Purely visual
  // (wraps don't survive textContent reads). Read-only fallback uses the
  // doc-body editor when live collab is off.
  useRosPositivesBolding(liveQuill);

  // The collab hook MUST be called unconditionally on every render (Rules of
  // Hooks). When the feature flag is off, we pass null/null to short-circuit
  // its internal effect — same render budget, zero network. The hook itself
  // also bails on a missing documentPath / userId.
  const collab = useDocumentCollab({
    documentPath: (collabActive && !safeShareMode) ? documentPath : null,
    userInfo: collabActive ? userInfo : { userId: null },
    quill: (collabActive && !safeShareMode) ? liveQuill : null,
    readOnly: isReadOnly || safeShareMode,
    patient: collabActive ? visit.patient : null,
    schema: chartDocSchema,
  });
  // Always-current handle on the collab session. Memoized callbacks that don't
  // list `collab` in their deps would otherwise close over an EARLY render's
  // value — before the WebSocket connected — and see `getYText()` as
  // undefined. For anything that replaces document content that is not a
  // harmless staleness: it silently takes the no-collab branch and merges
  // instead of replacing.
  const collabRef = useRef(collab);
  collabRef.current = collab;

  // The collab server closed this tab's socket because the document store
  // changed under the note (RDS went down or came back, RDS paused or
  // enabled) or the server held a different Yjs history of it. The hook
  // already rebuilt the editor from the server's current copy; tell the
  // user, because edits that had not reached the server are not in it
  // (the server parks them in Version History as "Unsaved").
  useEffect(() => {
    if (!collab.reloadCount) return;
    toast({
      title: 'Document reloaded from the server',
      description: 'The document store changed, so the editor now shows the server\'s current copy. '
        + 'Edits that had not reached the server were parked in Version History as "Unsaved".',
      status: 'warning',
      duration: 10000,
      isClosable: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab.reloadCount]);

  const presenceUsers = collabActive ? collab.awarenessUsers : legacyPresenceUsers;
  const handleAvatarClick = useCallback((user) => {
    if (!collabActive) return;
    if (!user || user.cursorIndex == null) {
      toast({
        title: `${user?.name || 'This user'} hasn’t moved their cursor yet`,
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    const r = collab.scrollToUser(user.userId);
    if (!r.ok && r.reason === 'no-cursor') {
      toast({
        title: `${user.name} hasn’t moved their cursor yet`,
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
    }
  }, [collab, toast, collabActive]);
  // Live-collab connection gating. While the WebSocket is down, every
  // keystroke would go into a local Yjs doc the server can't see — fine for
  // the sub-second blip the hook graces over, but a real risk once an
  // outage drags on, so we hard-block input instead of letting someone
  // write a paragraph into a void. `editingLocked` already carries that
  // grace period and is false whenever collab isn't wired up at all.
  const collabTypingLocked = collabActive && !safeShareMode && !!collab.editingLocked;
  const inputBlocked = effectiveReadOnly || safeShareMode || collabTypingLocked;
  // Only *announce* the lock when the user could otherwise be typing. In
  // read mode or the patient portal the editor is already locked, so a
  // "typing is paused" banner underneath the read-mode banner is noise.
  const collabBlocksEditing = collabTypingLocked && !effectiveReadOnly;

  // Auto-versioning: wire VersioningSystem to the Quill editor so edits create
  // backups per the doctor's existing rules (char-delta thresholds, inactivity
  // timer, pre/post-appointment snapshots). One instance per document path.
  const versioningSystemRef = useRef(null);
  // Post-sign edit tracking refs (declared here so the versioning effect can
  // populate postSignVsRef). Used by onDocumentChange, finishPostSignEdit, and
  // the exit-flush effect below.
  const postSignVsRef = useRef(null);
  const postSignDirtyRef = useRef(false);
  const signedEditGrantedRef = useRef(false);
  const postSignLatestDeltaRef = useRef(null);
  useEffect(() => {
    if (!documentPath || !documentBodyRef) return;
    const vs = createVersioningSystem(documentPath, documentBodyRef, chartDocSchema);
    versioningSystemRef.current = vs;
    postSignVsRef.current = vs; // survives cleanup so the exit-flush can still write
    console.log('[sign-lock] versioning ATTACHED', { documentPath, chartDocSchema });
    return () => {
      console.log('[sign-lock] versioning DETACHED', { documentPath });
      try { vs.destroy?.(); } catch {}
      versioningSystemRef.current = null;
    };
  // We deliberately key only on documentPath here. setCurrentUser + version
  // count seeding run in their own effects so user-info hydration doesn't
  // tear down and recreate the system mid-edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentPath]);

  // Push the active user onto the versioning system as soon as we know who
  // they are. Done as a separate effect so a slow users-table fetch doesn't
  // recreate the system. Without this the first backup row has empty
  // user_name → "User information not available" in the history modal.
  useEffect(() => {
    const vs = versioningSystemRef.current;
    if (!vs || !userInfo.userId) return;
    try {
      vs.setCurrentUser?.({
        id: userInfo.userId,
        name: userInfo.name || userInfo.email || `User ${userInfo.userId.slice(0, 8)}`,
        email: userInfo.email,
      });
    } catch (e) { console.warn('[VS] setCurrentUser failed:', e?.message); }
  }, [userInfo.userId, userInfo.name, userInfo.email]);

  // Version count drives the V<n> badge in the header bar. Cross-schema: the
  // count AND the whole VersioningSystem are threaded with `chartDocSchema`, so a
  // FOREIGN (linked-provider) patient's version rows + blobs read AND write under
  // THEIR schema — not the viewer's home. `documentPath` already carries the
  // foreign schema segment, so the effect that recreates the system on path
  // change also picks up a schema change. (Was final-review M1; now fixed.)
  const { count: versionCount, refresh: refreshVersionCount } = useVersionCount(documentPath, chartDocSchema);

  // Signed state drives the header's locked "Signed" button. Backed by the
  // signed version row, so it persists across reload/logout and reads the same
  // for every viewer of this note.
  const {
    signed: isSigned,
    signedBy,
    signedAt,
    signatureText: signedSignatureText,
    isSigner,
    password: signerPassword,
    verifyPassword,
    refresh: refreshSignedStatus,
  } = useSignedStatus(documentPath, userInfo.email, chartDocSchema);

  // Staff may assign a task from highlighted note text in View mode too; the
  // link is applied as a one-off edit. Not for patients, not in Safe-Share
  // (autosave is off), and not on a signed note unless post-sign edit is granted.
  const canAssignTasks = !patientReadOnly && !safeShareMode && !(isSigned && !signedEditGranted);

  // Rich version status from VersioningSystem.getVersionDisplayState():
  // { savedNumber, isOutOfDate, isStaleVersion, label, loading }. Lets the
  // header badge show qualifiers ("v2 · edited", "v3 · behind", etc.) on top
  // of the bare V<n> count.
  const [documentVersionStatus, setDocumentVersionStatus] = useState(null);

  // Push a fresh count into the badge each time the versioning system creates
  // a backup (auto OR manual). Without this, V<n> only updates after a page
  // refresh — the badge would sit at V1 even after several auto-versions had
  // already been written to `document_backups`. _doCreateVersion calls
  // notifyVersionDisplay AFTER the DB insert resolves, so refreshing here
  // returns the new count.
  useEffect(() => {
    const vs = versioningSystemRef.current;
    if (!vs) return;
    vs.setVersionDisplayListener((state) => {
      setDocumentVersionStatus(state || null);
      refreshVersionCount();
    });
    return () => { vs.setVersionDisplayListener(null); };
  // Re-bind when the documentPath changes (the effect above tears down and
  // recreates the versioning system on path change, so we need a fresh hook).
  }, [documentPath, refreshVersionCount]);

  // Seed the versioning system's internal counter with the existing version
  // count so the next auto-version is numbered correctly. Without this, the
  // first auto-version created in a session always reads `savedVersionCount=0`
  // and gets stamped "Version 1" even when there are older backups in the DB.
  useEffect(() => {
    const vs = versioningSystemRef.current;
    if (!vs || versionCount == null) return;
    // savedVersionCount is what _doCreateVersion falls back to when the cache
    // hasn't been hydrated yet (see versioningSystem.js _doCreateVersion).
    vs.savedVersionCount = versionCount;
  }, [versionCount]);

  // Persist the latest Quill delta to Supabase Storage at the same path
  // usePatientDocument reads from. Upserts the existing JSON envelope so
  // sections/lastUpdated metadata isn't lost.
  //
  // The autosave uses the service-role admin client because storage RLS on the
  // `files` bucket only allows the original creator to update — medstudent
  // edits on an existing MedADocs JSON hit "new row violates row-level
  // security policy" with the user-scoped client. The versioning system uses
  // the same admin client for its backup uploads, so this stays consistent.
  const persistDelta = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    if (safeShareMode) {
      console.log('Safe-Share active: autosave aborted.');
      return;
    }
    // When collab is OFF: existing behavior — bail without an envelope
    // (visit.document is the source of truth for sections/lastUpdated).
    // When collab is ON: visit.document is null by design (T15 skips the
    // client-side fetch); synthesize a minimal envelope so the .json write
    // path still produces a valid file for AI/version readers.
    if (savingRef.current || !documentPath) return;
    if (!collabActive && !visit.document) return;
    // Task #G: a Reset to Seed in flight (or just-completed) must NOT be
    // overwritten by autosaves carrying the pre-reset processedSources +
    // delta from the dying tab session. The Reset button sets this flag
    // before POSTing and clears it on reload (it's per-session in memory,
    // so the next page load starts fresh). When set, every autosave bails
    // until the page reloads. Belt-and-suspenders: also a sessionStorage
    // marker so that an autosave fired right after a reset-and-before-reload
    // window also bails.
    if (window.__vmaiResetInFlight) return;
    try {
      if (sessionStorage.getItem('vmai:reset-in-flight') === '1') return;
    } catch (_) { /* sessionStorage blocked — ignore */ }
    // If the patient changed since this save was scheduled, the path in our
    // closure now belongs to a different file than the editor's current
    // content. Aborting here prevents writing one patient's content into
    // another patient's storage file.
    if (documentPathRef.current !== documentPath) {
      console.warn('[clinic doc] autosave aborted — path changed', {
        scheduledFor: documentPath,
        currentPath: documentPathRef.current,
      });
      return;
    }
    const delta = pendingDeltaRef.current;
    if (!delta) return;
    savingRef.current = true;
    pendingDeltaRef.current = null;
    try {
      const sb = getSupabaseAdmin();
      // The stored ledger may have gained entries since this page read it (the
      // mobile app's Master Note Accept stamps the .json directly): keep them
      // rather than writing this page's older copy over them. If the read fails,
      // save with this page's copy, as before.
      let stored = null;
      try {
        stored = readLedger(await downloadFreshJson(sb, bucketName_files, documentPath));
      } catch (_e) { /* keep this page's ledger */ }
      const ledger = stored
        ? withLedgerAddedElsewhere(processedSourcesRef.current, stored, ledgerSyncedRef.current)
        : processedSourcesRef.current;
      const updatedDoc = mergeLedgerIntoEnvelope({
        ...(visit.document || { sections: [] }),
        delta,
        lastUpdated: new Date().toISOString(),
      }, ledger);
      const blob = new Blob([JSON.stringify(updatedDoc)], { type: 'application/json' });
      const { error } = await sb.storage
        .from(bucketName_files)
        .upload(documentPath, blob, { upsert: true });
      if (error) throw error;
      ledgerSyncedRef.current = ledger;
      // Show what other writers added (their sources now read as processed here too).
      const current = processedSourcesRef.current;
      const shown = withLedgerAddedElsewhere(current, ledger, []);
      if (shown.length !== current.length && documentPathRef.current === documentPath) {
        processedSourcesRef.current = shown;
        setProcessedLedger(shown);
      }
      setSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      // Surface but don't crash — user can keep typing; next debounce tries again.
      console.warn('[MedStudentClinic] autosave failed:', err.message);
    } finally {
      savingRef.current = false;
      // If another change came in while we were saving, schedule another save —
      // but only if we still belong to the same patient. Without this guard,
      // a switch-during-save could re-arm `persistDelta` (closure = OLD path)
      // with pendingDeltaRef pointing at the NEW patient's content, and the
      // re-armed timer would clobber the OLD patient's file.
      if (pendingDeltaRef.current && documentPathRef.current === documentPath && !savingRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(persistDelta, 1500);
      }
    }
  }, [documentPath, visit.document, patientReadOnly, showPatientReadOnlyToast, safeShareMode, collabActive]);

  const toggleSafeShareMode = async () => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) return;

    if (safeShareMode) {
      // Toggling OFF: Restore original content
      if (originalContents) {
        editor.setContents(originalContents);
      }
      setOriginalContents(null);
      setSafeShareMode(false);
      
      toast({
        title: "Safe-Share Disabled",
        description: "Original document content restored.",
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    } else {
      // Toggling ON:
      // Save changes first if any
      if (!isReadOnly && pendingDeltaRef.current) {
        await persistDelta();
      }

      setIsDeidentifying(true);
      
      // Set safeShareMode to true first to disconnect Yjs collaboration
      setSafeShareMode(true);

      // Defer the rest to allow React to re-render and clean up the WebSocket connection
      setTimeout(async () => {
        try {
          const currentContents = editor.getContents();
          const plainText = editor.getText();

          // Call the Django de-identification service
          const backendUrl = getBackendURL();
          const response = await fetch(`${backendUrl}/api/deidentify/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              text: plainText,
              method: safeShareMethod,
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to de-identify text: ${response.statusText}`);
          }

          const data = await response.json();
          const deidentifiedText = data.deidentified_text;

          // Store original contents
          setOriginalContents(currentContents);
          
          // Update editor text
          editor.setText(deidentifiedText);

          toast({
            title: "Safe-Share Enabled",
            description: `Document de-identified using '${safeShareMethod}' method. Auto-save disabled.`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          console.error("Error enabling Safe-Share mode:", error);
          // Revert safeShareMode to false on failure
          setSafeShareMode(false);
          toast({
            title: "Safe-Share Error",
            description: `Failed to de-identify document: ${error.message}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        } finally {
          setIsDeidentifying(false);
        }
      }, 300);
    }
  };

  const onDocumentChange = useCallback((delta) => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    pendingDeltaRef.current = delta;
    if (collabActive) {
      aiDocumentPlainRef.current = quillDeltaToPlain(delta);
    }
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(persistDelta, 1500);
    // Post-sign edit session: stash the latest content + mark dirty. The tracked
    // version is written on Done or on exit — never per keystroke.
    if (signedEditGrantedRef.current) {
      postSignDirtyRef.current = true;
      postSignLatestDeltaRef.current = delta;
    }
  }, [persistDelta, patientReadOnly, showPatientReadOnlyToast, collabActive]);

  // Flush any pending save when the page unmounts or patient switches.
  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
    if (pendingDeltaRef.current && !savingRef.current) persistDelta();
  }, [persistDelta]);

  // ── Action handlers wired to PatientHeaderBar ────────────────────────────

  const onForms = useCallback(() => setIsFormsOpen(true), []);

  // Doctor-side medication management. Clicking the meds button in the header
  // opens ClinicPatientMedsModal — a doctor-side mirror of the patient's
  // /meds page that uses the clinic_* proxy endpoints so the doctor's edits
  // land in the same `public.patients_with_ehrid.meds` store the patient's
  // /meds page reads from.
  const [isClinicMedsOpen, setIsClinicMedsOpen] = useState(false);
  const canOpenClinicMeds = !patientReadOnly && Boolean(visit.patient?.id) && visit.patient?.id !== 'unknown';
  const onOpenClinicMeds = useCallback(() => {
    if (!canOpenClinicMeds) return;
    setIsClinicMedsOpen(true);
  }, [canOpenClinicMeds]);

  // Orders & followups. Read-only: `clinical_action_items` is already
  // populated by the copilot when a recording's snapshot is saved, and by
  // staff in the Compliance tab. This just surfaces the patient's rows in the
  // chart so the clinician doesn't have to leave the note to see what was
  // ordered. Unlike Meds it stays available in read-only mode — nothing here
  // writes.
  const [isClinicOrdersOpen, setIsClinicOrdersOpen] = useState(false);
  const canOpenClinicOrders = Boolean(visit.patient?.id) && visit.patient?.id !== 'unknown';
  const onOpenClinicOrders = useCallback(() => {
    if (!canOpenClinicOrders) return;
    setIsClinicOrdersOpen(true);
  }, [canOpenClinicOrders]);
  // A task's "Open in chart" (Messages → a paperwork task):
  // ?orders=paperwork&paperwork=<formId> opens Orders on that form, once per
  // arrival (closing Orders forgets it).
  const { link: ordersLink, clear: clearOrdersLink, leave: leaveOrdersLink } = useOrdersDeepLink(canOpenClinicOrders);
  useEffect(() => {
    if (ordersLink) setIsClinicOrdersOpen(true);
  }, [ordersLink]);

  // Restore: opens a confirmation prompt; on confirm we roll the editor back
  // to the most recent backup_path in `document_backups`. The Tools/header
  // "version history" entry point still opens the full modal (onVersionHistory).
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const onRestore = useCallback(() => setIsRestoreConfirmOpen(true), []);
  const onVersionHistory = useCallback(() => setIsVersionsOpen(true), []);

  const processDiffPanelSlotRef = useRef(null);
  const [processPinAudioSessionId, setProcessPinAudioSessionId] = useState(null);
  const [processPinReportPath, setProcessPinReportPath] = useState(null);
  const [processAutoGenerate, setProcessAutoGenerate] = useState(false);

  // Parked Process review — surfaced as a "Review" tab in the right bar
  // (alongside AI Chat / Reports / Live Transcript / Copilot) after the
  // user clicks "Park in sidebar" from the review screen header. Lets the
  // clinician keep editing the note while the diff waits for decisions;
  // closing the modal doesn't lose the diff. Shape mirrors what
  // ProcessModal lifts via onParkReview:
  //   { edits, approvals, debugInfo, generatedRequestItems, selectedItems,
  //     documentText }
  const [parkedReview, setParkedReview] = useState(null);
  const [parkedApplying, setParkedApplying] = useState(false);
  const parkedReviewNonceRef = useRef(0);
  // PHI guard: the patient whose chart was open when Generate Master Note was
  // kicked off. A backgrounded generation resolves into whatever chart is open
  // when it finishes; without pinning we'd apply patient A's note into patient
  // B's chart. Mirrors the recorder's live-transcript `uuid` guard (#269).
  const masterNoteOriginRef = useRef(null);
  // Unpark payload — handed to ProcessModal on the next open so it can
  // restore the source-picker selections (or the AI's edits in review
  // phase) the user had parked. Cleared once the modal consumes it so
  // re-opens of Process don't re-apply stale state.
  const [unparkState, setUnparkState] = useState(null);

  // Generation state — lifted from ProcessModal so the live "Generating…"
  // timer continues to count across the unpark/park cycle. Before this
  // was lifted, both modal and ParkedReviewTab carried their own
  // `generating` state (with their own setInterval for the timer chip).
  // When the user parked mid-generation or unparked mid-generation, the
  // active surface unmounted, the local state was lost, and the next
  // surface mounted showing the timer reset to 0:00 — confusing because
  // the user already saw 1:30 on the clock.
  //
  // Now: the parent owns the running flag and the start timestamp. Both
  // surfaces read it from props and the timer computes elapsed seconds
  // from `generationStartedAt`, so parking/unparking during a generation
  // produces zero clock discontinuity.
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStartedAt, setGenerationStartedAt] = useState(null);
  const beginGeneration = useCallback(() => {
    setIsGenerating(true);
    setGenerationStartedAt(Date.now());
  }, []);
  const endGeneration = useCallback(() => {
    setIsGenerating(false);
    setGenerationStartedAt(null);
  }, []);

  // Auto-park a fresh Process flow in the right sidebar — clicking the
  // toolbar Process button no longer opens a floating modal. Instead it
  // materializes a sources-phase parked review inline in the right bar's
  // Review tab (so the user stays next to the note while picking sources
  // and generating the diff). Modal-based flows (Audio Manager's
  // "Process this recording", Reports modal's per-row "Process this
  // report") are unchanged — those keep using ProcessModal in floating
  // mode to surface the AI's diff immediately for a specific pin.
  const openProcessInSidebar = useCallback(() => {
    // A review is already parked — don't overwrite the user's selection
    // or generated diff. Just navigate to the Review tab so they can
    // continue work in the parked flow.
    if (parkedReview) {
      setIsProcessOpen(false);
      setRightCollapsed(false);
      setRightTab('review');
      return;
    }
    // Fresh auto-park — compute default selections the same way
    // ProcessModal does on its open transition so the sidebar behavior
    // exactly matches the in-modal source picker.
    const { selectedIds, expanded } = computeDefaultSourceSelection(sourceGroups);
    parkedReviewNonceRef.current += 1;
    setParkedReview({
      phase: 'sources',
      sourceGroups,
      selectedIds,
      expanded,
      useTemplate: true,
      pinAudioSessionId: null,
      pinReportPath: null,
      autoGenerate: false,
      nonce: parkedReviewNonceRef.current,
    });
    setIsProcessOpen(false);
    setProcessPinAudioSessionId(null);
    setProcessPinReportPath(null);
    setProcessAutoGenerate(false);
    setRightCollapsed(false);
    setRightTab('review');
  }, [parkedReview, sourceGroups]);

  const onProcess = useCallback(() => {
    openProcessInSidebar();
  }, [openProcessInSidebar]);

  useEffect(() => {
    const handler = (event) => {
      const sessionId = event?.detail?.sessionId || null;
      const autoGenerate = !!event?.detail?.autoGenerate;
      setProcessPinAudioSessionId(sessionId ? String(sessionId) : null);
      setProcessPinReportPath(null);
      setProcessAutoGenerate(autoGenerate);
      try { visit.refetch?.(); } catch (_e) { /* non-fatal */ }
      setIsProcessOpen(true);
      if (autoGenerate) setRightTab('copilot');
    };
    const reportHandler = (event) => {
      const filePath = event?.detail?.filePath || null;
      const autoGenerate = !!event?.detail?.autoGenerate;
      setProcessPinReportPath(filePath ? String(filePath) : null);
      setProcessPinAudioSessionId(null);
      setProcessAutoGenerate(autoGenerate);
      try { visit.refetch?.(); } catch (_e) { /* non-fatal */ }
      setIsProcessOpen(true);
      if (autoGenerate) setRightTab('copilot');
    };
    window.addEventListener('vmed:openProcessForRecording', handler);
    window.addEventListener('vmed:openProcessForReport', reportHandler);
    return () => {
      window.removeEventListener('vmed:openProcessForRecording', handler);
      window.removeEventListener('vmed:openProcessForReport', reportHandler);
    };
  }, [visit]);

  useEffect(() => {
    const handler = (event) => {
      const action = event?.detail?.action;
      if (!action) return;
      const evVisitId = event?.detail?.visitId;
      if (evVisitId && visit?.patient?.id && evVisitId !== visit.patient.id) return;
      switch (action) {
        case 'generate':
          // Keyboard / hotkey shortcut for the Process button — same
          // auto-park flow as clicking the toolbar Process. Mirrors
          // onProcess above so ⌘K callers land in the same parked
          // Review tab.
          openProcessInSidebar();
          break;
        case 'record':
          try {
            const btn = document.querySelector('[data-recording-bubble-trigger]') || document.getElementById('recording-bubble-trigger');
            if (btn && typeof btn.click === 'function') btn.click();
          } catch (_) { /* no-op */ }
          break;
        case 'ask': setRightTab('copilot'); break;
        case 'icd':
          // Open the Add Problem dialog (mirrors the header-bar Add Problem
          // button). Just switching to the Copilot tab did nothing visible
          // for the user — DoctorPortalBar's "ICD-10" chip + 'I' shortcut
          // now both funnel through here. AddProblemButton in
          // PatientHeaderBar listens for the event and opens itself.
          try { window.dispatchEvent(new CustomEvent('vmai:add-icd-problem')); } catch (_) { /* no-op */ }
          break;
        case 'forms': setRightTab('forms'); break;
        case 'sign':
          try {
            const signBtn = document.querySelector('[data-sign-save-button]');
            if (signBtn) signBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (_) { /* no-op */ }
          break;
        default: break;
      }
    };
    window.addEventListener('vmai:doctor-action', handler);
    return () => window.removeEventListener('vmai:doctor-action', handler);
  }, [visit, openProcessInSidebar]);

  const handleApplyProcessEdits = useCallback(async (ops, processedItems) => {
    if (patientReadOnly) { showPatientReadOnlyToast(); return; }
    const editor = documentBodyRef.current?.getEditor?.(0);
    const yText = collabActive ? collab?.getYText?.() : null;
    const yDoc = collabActive ? collab?.getYDoc?.() : null;
    applyOpsToEditor({ ops, yText, yDoc, editor });
    // Stamp the ledger for every included source, then persist (carries the new delta + ledger).
    const nextLedger = mergeLedger(processedSourcesRef.current, processedItems, new Date().toISOString());
    processedSourcesRef.current = nextLedger;
    setProcessedLedger(nextLedger);
    if (editor) {
      pendingDeltaRef.current = editor.getContents();
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(persistDelta, 250);
    }
  }, [collab, persistDelta, patientReadOnly, showPatientReadOnlyToast, collabActive]);

  // Master Note flow (Leejoe 2026-07-01) — physician clicked "Accept & replace"
  // in the ProcessModal preview. Replace the editor content wholesale with the
  // Master Note text. Uses the yjs-aware setContents pattern (feedback_yjs_
  // setcontents_doubles memory) to avoid the double-stacking bug when
  // collabActive is on: delete YText + applyDelta in one yDoc.transact
  // for collab; empty-then-set for non-collab.
  // Task #280 — latest live-transcript text for THIS chart's patient.
  // RecordingBubble re-dispatches popup transcript frames tagged with the
  // recording's patientId; we keep the text only when it matches the open
  // chart, so a recorder running on another patient can never leak into this
  // one. Cleared on every patient switch. Master Note uses it as a fallback
  // when the live transcript was never saved (Chen/Banh 2026-07-07).
  const [liveTranscriptText, setLiveTranscriptText] = useState('');
  // Set when tagged live-transcript frames are flowing to a DIFFERENT patient
  // than the open chart — the recorder is transcribing this visit into the
  // wrong chart (Gallegos audio saved under Hollingsworth, 2026-07-08). The
  // popup's red banner wasn't enough because the popup is often behind other
  // windows; this surfaces the mismatch in the MAIN window. Auto-clears when
  // the frames stop (~60s) or when matching frames resume.
  const [foreignRecordingActive, setForeignRecordingActive] = useState(false);
  useEffect(() => {
    setLiveTranscriptText('');
    setForeignRecordingActive(false);
    let foreignClearTimer = null;
    const onLive = (e) => {
      const d = e?.detail || {};
      if (!d.text) return;
      // Strict match — untagged frames (old popups) are dropped, not guessed.
      if (d.patientId == null) return;
      if (String(d.patientId) !== String(uuid)) {
        setForeignRecordingActive(true);
        clearTimeout(foreignClearTimer);
        foreignClearTimer = setTimeout(() => setForeignRecordingActive(false), 60000);
        return;
      }
      setForeignRecordingActive(false);
      setLiveTranscriptText(String(d.text));
    };
    window.addEventListener('liveTranscriptUpdate', onLive);
    return () => {
      clearTimeout(foreignClearTimer);
      window.removeEventListener('liveTranscriptUpdate', onLive);
    };
  }, [uuid]);

  // Build a Quill delta from Master Note text. Shared by Accept-&-replace and
  // Save-as-version so both produce identical rich formatting (ICD problem
  // blots, section headings, bold labels). Reads the current editor to harvest
  // note-as-template styling. Returns null if the editor isn't ready.
  const buildMasterNoteDelta = useCallback((newText) => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) return null;
    const plain = String(newText || '');
    // Quill delta from the Master Note text. Trailing '\n' is required or
    // Quill complains ('Documents must end with a newline'). Three transforms
    // on the way in — Quill doesn't parse markdown:
    //   1. A&P problem-header lines (`**N. Diagnosis (ICD10)**`) become
    //      interactive icd-problem blots — same embed Add Problem inserts —
    //      so the code chip / pencil-edit / NextGen-paste behavior survives
    //      a whole-note replace. Leejoe 2026-07-04: "not getting the ICD 10
    //      codes" after first Accept — codes were plain text, not blots.
    //   2. Remaining `**bold**` spans expand into bold-attributed ops.
    // Lines whose parens hold no ICD-shaped token ("(suspected)") stay as
    // bold text — no blot without a code.
    const insertText = plain.endsWith('\n') ? plain : plain + '\n';
    // Qualifier suffixes after the code are allowed and folded into the
    // display name — "**6. Gout, unspecified (M10.9) — history uncertain**"
    // previously stayed plain text because the line had to END at the code.
    // Accepts '**# Name (CODE)**' (current, unnumbered — Leejoe 2026-07-07:
    // problems reorder freely without stale numbering) and legacy
    // '**N. Name (CODE)**'. Qualifier text after the code survives.
    const PROBLEM_LINE_RE = /^\*\*(?:(\d+)\.|#)\s+(.+)\(([^()]+)\)\s*([^()]*?)\s*\*\*$/;
    const ICD_CODE_RE = /[A-Z]\d[\dA-Z]*(?:\.[\dA-Z]+)?/;
    //   3. Template section headers — standalone ALL-CAPS lines the engine
    //      preserves verbatim ("VITALS", "ASSESSMENT & PLAN", "PFT / SPIROMETRY")
    //      — become styled Quill headings: bold+colored text on an h2 block.
    //      Bold+color is what lineIsStyledHeading/section logic recognizes;
    //      the h2 block is what heading-based section navs scan. Short acronym
    //      headers are whitelisted so sub-labels like "CXR:" stay plain.
    //      Colors follow Brett's section-bar palette (clinic.css +
    //      StructuredSectionRenderer): Vitals orange-600, ROS purple-600,
    //      everything else his hl-blue.
    const HEADING_COLOR = '#2563eb';
    const HEADING_COLOR_BY_SECTION = [
      [/^VITALS/, '#ea580c'],
      [/^(SLEEP\s+)?(ROS\b|REVIEW OF SYSTEMS)/, '#9333ea'],
    ];
    const headingColor = (name) => {
      for (const [re, c] of HEADING_COLOR_BY_SECTION) if (re.test(name)) return c;
      return HEADING_COLOR;
    };
    const SHORT_HEADERS = new Set(['HPI', 'ROS', 'PE', 'PMH', 'PSH', 'FH', 'SH', 'CC', 'AP', 'A&P', 'PLAN', 'LABS', 'MEDS', 'EXAM']);
    // Note-as-template for STYLING too (Quiles 2026-07-10): carried-forward
    // report bodies are full of ALL-CAPS lines ("FINDINGS AND IMPRESSION:",
    // "TECHNIQUE:") that must stay body text. Only lines that are ALREADY
    // headings in the current note may render as section headings. Blank
    // charts (no headings yet) fall back to the caps-shape heuristic.
    const normalizeHeader = (t) => t.replace(/:\s*$/, '').replace(/\s+/g, ' ').trim().toUpperCase();
    const existingHeaders = new Set();
    // Inline labels ("HPI:", "Imaging:", "1/12/26:") that were BOLD at the
    // start of a line in the pre-Accept note — the engine round-trips plain
    // text, so re-applying them here is the only way label bolding survives
    // a regenerate (Yearwood 2026-07-10: "HPI was not bold").
    const boldLeadLabels = new Set();
    try {
      let lineBuf = '';
      let leadRun = null; // first text run of the current line
      for (const op of (editor.getContents() || {}).ops || []) {
        if (typeof op.insert !== 'string') { lineBuf = ''; leadRun = null; continue; }
        const parts = op.insert.split('\n');
        for (let pi = 0; pi < parts.length; pi += 1) {
          if (lineBuf === '' && leadRun === null && parts[pi]) {
            leadRun = { text: parts[pi], bold: !!(op.attributes && op.attributes.bold) };
          }
          lineBuf += parts[pi];
          if (pi === parts.length - 1) continue; // no newline after last part
          if (op.attributes && op.attributes.header && lineBuf.trim()) {
            existingHeaders.add(normalizeHeader(lineBuf));
          }
          if (leadRun && leadRun.bold) {
            const ci = leadRun.text.indexOf(':');
            const label = (ci > 0 ? leadRun.text.slice(0, ci) : leadRun.text.replace(/:\s*$/, '')).trim();
            if (label && label.length <= 40) boldLeadLabels.add(label.toUpperCase());
          }
          lineBuf = '';
          leadRun = null;
        }
      }
    } catch (_) { /* harvest is best-effort */ }
    const isSectionHeaderLine = (text) => {
      const bare = text.replace(/:\s*$/, '').trim();
      if (bare.length > 40 || !/^[A-Z][A-Z &/()\-]*$/.test(bare)) return false;
      if (!(bare.length >= 5 || SHORT_HEADERS.has(bare.replace(/\s+/g, '')))) return false;
      if (existingHeaders.size > 0) return existingHeaders.has(normalizeHeader(text));
      return true;
    };
    const ops = [];
    const lines = insertText.split('\n');
    for (let li = 0; li < lines.length - 1; li += 1) {
      const line = lines[li];
      const m = line.trim().match(PROBLEM_LINE_RE);
      const parenContent = m ? m[3].trim() : '';
      if (m && ICD_CODE_RE.test(parenContent)) {
        ops.push({
          insert: {
            [ICD_PROBLEM_BLOT_NAME]: {
              id: `icd_${Math.random().toString(36).slice(2, 9)}`,
              problemNumber: m[1] ? Number(m[1]) : 0, // 0 = unnumbered → chip renders '#'
              displayName: `${m[2].trim()}${m[4] ? ` ${m[4].trim()}` : ''}`.trim(),
              // Keep the full paren content — multi-code problems like
              // "E06.3 / E05.80" show both codes on the chip.
              icd10Code: parenContent,
            },
          },
        });
      } else {
        const stripped = line.trim().replace(/^\*\*|\*\*$/g, '');
        if (isSectionHeaderLine(stripped)) {
          ops.push({ insert: stripped, attributes: { bold: true, color: headingColor(stripped) } });
          ops.push({ insert: '\n', attributes: { header: 2 } });
        } else {
          // Re-bold harvested inline labels at line start — note-as-template
          // for labels too ("HPI:", "Imaging:", date lines keep their bold).
          let rest = line;
          const lm = line.match(/^([^:*[\]]{1,40}):/);
          if (lm && boldLeadLabels.has(lm[1].trim().toUpperCase())) {
            ops.push({ insert: `${lm[1]}:`, attributes: { bold: true } });
            rest = line.slice(lm[0].length);
          }
          for (const op of expandMarkdownInsertToOps(`${rest}\n`)) {
            // 4. [edit: ...] markers keep their amber highlight AFTER Accept
            //    (Leejoe 2026-07-06: "hard to find all of them in a long
            //    note"). Same token regex as the preview's renderEditMarkers;
            //    the highlight leaves the note when the doctor deletes the
            //    token while addressing it.
            if (typeof op.insert === 'string' && op.insert.includes('[edit:')) {
              for (const part of op.insert.split(/(\[edit:[^\]]*\])/g)) {
                if (!part) continue;
                if (/^\[edit:[^\]]*\]$/.test(part)) {
                  // Explicit dark text — in dark mode the editor's light text
                  // is unreadable on the light amber highlight.
                  ops.push({ insert: part, attributes: { ...(op.attributes || {}), background: '#fde68a', color: '#78350f' } });
                } else {
                  ops.push(op.attributes ? { insert: part, attributes: op.attributes } : { insert: part });
                }
              }
            } else {
              ops.push(op);
            }
          }
        }
      }
    }
    // BlockEmbeds carry their own line; only pad when the doc would
    // otherwise end without a trailing newline.
    const lastOp = ops[ops.length - 1];
    if (!lastOp || typeof lastOp.insert !== 'string' || !lastOp.insert.endsWith('\n')) {
      ops.push({ insert: '\n' });
    }
    const delta = { ops };
    return delta;
  }, []);

  // Master Note PHI guard — never apply/save a note generated for another chart.
  // originPatientId travels on the parked review; masterNoteOriginRef is the
  // kickoff fallback for when Unpark already consumed the parked payload.
  // Mirrors the recorder's `uuid` guard (#269).
  const masterNoteChartOk = useCallback((actionVerb) => {
    const mnOriginId = parkedReview?.originPatientId ?? masterNoteOriginRef.current?.id ?? null;
    if (mnOriginId != null && String(mnOriginId) !== String(uuid)) {
      const mnWho = parkedReview?.originPatientName || masterNoteOriginRef.current?.name || 'another patient';
      toast({
        title: `Master Note not ${actionVerb}`,
        description: `This note was generated for ${mnWho}. Open that chart to ${actionVerb} it.`,
        status: 'warning', duration: 6000, isClosable: true,
      });
      return false;
    }
    return true;
  }, [parkedReview, uuid, toast]);

  // Accept & replace: overwrite the editor with the Master Note.
  // Auto-snapshot the just-applied Master Note into the doc's version history as
  // "Master Note Version X" (X = next after however many this doc already has).
  // Fire-and-forget from the apply path — there is no user-facing button.
  const saveMasterNoteVersion = useCallback(async (delta) => {
    const vs = versioningSystemRef.current;
    if (!vs || !delta) return;
    try {
      const existing = await vs.countVersionsByNamePrefix(MASTER_NOTE_VERSION_PREFIX);
      const name = `${MASTER_NOTE_VERSION_PREFIX}${existing + 1}`;
      const v = await vs.createVersionFromDelta(delta, name, 'MASTER NOTE VERSION');
      if (v != null) {
        refreshVersionCount();
        toast({ title: `Saved “${name}”`, description: 'Added to this note’s version history.', status: 'success', duration: 3000, isClosable: true });
      } else {
        console.warn('[master-note version] createVersion returned null', vs.lastCreateError || '');
      }
    } catch (e) {
      console.warn('[master-note version] save failed:', e?.message || e);
    }
  }, [refreshVersionCount, toast]);

  // Accept & replace: overwrite the editor with the Master Note, then
  // automatically record a "Master Note Version X" snapshot of what was applied.
  const handleReplaceNoteFromMasterNote = useCallback((newText, processedItems = []) => {
    if (patientReadOnly) { showPatientReadOnlyToast(); return; }
    if (!masterNoteChartOk('applied')) return;
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) return;
    const delta = buildMasterNoteDelta(newText);
    if (!delta) return;
    const yText = collabActive ? collab?.getYText?.() : null;
    const yDoc = collabActive ? collab?.getYDoc?.() : null;
    if (yText && yDoc?.transact) {
      yDoc.transact(() => {
        if (yText.length > 0) yText.delete(0, yText.length);
        yText.applyDelta(delta.ops);
      });
    } else {
      editor.setContents([{ insert: '\n' }], 'api');
      editor.setContents(delta, 'api');
    }
    // The sources this note was built from are now in the chart — stamp the
    // same processedSources ledger Process's Apply stamps, so they stop
    // showing as "new" in the modal and the Process badge.
    if (Array.isArray(processedItems) && processedItems.length > 0) {
      const nextLedger = mergeLedger(processedSourcesRef.current, processedItems, new Date().toISOString());
      processedSourcesRef.current = nextLedger;
      setProcessedLedger(nextLedger);
    }
    const applied = editor.getContents();
    pendingDeltaRef.current = applied;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(persistDelta, 250);
    // Auto-version the applied master note (fire-and-forget).
    void saveMasterNoteVersion(applied);
  }, [collab, persistDelta, patientReadOnly, showPatientReadOnlyToast, buildMasterNoteDelta, masterNoteChartOk, collabActive, saveMasterNoteVersion]);

  const handleTemplateOff = useCallback((selectedItems) => {
    const cats = new Set();
    for (const it of selectedItems) {
      if (it.type === 'audio') cats.add('audio_transcripts');
      if (it.type === 'form') cats.add('forms');
      if (it.type === 'report') cats.add('uploaded_files');
    }
    injectNonceRef.current += 1;
    setRightCollapsed(false);
    setRightTab('ai');
    setInjectPrompt({
      text: 'Using the selected sources and the current document or template, suggest specific document changes. This should work for clinical notes, social work/case notes, student notes, other templates, and empty documents. If the document is empty, propose a concise starter draft organized with sensible headings. For dated source material, keep suggestions with the matching dated section; if no matching date exists, use a relevant undated section or propose a new section for that source date. For NP, NS, FFP, or FFS row types, put plan, follow-up, and order suggestions under the existing Routine Health Maintenance (RHM) section.',
      sources: Array.from(cats),
      nonce: injectNonceRef.current,
    });
  }, []);

  // ── Parked Review handlers ─────────────────────────────────────────────
  // ProcessModal lifts the review state up via onParkReview when the user
  // clicks "Park in sidebar". We capture it here, close the modal, and
  // flip the right bar to the Review tab. The handlers below mirror the
  // in-modal review-screen handlers so the inline ParkedReviewTab behaves
  // identically — approve/deny/edit only mutate local state, Apply/Reject
  // All go through the same handleApplyProcessEdits path the modal used.
  //
  // The parked payload can be in either `phase: 'sources'` (the user hit
  // Park while picking sources — selection + useTemplate + pins snapshotted)
  // or `phase: 'review'` (the user hit Park from the AI review screen).
  // Both share `nonce`. The handlers below cover both phases; phase-specific
  // ones bail when the payload's phase doesn't match.
  const handleParkReview = useCallback((payload) => {
    if (!payload) return;
    parkedReviewNonceRef.current += 1;
    // PHI guard — stamp the origin patient (captured at Master Note kickoff)
    // onto master payloads, so the display gate + apply guard keep the note
    // bound to the chart it was generated for even across navigation.
    const isMasterPayload = payload.phase === 'master'
      || Object.prototype.hasOwnProperty.call(payload, 'masterNoteOutput')
      || payload.masterGenerating === true;
    // Stamp the origin patient on EVERY parked payload. A Master Note can land
    // AFTER the user navigates away, so it uses the kickoff ref; a diff/sources
    // park always happens in-chart, so the open patient IS its origin. Having an
    // origin on every payload is what lets a parked review survive navigation
    // instead of being wiped (see the patient-change effect below).
    const parkOriginId = (isMasterPayload ? masterNoteOriginRef.current?.id : null) ?? uuid;
    const parkOriginName = (isMasterPayload ? masterNoteOriginRef.current?.name : null)
      ?? visit?.patient?.patient_name ?? null;
    setParkedReview({
      ...payload,
      // Stamp a nonce so the right panel can detect "the user parked a
      // newer review while the old one was still around" — currently
      // unused but keeps the option open for a future "you have an
      // older parked review, replace it?" prompt.
      nonce: parkedReviewNonceRef.current,
      originPatientId: parkOriginId ?? null,
      originPatientName: parkOriginName,
    });
    // Ping — the Master Note finished while you were in a DIFFERENT chart.
    // Without this it lands silently and you'd only discover it by wandering
    // back (Leejoe 2026-07-16). Clicking it takes you to the right chart, which
    // is only possible because we stamped the origin patient above. No ping when
    // you're already on the origin chart — you can see it in the Review tab.
    if (isMasterPayload && payload.masterNoteOutput
        && parkOriginId && String(parkOriginId) !== String(uuid)) {
      const pingWho = parkOriginName || 'another patient';
      const pingTo = `/clinic/${parkOriginId}${dateParam ? `?date=${dateParam}` : ''}`;
      toast({
        duration: 12000,
        isClosable: true,
        render: ({ onClose }) => (
          <div
            role="button"
            tabIndex={0}
            onClick={() => { onClose(); navigate(pingTo); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { onClose(); navigate(pingTo); }
            }}
            style={{
              cursor: 'pointer', background: '#7c3aed', color: '#fff',
              padding: '12px 16px', borderRadius: 8, maxWidth: 380,
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)', fontSize: 14, lineHeight: 1.35,
            }}
          >
            <strong>✨ Master Note ready for {pingWho}</strong>
            <div style={{ opacity: 0.9, marginTop: 2 }}>Click to open that chart and review.</div>
          </div>
        ),
      });
    }
    // Truncation safety net — a Master Note cut off at the model's length cap
    // must never be applied silently. Backend flags it (metadata.truncated);
    // warn here on the parked path (the long notes that could still truncate at
    // 32k are exactly the ones that get backgrounded/parked).
    if (isMasterPayload && payload.masterNoteMetadata?.truncated) {
      toast({
        title: 'Master Note may be incomplete',
        description: 'It hit the length limit and may be cut off at the end. Review the end or regenerate before signing.',
        status: 'warning', duration: 10000, isClosable: true,
      });
    }
    setIsProcessOpen(false);
    // Clear the pin + auto-generate flags so a subsequent open of the
    // Process modal (e.g. another Process button click) doesn't try to
    // auto-generate against the now-consumed pin.
    setProcessPinAudioSessionId(null);
    setProcessPinReportPath(null);
    setProcessAutoGenerate(false);
    // Surface the parked review in the right bar — expand if collapsed
    // so the tab badge is visible.
    setRightCollapsed(false);
    setRightTab('review');
  }, [toast, uuid, visit, navigate, dateParam]);

  const parkedSetApproval = useCallback((id, status) => {
    setParkedReview((p) => (p ? { ...p, approvals: { ...p.approvals, [id]: status } } : p));
  }, []);

  const parkedOnApprove = useCallback((id) => parkedSetApproval(id, 'approved'), [parkedSetApproval]);
  const parkedOnDeny = useCallback((id) => parkedSetApproval(id, 'denied'), [parkedSetApproval]);

  // Inline edit refinement — mirrors ProcessModal.onEditSave: keep the
  // matchLen bump so a previously pure-deletion edit (matchLen=0,
  // newText='') becomes a replacement when the clinician typed
  // something to keep.
  const parkedOnEditSave = useCallback((id, newText) => {
    setParkedReview((p) => {
      if (!p) return p;
      return {
        ...p,
        edits: p.edits.map((e) => {
          if (e.id !== id) return e;
          const updated = { ...e, newText };
          if (!e.newText && e.oldText && newText) {
            updated.matchLen = e.oldText.length;
          }
          return updated;
        }),
      };
    });
  }, []);

  // Approve every still-pending edit, but leave the user's explicit X
  // choices alone — same additive-bulk semantics as the in-modal button.
  const parkedOnApproveRest = useCallback(() => {
    setParkedReview((p) => {
      if (!p) return p;
      const next = { ...p.approvals };
      for (const e of p.edits) {
        if (next[e.id] !== 'denied') next[e.id] = 'approved';
      }
      return { ...p, approvals: next };
    });
  }, []);

  const parkedOnRejectAllClose = useCallback(async () => {
    if (!parkedReview) return;
    if (parkedApplying) return;
    setParkedApplying(true);
    try {
      const denied = Object.fromEntries(parkedReview.edits.map((e) => [e.id, 'denied']));
      setParkedReview((p) => (p ? { ...p, approvals: denied } : p));
      // Empty ops → still flows through handleApplyProcessEdits so the
      // ledger gets stamped for "this source was reviewed, no changes
      // applied", which greys the source out next time.
      await handleApplyProcessEdits([], parkedReview.selectedItems || []);
      setParkedReview(null);
      toast({ title: 'Marked reviewed', status: 'success', duration: 1800 });
    } catch (e) {
      toast({
        title: 'Could not mark reviewed',
        description: e?.message || 'Unknown error',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setParkedApplying(false);
    }
  }, [parkedReview, parkedApplying, handleApplyProcessEdits, toast]);

  const parkedOnApply = useCallback(async () => {
    if (!parkedReview) return;
    // Parked reviews now survive navigation, so the write is guarded the same
    // way the Master Note apply is: a review composed against another patient's
    // document must never be applied to the open chart. (Belt to the display
    // gate's braces — the Review tab is already hidden off its origin chart.)
    if (parkedReview.originPatientId != null
        && String(parkedReview.originPatientId) !== String(uuid)) {
      toast({
        title: 'Not applied',
        description: `This review was generated for ${parkedReview.originPatientName || 'another patient'}. Open that chart to apply it.`,
        status: 'warning', duration: 6000, isClosable: true,
      });
      return;
    }
    if (parkedApplying) return;
    setParkedApplying(true);
    try {
      const ops = composeOps(
        parkedReview.documentText || '',
        parkedReview.edits || [],
        parkedReview.approvals || {},
      );
      await handleApplyProcessEdits(ops, parkedReview.selectedItems || []);
      setParkedReview(null);
      toast({ title: 'Edits applied', status: 'success', duration: 1800 });
    } catch (e) {
      toast({
        title: 'Apply failed',
        description: e?.message || 'Unknown error',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setParkedApplying(false);
    }
  }, [parkedReview, parkedApplying, handleApplyProcessEdits, toast, uuid]);

  // Dismiss without applying or marking reviewed — the user just wants
  // to clear the parked review (e.g. they're going to re-Process from
  // scratch with different sources). Sources stay "unprocessed" so the
  // red NEW badge still surfaces them next time.
  const parkedOnDismiss = useCallback(() => {
    setParkedReview(null);
  }, []);

  // Unpark — opposite direction of handleParkReview. Takes the parked
  // payload (both phases) and re-opens the Process modal with the
  // selections / edits restored.
  //
  // Critically: parkedReview is NOT cleared here. Two reasons:
  //   1. If a Generate fetch is in-flight when the user unpark
  //      (parkedGenerate is running in the parent), the result lands
  //      via parkedGenerate's setParkedReview updater, which checks
  //      `p.phase === 'sources'`. Clearing it here means that updater
  //      hits the `p == null` branch and silently drops the AI's
  //      reply — the modal stays stuck on sources phase while the
  //      fetch actually completed. Keeping parkedReview alive means
  //      the in-flight fetch completion still updates the right bar's
  //      Review tab AND propagates back to the modal on its next Park.
  //   2. The right bar stays consistent — park and unpark are now
  //      symmetric moves that don't destroy each other's data. The
  //      user can hop between modal and sidebar freely, mid-fetch, and
  //      nothing is lost.
  const handleUnparkReview = useCallback(() => {
    setParkedReview((current) => {
      if (!current) return current;
      // Hand the parked payload to the modal as its seed state. The
      // modal opens on the next render; once consumed, its
      // onUnparkConsumed callback clears unparkState so re-opens don't
      // re-apply stale state.
      setUnparkState(current);
      setIsProcessOpen(true);
      setProcessPinAudioSessionId(current.pinAudioSessionId || null);
      setProcessPinReportPath(current.pinReportPath || null);
      setProcessAutoGenerate(!!current.autoGenerate);
      setRightTab('ai');
      // Keep parkedReview alive (see comment above).
      return current;
    });
  }, []);

  // Fired by ProcessModal after it consumes unparkState. Clears the
  // payload so subsequent opens of the modal (e.g. user closes then
  // re-opens) don't re-seed stale selections.
  const handleUnparkConsumed = useCallback(() => {
    setUnparkState(null);
  }, []);

  // ── Parked source-picker handlers ───────────────────────────────────
  // When the user parks the Process flow from the source-picker screen,
  // we serialize the selection into the parked payload (selectedIds as
  // an Array, expanded as an Array — Sets aren't great for state
  // diffing). These handlers mutate the parked state directly so the
  // inline source picker stays in sync with the user's toggles. Each
  // is a 1:1 port of the corresponding ProcessModal.setX helper, just
  // scoped to the parked payload.

  const parkedToggleItem = useCallback((type, id) => {
    setParkedReview((p) => {
      if (!p || p.phase !== 'sources') return p;
      const next = new Set(p.selectedIds || []);
      const k = `${type}:${id}`;
      if (next.has(k)) next.delete(k); else next.add(k);
      return { ...p, selectedIds: Array.from(next) };
    });
  }, []);

  const parkedToggleGroup = useCallback((groupKey) => {
    setParkedReview((p) => {
      if (!p || p.phase !== 'sources') return p;
      const next = new Set(p.selectedIds || []);
      const g = (p.sourceGroups || []).find((x) => (x.key || x.type) === groupKey);
      if (!g) return p;
      const all = g.items.every((i) => next.has(`${i.type}:${i.id}`));
      for (const i of g.items) {
        const k = `${i.type}:${i.id}`;
        if (all) next.delete(k); else next.add(k);
      }
      return { ...p, selectedIds: Array.from(next) };
    });
  }, []);

  const parkedToggleExpand = useCallback((groupKey) => {
    setParkedReview((p) => {
      if (!p || p.phase !== 'sources') return p;
      const next = new Set(p.expanded || []);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return { ...p, expanded: Array.from(next) };
    });
  }, []);

  const parkedToggleTemplate = useCallback(() => {
    setParkedReview((p) => (p && p.phase === 'sources' ? { ...p, useTemplate: !p.useTemplate } : p));
  }, []);

  const parkedResetSelection = useCallback(() => {
    setParkedReview((p) => {
      if (!p || p.phase !== 'sources') return p;
      const sel = new Set();
      const exp = new Set();
      for (const g of p.sourceGroups || []) {
        const hasNew = g.items.some((i) => !i.processed);
        const hasProcessed = g.items.some((i) => i.processed);
        for (const i of g.items) if (!i.processed) sel.add(`${i.type}:${i.id}`);
        if (hasNew && hasProcessed) exp.add(g.key || g.type);
      }
      return { ...p, selectedIds: Array.from(sel), expanded: Array.from(exp) };
    });
  }, []);

  const parkedSelectOnlyNew = useCallback(() => {
    setParkedReview((p) => {
      if (!p || p.phase !== 'sources') return p;
      const next = new Set();
      for (const g of p.sourceGroups || []) {
        for (const i of g.items) {
          if (!i.processed) next.add(`${i.type}:${i.id}`);
        }
      }
      return { ...p, selectedIds: Array.from(next) };
    });
  }, []);

  const parkedClearAllSelection = useCallback(() => {
    setParkedReview((p) => (p && p.phase === 'sources' ? { ...p, selectedIds: [] } : p));
  }, []);

  // Run the AI suggest-edits endpoint from the parked source picker.
  // Mirrors ProcessModal.generate but lands the result in the parked
  // payload (transitioning phase='sources' → phase='review') instead
  // of switching an internal screen flag. On success the new payload has
  // the review fields ready for ProcessDiffReview; on failure we keep
  // the source-picker phase intact and toast the error so the user can
  // fix a bad source selection without losing their work.
  const parkedGenerate = useCallback(async () => {
    if (!parkedReview || parkedReview.phase !== 'sources') return;
    // Guard against double-clicking Generate while a generation is
    // already in flight (modal or sidebar). Both surfaces drive the
    // same parent state — re-entry is silently dropped.
    if (isGenerating) return;

    // Replicate the useTemplate-off shortcut from ProcessModal: if the
    // user toggled off the template, route the selected sources to the
    // AI Assistant (the Template-Off workflow that ends in an injectPrompt).
    if (!parkedReview.useTemplate) {
      const selectedItems = [];
      const sel = new Set(parkedReview.selectedIds || []);
      for (const g of parkedReview.sourceGroups || []) {
        for (const i of g.items) if (sel.has(`${i.type}:${i.id}`)) selectedItems.push(i);
      }
      handleTemplateOff(selectedItems.map((i) => ({ type: i.type, id: i.id })));
      setParkedReview(null);
      return;
    }

    // Lifted generation state — begin/endGeneration update the parent's
    // isGenerating/generationStartedAt so the GeneratingTimer + button
    // text on both the modal and the sidebar source picker stay in sync
    // even when the user parks/unparks mid-fetch.
    beginGeneration();
    try {
      const allItems = (parkedReview.sourceGroups || []).flatMap((g) => g.items);
      const sel = new Set(parkedReview.selectedIds || []);
      const selectedSourceItems = allItems.filter((i) => sel.has(`${i.type}:${i.id}`));
      const selectedRequestItems = selectedSourceItems.map((item) => ({
        type: item.type,
        id: item.id,
        label: item.label,
        date: item.date || null,
        duration: item.duration ?? null,
      }));
      const generatedRequestItems = selectedRequestItems;
      const selectedItems = selectedRequestItems.map((i) => ({ type: i.type, id: i.id }));

      const res = await fetch(`${getBackendURL()}/api/ai-assistant/suggest-edits/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: uuid,
          patientData: visit.patient,
          schema: chartDocSchema,
          userRole: 'medstudent',
          documentType: visit.patient?.type || null,
          documentText: parkedReview.documentText !== undefined
            ? parkedReview.documentText
            : (collabActive ? (aiDocumentPlainRef.current || '') : (documentToText(visit.document) || '')),
          selectedItems: selectedRequestItems,
          engine: 'fill_template',
          overlay_mode: 'overwrite',
          ...processAiRequestSettings,
        }),
      });
      let body = null;
      try { body = await res.json(); } catch (_) { /* non-JSON body */ }
      if (!res.ok) {
        const reason = (body && body.error) ? body.error : `Request failed (${res.status})`;
        throw new Error(reason);
      }
      if (!body) throw new Error('Empty response from server');
      if (body.error) throw new Error(body.error);
      const sectionEdits = Array.isArray(body.sections)
        ? body.sections.filter((e) => e && e.kind && e.kind !== 'section_drop')
        : [];
      const useSections = sectionEdits.length > 0;
      const rawEdits = useSections ? sectionEdits : (body.edits || []);
      if (rawEdits.length === 0) {
        throw new Error('The AI suggested no changes for the selected sources.');
      }
      const documentTextForDiff = parkedReview.documentText !== undefined
        ? parkedReview.documentText
        : (collabActive ? (aiDocumentPlainRef.current || '') : (documentToText(visit.document) || ''));
      const selectedByKey = new Map(selectedRequestItems.map((i) => [`${i.type}:${i.id}`, i]));
      const enrichedEdits = rawEdits.map((edit) => {
        const source = edit.source || {};
        const meta = selectedByKey.get(`${source.type}:${source.id}`);
        return {
          ...edit,
          source: meta ? { ...source, ...meta, label: source.label || meta.label } : source,
        };
      });
      const located = useSections
        ? locateSectionEdits(documentTextForDiff, enrichedEdits, {
          selectedItems: selectedRequestItems,
          documentType: visit.patient?.type || null,
        })
        : locateEdits(documentTextForDiff, enrichedEdits, {
          selectedItems: selectedRequestItems,
          documentType: visit.patient?.type || null,
        });
      const approvals = Object.fromEntries(located.map((e) => [e.id, 'pending']));
      // Transition the parked payload into review phase with the AI's
      // edits + the user's selections still carried forward.
      setParkedReview((p) => p && p.phase === 'sources' ? {
        ...p,
        phase: 'review',
        edits: located,
        approvals,
        debugInfo: body.debug || null,
        generatedRequestItems,
        selectedItems,
        documentText: documentTextForDiff,
        // Clear source-picker-specific fields — no longer used in review
        // phase. Kept on the object just in case the user wants to view
        // the source list in a follow-up task.
        sourceGroups: undefined,
        selectedIds: undefined,
        expanded: undefined,
        useTemplate: undefined,
      } : p);
    } catch (e) {
      toast({
        title: 'Generate failed',
        description: e?.message || 'Failed to generate edits.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      endGeneration();
    }
  }, [
    parkedReview, isGenerating,
    uuid, visit.patient,
    processAiRequestSettings, toast, handleTemplateOff,
    beginGeneration, endGeneration,
    collabActive,
  ]);

  // Patient changed. The old behavior WIPED the parked review here, because a
  // review is scoped to one document and applying it against another patient's
  // note would strand a stale approval set (or worse, write the wrong PHI).
  // That's a real risk — but the blunt wipe also meant a parked review never
  // survived navigation: start a Master Note, step into another chart, come
  // back, and it was gone, so you had to regenerate and re-wait (Leejoe
  // 2026-07-16 — the whole point of parking is that you DON'T wait).
  //
  // Every parked payload now carries originPatientId (see handleParkReview), so
  // we keep it instead of wiping. The display gate (right-panel prop) hides it
  // off its origin chart, and the apply guards refuse to write it anywhere but
  // its origin — so the stale-approval risk is covered by identity rather than
  // amnesia, and the review is still sitting there when you come back.
  // A payload with no provable origin still gets wiped (old safe behavior).
  useEffect(() => {
    setParkedReview((p) => (p && p.originPatientId != null ? p : null));
    setParkedApplying(false);
  }, [uuid]);

  // If the active tab ever points at the parked Review tab but there's no
  // parked review (just dismissed / applied / patient switched), fall back
  // to AI so the panel doesn't render an empty section.
  useEffect(() => {
    if (rightTab === 'review' && !parkedReview) {
      setRightTab('ai');
    }
  }, [rightTab, parkedReview]);

  // Sign & Save. Signing happens HERE, on the button click — not inside the
  // PCP modal. Two reasons: the note is committed even if the clinician
  // cancels the modal, and a cancelled modal can never leave a note that
  // claims to be signed.
  //
  // The signature is a manual version row stamped with SIGNED_VERSION_PREFIX.
  // That row carries who (user_name/user_email), when (backup_timestamp) and a
  // full snapshot of what was signed, and manual versions are never pruned.
  // Sign & Save now opens the signature modal instead of signing immediately.
  const onSign = useCallback(() => {
    if (patientReadOnly) { showPatientReadOnlyToast(); return; }
    if (isSigned || isSigning) return;
    setSignModalMode('sign');
    setIsSignModalOpen(true);
  }, [patientReadOnly, showPatientReadOnlyToast, isSigned, isSigning]);

  // The actual sign + lock, invoked by SignAndSaveModal's "Sign & Lock".
  // Writes the immutable [SIGNED] snapshot AND the document_signatures row
  // (signature + generated password + locked). On success the modal switches
  // to reveal mode; the note is locked; the PCP step follows when reveal closes.
  const performSignAndLock = useCallback(async (signatureText) => {
    const vs = versioningSystemRef.current;
    if (!vs) {
      toast({ title: 'Versioning not ready yet', status: 'warning', duration: 2500 });
      return;
    }
    setIsSigning(true);
    try {
      clearTimeout(saveTimerRef.current);
      const editor = documentBodyRef.current?.getEditor?.(0);
      if (editor) pendingDeltaRef.current = editor.getContents();
      await persistDelta();

      const snapshot = vs.getCurrentContent?.();
      if (!snapshot) {
        toast({ title: 'Sign failed — editor not ready', description: 'Give the note a moment to finish loading, then sign again.', status: 'error', duration: 6000, isClosable: true });
        return;
      }
      const snapshotText = vs.getTextFromDelta?.(snapshot) || '';
      if (!snapshotText.trim()) {
        toast({ title: 'Sign failed — note is empty', description: 'There is no text in this note to sign. Add content first, then sign.', status: 'error', duration: 6000, isClosable: true });
        return;
      }
      if (!envModule.supabaseServiceKey) {
        try { await loadConfig(); } catch (_) { /* reported below */ }
      }
      if (!envModule.supabaseServiceKey) {
        toast({ title: 'Sign failed — backend unreachable', description: 'The Supabase service key could not be loaded, so the signed version cannot be written. Check that the backend is running, then sign again.', status: 'error', duration: 8000, isClosable: true });
        return;
      }

      const signerName = (signatureText || '').trim() || vs.currentUser?.name || userInfo.name || 'Unknown';
      const stamp = new Date().toLocaleString();
      const versionNumber = await vs.createVersion(`${SIGNED_VERSION_PREFIX}${signerName} · ${stamp}`, 'SIGNED BY USER');
      if (versionNumber == null) {
        console.warn('[sign] createVersion returned null', { documentPath, textLength: snapshotText.length, signerName });
        toast({ title: 'Sign failed', description: 'Could not save the signed version — see the browser console for the underlying storage/database error. The note was NOT signed.', status: 'error', duration: 8000, isClosable: true });
        return;
      }

      // Generate the lock password and persist the signature/lock record.
      const password = generateLockPassword();
      const rec = await writeSignatureRecord({
        documentPath,
        signerName,
        signerEmail: vs.currentUser?.email || userInfo.email || '',
        signatureText: signatureText || signerName,
        password,
        schema: chartDocSchema,
      });
      if (!rec.ok) {
        toast({ title: 'Signed, but lock failed', description: `The signature snapshot saved, but the lock record did not: ${rec.error}. Try signing again.`, status: 'error', duration: 9000, isClosable: true });
        return;
      }

      // Reveal step + lock the note locally.
      justSignedRef.current = true;
      setRevealPassword(password);
      setRevealSignatureText(signatureText || signerName);
      setSignModalMode('reveal');
      setSignedEditGranted(false);
      setIsReadOnly(true);
      // Drop any stale 'edit' preference so reopening this note lands in read
      // mode immediately (no editable flash before signed-status reloads).
      try { if (uuid) sessionStorage.setItem(editModeKey(uuid), 'read'); } catch {}

      refreshVersionCount();
      refreshSignedStatus();
      setVisitInProgress(false);
    } catch (e) {
      toast({ title: 'Sign failed', description: e?.message || String(e), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsSigning(false);
    }
  }, [persistDelta, userInfo.name, userInfo.email, documentPath, toast, refreshVersionCount, refreshSignedStatus]);

  // Signer re-opens the reveal modal later to view/copy the password.
  const onRevealPassword = useCallback(() => {
    justSignedRef.current = false;
    setRevealPassword(signerPassword || '');
    setRevealSignatureText(signedSignatureText || '');
    setSignModalMode('reveal');
    setIsSignModalOpen(true);
  }, [signerPassword, signedSignatureText]);

  // Closing the reveal modal: after a FRESH sign, continue into the PCP step.
  const onSignModalClose = useCallback(() => {
    setIsSignModalOpen(false);
    if (signModalMode === 'reveal' && justSignedRef.current) {
      justSignedRef.current = false;
      setIsPcpModalOpen(true);
    }
  }, [signModalMode]);

  // Finish a post-sign edit session: snapshot ONE attributed manual version,
  // re-enable auto-versioning, and re-lock. Best-effort — never throws to the
  // caller (the backstop calls this during unmount).
  // "Done editing": write ONE tracked version from the latest content, then
  // re-lock. Runs while mounted. (The exit-flush effect handles navigate-away.)
  const finishPostSignEdit = useCallback(async () => {
    const vs = versioningSystemRef.current;
    try {
      // Capture the freshest content and persist the doc first.
      const editor = documentBodyRef.current?.getEditor?.(0);
      if (editor) {
        const latest = editor.getContents();
        pendingDeltaRef.current = latest;
        postSignLatestDeltaRef.current = latest;
      }
      await persistDelta();

      if (!postSignDirtyRef.current) {
        // Unlocked but nothing edited — nothing to track.
        toast({ title: 'Note re-locked', status: 'success', duration: 2000 });
        return;
      }
      if (!vs || !postSignLatestDeltaRef.current) {
        toast({ title: 'Edit not tracked', description: `Versioning isn't attached (schema: ${chartDocSchema || 'none'}). Your text is saved.`, status: 'warning', duration: 8000, isClosable: true });
        return;
      }

      const editorName = vs.currentUser?.name || userInfo.name || 'Unknown';
      const stamp = new Date().toLocaleString();
      const v = await vs.createVersionFromDelta(
        postSignLatestDeltaRef.current,
        `${POST_SIGN_EDIT_PREFIX}${editorName} · ${stamp}`,
        'POST-SIGN EDIT',
      );
      if (v != null) {
        postSignDirtyRef.current = false;
        refreshVersionCount();
        toast({ title: 'Edit tracked', description: `Saved as a version under ${editorName}. The note is re-locked.`, status: 'success', duration: 3500, isClosable: true });
      } else {
        const err = vs.lastCreateError;
        const detail = err ? `${err.stage}: ${err.code || ''} ${err.message || ''}`.trim() : 'unknown';
        console.warn('[post-sign edit] Done version not created —', detail, err || '');
        toast({ title: 'Edit not tracked', description: `Your text is saved, but the tracked version could not be written (${detail}). See the browser console.`, status: 'warning', duration: 9000, isClosable: true });
      }
    } catch (e) {
      console.warn('[post-sign edit] finish failed:', e?.message || e);
    } finally {
      vs?.setAutoVersioningSuppressed(false);
      setSignedEditGranted(false);
      setIsReadOnly(true);
    }
  }, [persistDelta, userInfo.name, refreshVersionCount, toast, chartDocSchema]);

  // Password prompt result. Correct → grant an in-session edit + suppress auto.
  const tryUnlock = useCallback((input) => {
    if (verifyPassword(input)) {
      versioningSystemRef.current?.setAutoVersioningSuppressed(true);
      // Start a fresh tracking session for this doc.
      postSignDirtyRef.current = false;
      postSignLatestDeltaRef.current = null;
      setSignedEditGranted(true);
      setIsReadOnly(false);
      setIsUnlockOpen(false);
      setUnlockError('');
    } else {
      setUnlockError('Incorrect password.');
    }
  }, [verifyPassword]);

  // Header edit toggle. Unsigned → normal toggle. Signed + locked → password
  // prompt. Signed + editing → finish (snapshot + re-lock).
  const handleEditToggle = useCallback(() => {
    if (patientReadOnly) { showPatientReadOnlyToast(); return; }
    if (!isSigned) { toggleReadOnly(); return; }
    if (!signedEditGranted) { setUnlockError(''); setIsUnlockOpen(true); return; }
    finishPostSignEdit();
  }, [patientReadOnly, showPatientReadOnlyToast, isSigned, signedEditGranted, toggleReadOnly, finishPostSignEdit]);

  // A signed/locked note always opens read-only for everyone (including the
  // signer) and stays that way until the viewer unlocks it with the password
  // this session — overriding any stale sessionStorage 'edit' preference left
  // from before it was signed.
  useEffect(() => {
    if (isSigned && !signedEditGranted && !isReadOnly) setIsReadOnly(true);
  }, [isSigned, signedEditGranted, isReadOnly]);

  // Expose the active-edit-session flag to onDocumentChange (a stable callback)
  // via a ref, so edits during a post-sign session arm the debounced snapshot.
  useEffect(() => { signedEditGrantedRef.current = isSigned && signedEditGranted; }, [isSigned, signedEditGranted]);

  // Exit-flush: leaving the document mid-session (navigate away / patient switch
  // / unmount) writes ONE tracked version from the last-known content. Uses the
  // captured delta + versioning ref (NOT the editor, which is already
  // unmounting) and fires-and-forgets via module-level clients, so it completes
  // even as the component tears down.
  useEffect(() => () => {
    if (
      signedEditGrantedRef.current &&
      postSignDirtyRef.current &&
      postSignVsRef.current &&
      postSignLatestDeltaRef.current
    ) {
      postSignDirtyRef.current = false;
      const vs = postSignVsRef.current;
      const editorName = vs.currentUser?.name || 'Unknown';
      const stamp = new Date().toLocaleString();
      Promise.resolve(
        vs.createVersionFromDelta(
          postSignLatestDeltaRef.current,
          `${POST_SIGN_EDIT_PREFIX}${editorName} · ${stamp}`,
          'POST-SIGN EDIT (EXIT)',
        ),
      ).catch(() => {});
    }
  }, [documentPath]);

  // Task #185 — listen for recording-state events from RecordingBubble.
  // Any non-closed state (opened, recording, paused, etc.) means the visit
  // is implicitly in progress. 'closed' = recorder window dismissed.
  //
  // Task #F (Leejoe 2026-06-28): also clear visitInProgress when the user
  // hits Stop+Save. Stop+Save fires a `transcriptionSaved` event after the
  // audio is persisted — treat that as 'visit complete from the recording
  // standpoint, clinician may still edit the note but the In Visit badge
  // can stand down'. Sign & Save still also clears it.
  useEffect(() => {
    const handler = (event) => {
      const state = event?.detail?.state;
      if (!state || state === 'closed') return;
      setVisitInProgress(true);
      // Task #207: also surface 'recording' as a more specific sub-state so
      // the top-bar badge can pulse during active capture and go steady
      // when paused or stopped-but-still-in-visit.
      try {
        window.dispatchEvent(new CustomEvent('vmai:visit-state', {
          detail: {
            inVisit: true,
            recording: state === 'recording' || state === 'live',
          },
        }));
      } catch (_) { /* non-fatal */ }
    };
    const onTranscriptionSaved = () => {
      setVisitInProgress(false);
    };
    window.addEventListener('recordingComponentStateChange', handler);
    window.addEventListener('recordingBubbleStateChange', handler);
    window.addEventListener('transcriptionSaved', onTranscriptionSaved);
    return () => {
      window.removeEventListener('recordingComponentStateChange', handler);
      window.removeEventListener('recordingBubbleStateChange', handler);
      window.removeEventListener('transcriptionSaved', onTranscriptionSaved);
    };
  }, []);

  // Task #207: emit visit-state whenever the local flag flips so the top
  // DoctorPortalBar can mirror without prop-drilling through 9k lines.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('vmai:visit-state', {
        detail: { inVisit: visitInProgress, recording: false },
      }));
    } catch (_) { /* non-fatal */ }
  }, [visitInProgress]);

  // Perform the actual restore — used by the confirmation modal's "Restore"
  // button. Mirrors DocumentNavBar.handleRestoreLatestVersion:
  //   1. Find the most recent row in `document_backups` for this doc.
  //   2. Download its content from `documentBackupBucketName`.
  //   3. Apply the delta to the editor and queue a save through the same
  //      pendingDeltaRef + saveTimerRef path the inline VersionHistoryModal
  //      callback uses, so collab + storage stay consistent.
  const performRestoreLatest = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    if (isRestoring || !documentPath) return;
    setIsRestoring(true);
    try {
      const vs = versioningSystemRef.current;
      const admin = getSupabaseAdmin();

      // 1. Latest version — cache first, DB fallback.
      let latestVersion = null;
      const cached = vs?.getVersionsCache?.();
      if (Array.isArray(cached) && cached.length > 0) {
        latestVersion = [...cached].sort(
          (a, b) =>
            new Date(b.backup_timestamp) - new Date(a.backup_timestamp),
        )[0];
      }
      if (!latestVersion) {
        const { data, error } = await admin
          .from(documentBackupTableName || 'document_backups')
          .select('backup_path, backup_timestamp, version_name, backup_type')
          .eq('original_path', documentPath)
          .order('backup_timestamp', { ascending: false })
          .limit(1);
        if (error) throw error;
        latestVersion = data?.[0] || null;
      }
      if (!latestVersion?.backup_path) {
        toast({
          title: 'No saved versions',
          description:
            'This document has no previous version to restore yet.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        setIsRestoreConfirmOpen(false);
        return;
      }

      // 2. Content — cache first, storage fallback.
      let parsed = vs?.getCachedContent?.(latestVersion.backup_path);
      if (!parsed) {
        const { data: blob, error } = await admin.storage
          .from(documentBackupBucketName)
          .download(latestVersion.backup_path);
        if (error) throw error;
        const text = await blob.text();
        parsed = JSON.parse(text);
        vs?.setCachedContent?.(latestVersion.backup_path, parsed);
      }
      const delta = parsed?.delta || parsed?.content;
      if (!delta || !Array.isArray(delta.ops)) {
        throw new Error('Selected version contains no valid content.');
      }

      // 3. Apply + queue save (same shape as the modal's onVersionRestored).
      const editor = documentBodyRef.current?.getEditor?.(0);
      if (!editor) throw new Error('Editor is not ready yet.');
      // Leejoe 2026-06-29 — FOREVER FIX for double-stacked notes. With
      // collabActive on, `editor.setContents()` echoes through
      // the y-quill binding and Yjs CRDT MERGES the new delta with the
      // existing YText content instead of replacing — producing two full
      // notes stacked. Janu already solved this in `onReloadTemplate`:
      // when collab is live, delete the YText then applyDelta in a single
      // yDoc.transact so collab peers see one atomic replace. Falling
      // back to the empty-then-set double-setContents pattern when collab
      // is off (catches stale Quill diff-merge residue).
      const yText = collabActive ? collab?.getYText?.() : null;
      const yDoc = collabActive ? collab?.getYDoc?.() : null;
      if (yText && yDoc?.transact) {
        yDoc.transact(() => {
          if (yText.length > 0) yText.delete(0, yText.length);
          yText.applyDelta(delta.ops || []);
        });
      } else {
        editor.setContents([{ insert: '\n' }], 'api');
        editor.setContents(delta, 'api');
      }
      // Leejoe 2026-06-29 — TIME-TRAVEL the processedSources ledger too,
      // not just the note delta. processedSources lives in the .json
      // envelope and tracks which sources have been Processed (drives the
      // red "NEW" badge on the Process button). Each backup snapshot has
      // its own processedSources value. Without this, Restore would leave
      // the badge stuck on the post-edit state — confusing because the
      // note content rewound but the badge didn't. The previous fix
      // blanket-cleared to [] which over-corrected for real patients
      // (forces re-processing of work that's still in the restored note).
      // Correct behavior: load the value that was active when the version
      // was saved. Fall back to [] for legacy backups missing the field.
      const restoredSources = Array.isArray(parsed?.processedSources)
        ? parsed.processedSources
        : [];
      processedSourcesRef.current = restoredSources;
      setProcessedLedger(restoredSources);
      pendingDeltaRef.current = editor.getContents();
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(persistDelta, 250);
      refreshVersionCount();

      toast({
        title: 'Version restored',
        description: latestVersion.version_name
          ? `Restored "${latestVersion.version_name}".`
          : 'Restored the latest saved version.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsRestoreConfirmOpen(false);
    } catch (err) {
      console.error('[restore-latest] failed:', err);
      toast({
        title: 'Restore failed',
        description:
          err?.message || 'Failed to restore the latest saved version.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsRestoring(false);
    }
  }, [
    documentPath,
    isRestoring,
    persistDelta,
    refreshVersionCount,
    toast,
    patientReadOnly,
    showPatientReadOnlyToast,
    collabActive,
  ]);

  // Reset-to-Seed for test fixtures — Leejoe 2026-06-29.
  //
  // First implementation (Task #204 / #227) POSTed to /api/fixtures/reset/
  // then hard-reloaded the page on success. The backend faithfully rewrote
  // the .json + deleted the .yjs blob, but the client's live Yjs collab
  // session held the post-edit state in memory (+ IndexedDB), and on
  // reconnect resync the dirty doc beat the freshly-written clean .json.
  // Net result: chip clickable, backend logged the reset, but HTN problems
  // the doctor added stuck around and processedSources never reset.
  //
  // Better: ride the SAME path performRestoreLatest already uses (the
  // version-history Restore button works fine for note content). After the
  // backend POST wipes storage, download the fresh .json directly from
  // bucketName_files at documentPath and call editor.setContents(delta).
  // setContents bypasses the Yjs sync race because we're authoritatively
  // overwriting the local doc; the subsequent persistDelta queues a save
  // that replaces both .json AND .yjs with the clean state.
  //
  // Triggered by the orange "Reset to Seed" chip in DoctorPortalBar, which
  // now dispatches `vmai:reset-to-seed` instead of hard-reloading.
  const performResetToSeed = useCallback(async () => {
    if (!uuid || !isFixtureUuid(uuid)) return;
    const ehrId = fixtureEhrIdForUuid(uuid);
    if (!ehrId) return;
    try {
      // 1. Backend rewrites the seed .json + deletes stale .yjs.
      const res = await fetch(`${getBackendURL()}/api/fixtures/reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ehr_id: ehrId, date: dateParam || '', schema: chartDocSchema }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Reset failed (${res.status})`);
      }
      // 2. Download the freshly-seeded .json from the documents bucket — past
      //    the CDN, or this can be the pre-reset document.
      const admin = getSupabaseAdmin();
      const parsed = await downloadFreshJson(admin, bucketName_files, documentPath);
      const delta = parsed?.delta || parsed?.content || parsed;
      if (!delta || !Array.isArray(delta.ops)) {
        throw new Error('Seeded document has no valid Quill delta.');
      }
      // 3. Hard-reload — Leejoe 2026-06-29.
      //
      // The setContents/yText.applyDelta path that works for Restore does
      // NOT work for Reset to Seed when the .yjs collab file already has
      // accumulated doubled CRDT history (.yjs is 31KB while .json is 9KB
      // for Bangston/Chan — that 3x size IS the doubled content as live
      // Yjs ops, not tombstones). Even though the backend deletes the
      // .yjs file + evicts the in-memory room, the user's still-connected
      // Yjs websocket session re-pushes its cached state on next flush,
      // re-creating the doubled .yjs and overlaying it on the clean .json.
      //
      // The only reliable break: tear down the connection by reloading
      // the page. Backend reset endpoint runs server-side: deletes .yjs,
      // writes clean .json, evicts in-memory room. The hard reload then
      // reconnects to a fresh room built from the clean .json.
      //
      // This is what the ORIGINAL Reset to Seed chip code did before I
      // "improved" it. Restoring that behavior fixes the doubling on
      // Reset while keeping the Yjs-aware setContents pattern for
      // Restore + Version History (those work because the snapshot
      // they restore from is already single-template).
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('_reset', String(Date.now()));
        window.location.replace(u.toString());
      } catch (_) {
        window.location.reload();
      }
      // Toast/cleanup is moot — page is about to reload. Bail.
      return;
    } catch (e) {
      console.error('[reset-to-seed] failed:', e);
      toast({
        title: 'Reset failed',
        description: e?.message || 'Could not restore fixture seed.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [uuid, dateParam, documentPath, persistDelta, refreshVersionCount, toast]);

  // Listen for the orange chip in DoctorPortalBar — single-shot event so a
  // double-tap doesn't fire the reset twice mid-run (each dispatch creates
  // a fresh handler attachment because performResetToSeed is in the dep
  // array, but the inflight POST is idempotent on the backend).
  useEffect(() => {
    const onReset = () => { performResetToSeed(); };
    window.addEventListener('vmai:reset-to-seed', onReset);
    return () => window.removeEventListener('vmai:reset-to-seed', onReset);
  }, [performResetToSeed]);

  // Leejoe 2026-06-29 — VoiceDictateChip in DoctorPortalBar (top white bar)
  // wraps the VoiceTyping component and dispatches `vmai:voice-dictate-result`
  // events with each transcript chunk. We mirror DocumentToolbar's
  // handleVoiceTypingResult behavior here: interim segments are inserted
  // at the cursor and tracked so the next interim replaces them; final
  // segments commit + clear the tracking.
  const voiceInterimRangeRef = useRef(null);
  useEffect(() => {
    const onVoice = (e) => {
      const detail = e?.detail || {};
      const editor = documentBodyRef.current?.getEditor?.(0);
      if (!editor) return;
      // Read mode silently swallows 'user' inserts — the widget looks like
      // it's listening but nothing lands in the note (Leejoe 2026-07-08:
      // "it listened but didn't write anything"). Say so instead, throttled
      // so streaming interim chunks don't spam the alert.
      if (typeof editor.isEnabled === 'function' && !editor.isEnabled()) {
        const now = Date.now();
        if (!onVoice._lastReadOnlyWarn || now - onVoice._lastReadOnlyWarn > 10000) {
          onVoice._lastReadOnlyWarn = now;
          window.alert('The note is in read mode — click Edit in the header first, then dictate.');
        }
        return;
      }
      if (detail.keepInterim) {
        voiceInterimRangeRef.current = null;
        return;
      }
      if (voiceInterimRangeRef.current) {
        const { index, length } = voiceInterimRangeRef.current;
        if (length > 0) {
          try { editor.deleteText(index, length, 'user'); } catch (_) {}
        }
        voiceInterimRangeRef.current = null;
      }
      const transcriptText = detail.transcriptText || '';
      if (!transcriptText) return;
      const selection = editor.getSelection();
      const cursorPosition = selection ? selection.index : editor.getLength();
      const isFinal = detail.isFinal !== false;
      if (isFinal) {
        const capitalized = capitalizeDictatedSegment(editor, cursorPosition, transcriptText);
        const text = capitalized + ' ';
        editor.insertText(cursorPosition, text, 'user');
        editor.setSelection(cursorPosition + text.length);
      } else {
        editor.insertText(cursorPosition, transcriptText, 'user');
        voiceInterimRangeRef.current = { index: cursorPosition, length: transcriptText.length };
        editor.setSelection(cursorPosition + transcriptText.length);
      }
    };
    window.addEventListener('vmai:voice-dictate-result', onVoice);
    return () => window.removeEventListener('vmai:voice-dictate-result', onVoice);
  }, []);

  // ── Document tools (mirror of EditableDocumentPage's "Document tools" menu)
  // Wired as stubs here so the menu surfaces consistently in the clinic
  // workspace; each entry will fold in its real handler in a follow-up.
  // ── Copy from older (real implementation) ─────────────────────────────
  // Mirrors EditableDocumentPage.openCopyFromOlderModal:
  //   1. Query notes-c for prior visits of this patient (by EHR_ID first,
  //      then patient_name).
  //   2. Walk newest → oldest, download each prior visit's doc, pick the
  //      first one with real content.
  //   3. Show a modal — either "No prior visit found" or
  //      "Last doc found on <date> — replace current doc?" with Confirm.
  //   4. On Confirm, atomically replace via the live YText (collab) or
  //      Quill.setContents (no-collab) to avoid binding-merge duplication.
  const [copyOlderOpen, setCopyOlderOpen] = useState(false);
  const [copyOlderLoading, setCopyOlderLoading] = useState(false);
  const [copyOlderMatch, setCopyOlderMatch] = useState(null); // { row, delta } or null
  // True when the search was fired automatically on first entry rather than
  // from the Tools menu. Drives the modal's wording and buttons (see render).
  const [copyOlderAuto, setCopyOlderAuto] = useState(false);

  const closeCopyOlder = useCallback(() => {
    setCopyOlderOpen(false);
    setCopyOlderMatch(null);
    setCopyOlderAuto(false);
  }, []);

  // The newest prior visit for this patient that has a populated document, or
  // null. Extracted from onCopyOlder so the Reload Template dialog can ask the
  // same question — "does FFP/FFS have anything to copy?" — without
  // duplicating the query and the candidate walk.
  const findPriorPopulatedVisit = useCallback(async (patientRow, schema) => {
    if (!patientRow?.date_) return null;
    const supabase = await getSupabase();
    // Same-patient rule as the silent FFP/FFS carry-forward (utils/priorVisitMatch):
    // the name must match, and a real EHR ID on both rows must match too. This
    // search used to key on EHR_ID alone whenever the row had one, so the name
    // was never checked and a filler ID ("-") matched every placeholder row.
    const { candidates, error } = await fetchPriorVisitCandidates(supabase, patientRow, schema, {
      log: (info) => console.log('[copy-older] prior-visit query', info),
    });
    if (error) return null;
    if (candidates.length === 0) {
      console.log('[copy-older] no earlier visit belongs to this patient');
      return null;
    }

    for (const row of candidates) {
      const path = buildDocPath(row, schema);
      if (!path) { console.log('[copy-older] candidate has no doc path', row.date_); continue; }
      try {
        const { data: blob, error: dlErr } = await supabase.storage
          .from(bucketName_files || 'files')
          .download(path);
        if (dlErr || !blob) {
          console.log('[copy-older] candidate .json missing', { date: row.date_, path, err: dlErr?.message });
          continue;
        }
        const parsed = JSON.parse(await blob.text());
        if (isEmptyOps(parsed?.delta?.ops)) {
          console.log('[copy-older] candidate .json is empty', { date: row.date_, path });
          continue;
        }
        console.log('[copy-older] MATCH — offering', { date: row.date_, path, opsCount: parsed.delta.ops.length });
        return { row, delta: parsed.delta };
      } catch (e) {
        console.log('[copy-older] candidate failed', { date: row.date_, path, message: e?.message });
      }
    }
    console.log('[copy-older] no candidate had a populated .json', { checked: candidates.length });
    return null;
  }, []);

  // `auto` = fired by the first-entry effect rather than the Tools menu.
  // The automatic path must be silent until it actually has something to
  // offer: no spinner, no toasts, and no modal at all unless a populated
  // prior visit is found. PatientHeaderBar calls this as `onCopyOlder()`
  // with no arguments (PatientHeaderBar.js:291), so the default holds.
  const onCopyOlder = useCallback(async (auto = false) => {
    if (patientReadOnly) {
      if (!auto) showPatientReadOnlyToast();
      return;
    }
    const patientRow = visit.patient;
    if (!patientRow || !patientRow.date_) {
      if (!auto) toast({ title: 'Patient not loaded yet', status: 'warning', duration: 2500 });
      return;
    }
    setCopyOlderMatch(null);
    setCopyOlderAuto(auto);
    if (!auto) {
      setCopyOlderLoading(true);
      setCopyOlderOpen(true);
    }

    try {
      const match = await findPriorPopulatedVisit(patientRow, chartDocSchema);
      if (match) {
        setCopyOlderMatch(match);
        // The auto path opens only now that there is something to show.
        if (auto) setCopyOlderOpen(true);
      }
    } catch (e) {
      console.warn('[copy-older] search threw', e?.message || e);
      // An automatic search that fails is not worth interrupting the user for;
      // they never asked for it. The manual menu action still reports.
      if (!auto) {
        toast({
          title: 'Copy from older — search failed',
          description: e?.message || String(e),
          status: 'error',
          duration: 3500,
          isClosable: true,
        });
      }
    }
    setCopyOlderLoading(false);
  }, [visit.patient, toast, patientReadOnly, showPatientReadOnlyToast, chartDocSchema, findPriorPopulatedVisit]);

  const handleCopyOlderConfirm = useCallback(() => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    if (!copyOlderMatch) return;
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) {
      toast({ title: 'Editor not ready', status: 'warning', duration: 2500 });
      return;
    }
    // The auto-prompt for `notes_behavior === 'accumulate'` fires while the
    // doc is still in read mode by default. Flipping to edit mode here makes
    // the copied content immediately editable; the persistDelta queued below
    // still works either way (YText.applyDelta bypasses Quill's disabled
    // state), but the visual cue would lie if we left it as Reading.
    if (isReadOnly) {
      try {
        setIsReadOnly(false);
        if (uuid) sessionStorage.setItem(editModeKey(uuid), 'edit');
      } catch (_) { /* fine */ }
    }
    const newOps = copyOlderMatch.delta?.ops || [];

    // Prefer the YText path when live-collab is on — Quill.setContents would
    // echo the existing state back through QuillBinding and double the doc.
    const yText = collabActive ? collab?.getYText?.() : null;
    const yDoc = collabActive ? collab?.getYDoc?.() : null;
    try {
      if (yText && yDoc?.transact) {
        yDoc.transact(() => {
          if (yText.length > 0) yText.delete(0, yText.length);
          yText.applyDelta(newOps);
        });
      } else {
        // No collab — clear then set so any residual diff-merge in Quill
        // doesn't keep parts of the prior content around.
        editor.setContents([{ insert: '\n' }], 'api');
        editor.setContents(copyOlderMatch.delta, 'api');
      }
      // Queue a save so the new contents land in storage even if the user
      // doesn't keep typing afterwards.
      try {
        pendingDeltaRef.current = editor.getContents();
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(persistDelta, 250);
      } catch (_) { /* fine */ }
      toast({
        title: `Copied from visit on ${copyOlderMatch.row.date_}`,
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Copy failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setCopyOlderOpen(false);
      setCopyOlderMatch(null);
      setCopyOlderAuto(false);
    }
  // collab is declared later in render but only read at click-time, so the
  // closure picks up the latest value each render. Listed in deps so the
  // memoized callback refreshes when collab swaps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyOlderMatch, toast, persistDelta, isReadOnly, uuid, patientReadOnly, showPatientReadOnlyToast]);

  // First-entry prompt: the first time a visit document is ever opened, if an
  // earlier visit for the same patient has a populated document, offer to copy
  // it over instead of starting from the blank template.
  //
  // Eligibility lives in shouldOfferPriorDocCopy (priorDocPrompt.js) — notably
  // it skips FFP/FFS (already seeded silently by usePatientDocument) and rows
  // explicitly set to notes_behavior 'fresh'.
  //
  // Contract: prompt AT MOST ONCE per visit, surviving reloads, new tabs, and
  // new sessions, via a localStorage flag. A visit has no real content until
  // that first open, so that is exactly when offering to copy helps; on every
  // later entry the user may have their own content and must not be nagged or
  // risk an accidental overwrite.
  //
  // The old implementation also polled the live editor and treated >2 visible
  // characters as "already populated". That cannot work under live collab: the
  // server seeds every new room with ~2,400 characters of template
  // (backend/api/collab/seed.py), so the poll saw the template and suppressed
  // the prompt. The `.json` check below is the reliable signal instead —
  // DocumentBody only forwards text-change events with source === 'user'
  // (DocumentBody.js:590) and the collab server writes `.yjs` only
  // (collab/room.py:106-108), so `.json` exists only after a real user edit.
  const firstEntryPromptFiredRef = useRef(false);
  // The async body below reads its inputs from this ref at execution time
  // rather than through the effect's dependency array.
  //
  // Keeping them OUT of the deps is load-bearing, not a style choice. When
  // `visit.patient` is refetched it arrives as a NEW OBJECT with the same
  // contents, and `chartDocSchema` / `onCopyOlder` are recomputed from it. If
  // those sit in the deps, React tears the effect down mid-flight: the cleanup
  // sets `cancelled = true`, the in-flight run aborts at its next `cancelled`
  // check, and the immediate re-run hits the "already fired" ref guard above
  // and returns. The work is silently abandoned and the prompt never appears —
  // observed in the field as logs that stop right after the `.json` check.
  //
  // Effect teardown is ALSO not a valid abort signal, for the same reason. The
  // readiness flag below flips true→false→true on every usePatientDocument
  // refetch (fetchAll does setIsLoading(true) … setIsLoading(false)), so the
  // effect legitimately re-runs mid-flight. A plain `cancelled` boolean set in
  // the cleanup would abort the in-flight run there, and the re-run would hit
  // the fired-ref guard and do nothing — the same silent abandonment, just via
  // a different trigger. The abort check is therefore "have we navigated to a
  // different visit, or unmounted?", which is what we actually care about.
  const firstEntryCtxRef = useRef(null);
  firstEntryCtxRef.current = {
    patient: visit.patient,
    isLoading: visit.isLoading,
    chartDocSchema,
    rosterMode,
    patientReadOnly,
    onCopyOlder,
  };
  const firstEntryReady = !!visit.patient && !visit.isLoading;

  // Live uuid + mounted flag, used as the abort signal instead of effect
  // teardown. Both are read at await-resume time, never captured.
  const firstEntryUuidRef = useRef(uuid);
  firstEntryUuidRef.current = uuid;
  const firstEntryAliveRef = useRef(true);
  useEffect(() => () => { firstEntryAliveRef.current = false; }, []);

  // The fired flag belongs to one specific visit — reset it during render when
  // the visit changes. Doing this in a separate `[uuid]` effect (as this code
  // originally did) is a latent bug: effects run in declaration order, so on a
  // uuid change the main effect below would run FIRST, still see the previous
  // visit's `true`, and bail — and the reset that follows would never cause a
  // re-run, so the new visit would silently never prompt.
  const firstEntryFlagUuidRef = useRef(uuid);
  if (firstEntryFlagUuidRef.current !== uuid) {
    firstEntryFlagUuidRef.current = uuid;
    firstEntryPromptFiredRef.current = false;
  }

  useEffect(() => {
    if (!uuid) return;
    if (firstEntryPromptFiredRef.current) return;

    const startedForUuid = uuid;
    const stale = () => !firstEntryAliveRef.current || firstEntryUuidRef.current !== startedForUuid;

    const ctx = firstEntryCtxRef.current;
    // Same stale pairing as the template-sync effect below: ctx.patient can be
    // a row that doesn't belong to this uuid (a nav-state seed, or one captured
    // in a closure). Evaluating the prompt with it checks the wrong patient's
    // doc path. Bail without
    // marking fired — firstEntryReady re-runs this once the right row lands.
    if (ctx.patient && !rowMatchesVisitParam(ctx.patient, uuid)) return;
    const flagKey = firstEntryPromptKey(uuid);
    let alreadyPrompted = false;
    try {
      alreadyPrompted = !!localStorage.getItem(flagKey);
    } catch (_) { /* storage blocked — treat as not yet prompted */ }

    const decision = priorDocPromptDecision({
      patient: ctx.patient,
      rosterMode: ctx.rosterMode,
      patientReadOnly: ctx.patientReadOnly,
      isLoading: ctx.isLoading,
      alreadyPrompted,
    });
    if (!decision.offer) {
      // Only log once the patient row exists — otherwise every pre-load render
      // spams "no patient row yet".
      if (ctx.patient) {
        console.log('[first-entry] skipped:', decision.reason, {
          uuid,
          flagKey,
          type: ctx.patient?.type,
          notes_behavior: ctx.patient?.notes_behavior,
          date_: ctx.patient?.date_,
          rosterMode: ctx.rosterMode,
          patientReadOnly: ctx.patientReadOnly,
          hint: alreadyPrompted
            ? `clear it to re-test: localStorage.removeItem('${flagKey}')`
            : undefined,
        });
      }
      return;
    }
    console.log('[first-entry] eligible — checking whether this note is untouched', {
      uuid,
      flagKey,
      patient: ctx.patient?.patient_name,
      type: ctx.patient?.type,
      notes_behavior: ctx.patient?.notes_behavior,
      date_: ctx.patient?.date_,
    });

    // Mark fired up-front so a re-render (e.g. patient object identity change)
    // can't double-run the async body while the downloads are still in flight.
    firstEntryPromptFiredRef.current = true;

    (async () => {
      // 1. Does this visit's own document already have user-written content?
      let parsed = null;
      const path = buildDocPath(ctx.patient, ctx.chartDocSchema);
      try {
        const supabase = await getSupabase();
        if (path) {
          const { data: blob, error } = await supabase.storage
            .from(bucketName_files || 'files')
            .download(path);
          if (!error && blob) parsed = JSON.parse(await blob.text());
          console.log('[first-entry] current visit .json', {
            path,
            schema: ctx.chartDocSchema,
            found: !!parsed,
            downloadError: error?.message,
            opsCount: parsed?.delta?.ops?.length ?? 0,
          });
        } else {
          console.log('[first-entry] no doc path for this row — cannot check', { chartDocSchema: ctx.chartDocSchema });
        }
      } catch (e) {
        // Absent / unparseable `.json` — nobody has typed here yet.
        console.log('[first-entry] current visit .json unreadable (treated as untouched)', {
          path,
          message: e?.message,
        });
      }
      if (stale()) return;

      // 2. What would this visit have been seeded with? ONLY resolve this when
      //    a `.json` actually exists to compare against. With collab on there
      //    never is one (the server writes `.yjs` only), so on the common path
      //    this is pure cost — and it is not free: resolveTemplateDoc reaches
      //    supabase.auth.getSession(), which can deadlock when several
      //    GoTrueClient instances share a storage key, as they do here. A hung
      //    await there stalled the whole prompt. The timeout below is the
      //    belt-and-braces version of the same guard for the collab-off path.
      //
      //    The candidate must be the FILLED template: a raw assets/templates
      //    JSON still holds [Patient Name] / [MRN] and could never match.
      const templateDocs = [];
      if (parsed) {
        try {
          const resolved = await withTimeout(resolveTemplateDoc(ctx.patient), 4000);
          if (resolved) templateDocs.push(resolved);
          else console.log('[first-entry] template resolution timed out or returned nothing');
        } catch (e) {
          // No candidate — isUntouchedDoc then treats any real content as
          // touched, which is the safe direction (no prompt, no overwrite risk).
          console.log('[first-entry] template resolution failed', e?.message);
        }
      }
      if (stale()) return;

      const markPrompted = () => {
        try { localStorage.setItem(flagKey, '1'); } catch (_) { /* fine */ }
      };

      const untouched = isUntouchedDoc(parsed, templateDocs);
      console.log('[first-entry] untouched check', {
        untouched,
        reason: !parsed
          ? 'no .json — nobody has typed'
          : untouched
            ? 'stored .json still matches the seeded template'
            : 'stored .json has user-written content — not offering',
        templateCandidates: templateDocs.length,
      });

      if (!untouched) {
        markPrompted();
        return;
      }

      console.log('[first-entry] searching prior visits…');
      await ctx.onCopyOlder(true);
      if (stale()) return;
      markPrompted();
    })();

  // Intentionally NOT depending on visit.patient / chartDocSchema / onCopyOlder
  // — see firstEntryCtxRef above. Those churn identity on refetch and would
  // cancel an in-flight run that then never restarts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, firstEntryReady]);


  const onRemoveDuplicates = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) {
      toast({ title: 'Editor not ready', status: 'warning', duration: 2500 });
      return;
    }
    try {
      const currentDelta = editor.getContents();
      if (!currentDelta || !currentDelta.ops) {
        toast({ title: 'No content to scan', status: 'info', duration: 2500 });
        return;
      }
      const fullText = editor.getText();
      const lines = fullText.split('\n');
      let firstLine = '';
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.length > 0) { firstLine = line; break; }
      }
      if (!firstLine) {
        toast({ title: 'No content found', status: 'info', duration: 2500 });
        return;
      }
      let firstDuplicateIndex = -1;
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim() === firstLine) { firstDuplicateIndex = i; break; }
      }
      if (firstDuplicateIndex === -1) {
        toast({
          title: 'No duplicates found',
          description: 'No duplicate content detected in this document.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const cleanLines = lines.slice(0, firstDuplicateIndex);
      const cleanText = cleanLines.join('\n');
      let cutIndex = -1;
      let accumulated = '';
      for (let i = 0; i < currentDelta.ops.length; i++) {
        const op = currentDelta.ops[i];
        if (op.insert && typeof op.insert === 'string') {
          accumulated += op.insert;
          if (accumulated.includes(cleanText) && accumulated.length >= cleanText.length) {
            cutIndex = i + 1;
            break;
          }
        }
      }
      if (cutIndex === -1) {
        toast({ title: 'Could not locate cut point', status: 'warning', duration: 3000 });
        return;
      }
      const cleanOps = currentDelta.ops.slice(0, cutIndex);
      const cleanDelta = { ...currentDelta, ops: cleanOps };
      const yText = collabActive ? collab?.getYText?.() : null;
      const yDoc = collabActive ? collab?.getYDoc?.() : null;
      if (yText && yDoc?.transact) {
        yDoc.transact(() => {
          if (yText.length > 0) yText.delete(0, yText.length);
          yText.applyDelta(cleanOps);
        });
      } else {
        editor.setContents([{ insert: '\n' }], 'api');
        editor.setContents(cleanDelta, 'api');
      }
      pendingDeltaRef.current = editor.getContents();
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(persistDelta, 250);
      const removed = currentDelta.ops.length - cleanOps.length;
      toast({
        title: 'Duplicates removed',
        description: `Trimmed ${removed} duplicate ops.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Remove duplicates failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast, persistDelta, patientReadOnly, showPatientReadOnlyToast]);

  // Reset the document to a FRESH template matching the patient's visit type
  // (e.g. NP). Reuses the same builder usePatientDocument seeds new docs from,
  // and the same atomic replace path as Copy-Older: live YText when collab is
  // on (Quill.setContents would echo through QuillBinding and double the doc),
  // plain Quill.setContents otherwise. Queues a save so the reset persists.
  const [reloadOpen, setReloadOpen] = useState(false);
  const [reloadCode, setReloadCode] = useState('');
  const [reloadOptions, setReloadOptions] = useState([]);
  const [reloadPrior, setReloadPrior] = useState(null);

  const onReloadTemplate = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    const patientRow = visit.patient;
    if (!patientRow) {
      toast({ title: 'Patient not loaded yet', status: 'warning', duration: 2500 });
      return;
    }
    setReloadPrior(null);
    setReloadCode(effectiveTemplateCode(patientRow));
    // Open on the built-ins straight away — the custom-template lookup reaches
    // supabase.auth.getSession(), which can hang, so it must never gate the UI.
    setReloadOptions(await listTemplateOptions({ includeFollowUps: false }));
    setReloadOpen(true);

    // FFP/FFS only make sense when there is something to copy, so they are
    // added once the prior-visit search resolves rather than blocking on it.
    try {
      const prior = await findPriorPopulatedVisit(patientRow, chartDocSchema);
      if (!prior) return;
      setReloadPrior(prior);
      setReloadOptions(await listTemplateOptions({ includeFollowUps: true }));
    } catch (e) {
      console.warn('[reload-template] prior-visit lookup failed:', e?.message);
    }
  }, [visit.patient, patientReadOnly, showPatientReadOnlyToast, toast, chartDocSchema, findPriorPopulatedVisit]);

  // localStorage ledger of which template code this visit's document currently
  // reflects. Lets the on-entry effect notice that the row's Template was
  // changed elsewhere (Edit Patient) since the document was last built.
  // v2: entries written before the stale-row guards existed can hold the
  // PREVIOUS patient's code (recorded mid-switch), and trusting one fires a
  // destructive rebuild on the next entry. The namespace bump orphans them;
  // each visit silently re-baselines to its current code on first entry.
  const appliedTemplateKey = (id) => `medstudent-applied-template-v2:${id}`;

  // Replace the document with `code`'s content. Shared by the Reload dialog and
  // the on-entry re-seed so both take the identical, collab-safe path.
  const applyTemplateByCode = useCallback(async (code, priorForFollowUp = null) => {
    const patientRow = visit.patient;
    // This function waits (editor mount, CRDT hydration) for up to several
    // seconds, so it can START on one visit and RESUME after the user has
    // switched to another — at which point collabRef points at the NEW
    // patient's live doc and writing would destroy it with content resolved
    // from the OLD row. Same live-ref abort discipline as the first-entry
    // effect: check at every await-resume, never act across a visit change.
    const startedForUuid = firstEntryUuidRef.current;
    const visitChanged = () =>
      !firstEntryAliveRef.current || firstEntryUuidRef.current !== startedForUuid;
    if (!patientRow || !rowMatchesVisitParam(patientRow, startedForUuid)) {
      console.warn('[apply-template] patient row does not belong to the current visit — refusing', {
        rowId: patientRow?.id, uuid: startedForUuid,
      });
      return false;
    }
    // The on-entry sync can fire before DocumentBody has mounted its Quill
    // instance, so wait briefly rather than bailing — bailing silently did
    // nothing at all and left the ledger stale, so the change never applied.
    let editor = documentBodyRef.current?.getEditor?.(0);
    for (let i = 0; i < 20 && !editor; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 250));
      editor = documentBodyRef.current?.getEditor?.(0);
    }
    if (!editor || !patientRow) return false;
    if (visitChanged()) return false;

    let newOps;
    if (FOLLOW_UP_TYPES.has(code)) {
      // FFP/FFS load no template file — they carry the prior visit forward.
      const prior = priorForFollowUp
        || await findPriorPopulatedVisit(patientRow, chartDocSchema);
      if (!prior) return false;
      newOps = prior.delta?.ops || [{ insert: '\n' }];
    } else {
      // Resolve as if the row already carried this code, so the custom-template
      // preference, the BUILTIN: pin and placeholder filling all behave exactly
      // as they would on a fresh seed.
      const seeded = await resolveTemplateDoc({ ...patientRow, template: code });
      newOps = seeded?.delta?.ops || [{ insert: '\n' }];
    }
    if (visitChanged()) return false;

    if (isReadOnly) {
      try {
        setIsReadOnly(false);
        if (uuid) sessionStorage.setItem(editModeKey(uuid), 'edit');
      } catch (_) { /* fine */ }
    }

    if (collabActive) {
      // Wait for the CRDT. On entry this runs while the WebSocket is still
      // connecting, and `collabRef` (not the closure) is what makes the live
      // session visible here at all.
      //
      // Also wait for the doc to have HYDRATED (length > 0): clearing an
      // empty YText and applying before the server's state arrives lets that
      // state merge in afterwards — which is exactly the "new template
      // appended below the old one" symptom.
      let yText = collabRef.current?.getYText?.();
      let yDoc = collabRef.current?.getYDoc?.();
      for (let i = 0; i < 40 && !(yText && yDoc?.transact && yText.length > 0); i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 250));
        yText = collabRef.current?.getYText?.();
        yDoc = collabRef.current?.getYDoc?.();
      }
      if (visitChanged()) return false;
      if (!(yText && yDoc?.transact)) {
        // Deliberately do NOT fall back to editor.setContents here. With a live
        // QuillBinding attached that merges rather than replaces, doubling the
        // document — worse than doing nothing and reporting failure.
        console.warn('[apply-template] collab never provided a YText — aborting rather than merging');
        return false;
      }
      yDoc.transact(() => {
        if (yText.length > 0) yText.delete(0, yText.length);
        yText.applyDelta(newOps);
      });
    } else {
      // No collab — clear then set so any residual diff-merge in Quill
      // doesn't keep parts of the prior content around.
      editor.setContents([{ insert: '\n' }], 'api');
      editor.setContents({ ops: newOps }, 'api');
    }
    try {
      pendingDeltaRef.current = editor.getContents();
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(persistDelta, 250);
    } catch (_) { /* fine */ }
    try { localStorage.setItem(appliedTemplateKey(uuid), String(code)); } catch (_) { /* fine */ }
    return true;
  // collab is read at call time, like the other document-replacing handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit.patient, isReadOnly, uuid, persistDelta, chartDocSchema, findPriorPopulatedVisit]);

  const handleReloadConfirm = useCallback(async () => {
    const patientRow = visit.patient;
    const code = reloadCode;
    try {
      const ok = await applyTemplateByCode(code, reloadPrior);
      if (!ok) {
        toast({ title: 'Could not load that template', status: 'warning', duration: 2500 });
        return;
      }
      // Persist the choice on the row so the visit remembers it.
      try {
        const supabase = await getSupabase();
        await supabase
          .schema(chartDocSchema)
          .from('notes-c')
          .update({ [TEMPLATE_FIELD]: code })
          .eq('id', patientRow.id);
        // Phase D (utils/rdsCutover.js `write_visit_template`): mirror the
        // changed column to RDS now; /write upserts by id and touches only
        // the columns sent. Best effort, reported, never awaited.
        mirrorWriteToRds('write_visit_template', chartDocSchema, { id: patientRow.id, [TEMPLATE_FIELD]: code });
        visit.refetch?.();
      } catch (e) {
        console.warn('[reload-template] could not save template on the row:', e?.message);
      }
      toast({ title: `Reloaded ${code} template`, status: 'success', duration: 2500, isClosable: true });
    } catch (e) {
      toast({
        title: 'Reload template failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setReloadOpen(false);
    }
  }, [visit, reloadCode, reloadPrior, toast, chartDocSchema, applyTemplateByCode]);

  // Re-seed on entry when the row's Template was changed elsewhere.
  //
  // Changing Template in Edit Patient only rewrites a column; the document
  // already exists (the collab server seeded its room from the OLD code and
  // persists it in `.yjs`), so without this the change appears to do nothing.
  //
  // The ledger records which code the document currently reflects; when it
  // diverges from the row, the document is rebuilt from the new template.
  //
  // This replaces the note UNCONDITIONALLY — including one that has been
  // written up. That is the requested behaviour: changing Template in Edit
  // Patient is meant to act exactly like pressing Reload Template. It is
  // therefore a destructive action taken without confirmation, and Version
  // History is the only way back.
  const templateSyncFiredRef = useRef(false);
  useEffect(() => { templateSyncFiredRef.current = false; }, [uuid]);
  useEffect(() => {
    if (!uuid || templateSyncFiredRef.current) return;
    const ctx = firstEntryCtxRef.current;
    if (!ctx?.patient || ctx.isLoading || ctx.rosterMode || ctx.patientReadOnly) return;

    // A row can still be paired with a uuid it doesn't belong to — a `seed`
    // handed over from nav state, or a row captured in a closure before the
    // switch. (The hook no longer holds the PREVIOUS visit's row across a
    // switch, which is what used to cause this.) Acting on that pairing compared
    // the OLD patient's template code against the NEW visit's ledger — a
    // spurious mismatch that destructively rebuilt the document from the
    // wrong patient's template (or poisoned the ledger so the rebuild fired
    // on the next entry). Bail WITHOUT setting the fired flag; the effect
    // re-runs via firstEntryReady once the row for this uuid lands.
    if (!rowMatchesVisitParam(ctx.patient, uuid)) return;

    const code = effectiveTemplateCode(ctx.patient);
    let applied = null;
    try { applied = localStorage.getItem(appliedTemplateKey(uuid)); } catch (_) { /* fine */ }

    // First time we've seen this visit: the server already seeded from this
    // code, so just record it — replacing content here would be pointless
    // churn and would fight the first-entry copy prompt.
    if (!applied) {
      try { localStorage.setItem(appliedTemplateKey(uuid), code); } catch (_) { /* fine */ }
      templateSyncFiredRef.current = true;
      return;
    }
    if (applied === code) { templateSyncFiredRef.current = true; return; }

    templateSyncFiredRef.current = true;
    console.log('[template-sync] row template changed since this doc was built', {
      documentReflects: applied, rowNowSays: code,
    });

    (async () => {
      const ok = await applyTemplateByCode(code);
      if (ok) {
        console.log('[template-sync] document rebuilt from the new template', { code });
        toast({
          title: `Loaded ${code} template`,
          description: 'The template changed on this visit. Use Version History to recover the previous content.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } else {
        console.warn('[template-sync] could not apply the new template', { code });
      }
    })();
  }, [uuid, firstEntryReady, toast, applyTemplateByCode]);

  // Save the CURRENT document as a reusable, user-scoped template. The student
  // picks a short type code; later typing that code into the AddPatientModal
  // "Type" field makes new visits (and Reload template) seed from this doc.
  const onSaveAsTemplate = useCallback(async () => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) {
      toast({ title: 'Editor not ready', status: 'warning', duration: 2500 });
      return;
    }
    const code = window.prompt(
      'Save this document as a reusable template.\n\nEnter a short type code (e.g. NP2, PEDS). Later put this code in the "Type" field when adding a patient to start from this template:',
    );
    if (code == null) return; // cancelled
    if (!code.trim()) {
      toast({ title: 'Template not saved', description: 'A type code is required.', status: 'warning', duration: 2500 });
      return;
    }
    try {
      const delta = documentBodyRef.current?.getFreshContents?.() || editor.getContents();
      await saveCustomTemplate({ typeCode: code, delta });
      toast({
        title: `Saved template "${normalizeTypeCode(code)}"`,
        description: 'Use this code in the Type field when adding a patient to load it.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Save template failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  }, [toast]);

  const onShare = useCallback(() => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    toast({
      title: 'Share patient — coming soon',
      description:
        'Will open the share dialog for this row (matches the share modal on EditableDocumentPage).',
      status: 'info',
      duration: 3500,
      isClosable: true,
    });
  }, [toast, patientReadOnly, showPatientReadOnlyToast]);

  // ── File menu handlers (mirror DocumentNavBar.js File dropdown) ──────────

  // New Document / Open both route the user back to the schedules screen —
  // that's where a fresh patient row is created and where existing visits
  // are picked. Matches the doctor flow's mental model.
  const onNewDocument = useCallback(() => {
    navigate(scheduleDate ? `/schedules?date=${scheduleDate}` : '/schedules');
  }, [navigate, scheduleDate]);

  const onOpenDocument = useCallback(() => {
    navigate(scheduleDate ? `/schedules?date=${scheduleDate}` : '/schedules');
  }, [navigate, scheduleDate]);

  // Force-save: cancel the debounce timer and flush whatever's pending.
  const onSaveDocument = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    try {
      clearTimeout(saveTimerRef.current);
      // Always grab the live delta from the editor so the save reflects
      // any keystrokes the autosave timer hadn't picked up yet.
      const editor = documentBodyRef.current?.getEditor?.(0);
      if (editor) pendingDeltaRef.current = editor.getContents();
      await persistDelta();
      toast({ title: 'Saved', status: 'success', duration: 1500, isClosable: true });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  }, [persistDelta, toast, patientReadOnly, showPatientReadOnlyToast]);

  const onSaveVersion = useCallback(async () => {
    if (patientReadOnly) {
      showPatientReadOnlyToast();
      return;
    }
    const vs = versioningSystemRef.current;
    if (!vs) {
      toast({ title: 'Versioning not ready yet', status: 'warning', duration: 2500 });
      return;
    }
    const name = window.prompt('Name this version:', '');
    if (!name || !name.trim()) return;
    try {
      await vs.createVersion(name.trim(), 'MANUALLY CREATED BY USER');
      toast({ title: `Saved version "${name.trim()}"`, status: 'success', duration: 2500 });
      refreshVersionCount();
    } catch (e) {
      toast({
        title: 'Save version failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  }, [toast, refreshVersionCount, patientReadOnly, showPatientReadOnlyToast]);

  const onDownloadDocx = useCallback(async () => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) {
      toast({ title: 'Editor not ready', status: 'warning', duration: 2500 });
      return;
    }
    const baseName =
      [visit.patient?.patient_name, visit.patient?.date_]
        .filter(Boolean)
        .join(' - ') || 'document';
    try {
      await exportEditorToDocx(editor, baseName);
    } catch (e) {
      toast({
        title: 'Download failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  }, [toast, visit.patient]);

  // Reformat reuses the existing reload-template stub — applying template
  // formatting to current content is a larger task (see DocumentNavBar
  // handleReformat) and will be folded in alongside Reload Template.
  const onReformat = useCallback(() => {
    toast({
      title: 'Reformat Document — coming soon',
      description:
        'Will reapply the template formatting to the current text without touching content.',
      status: 'info',
      duration: 3500,
      isClosable: true,
    });
  }, [toast]);

  // Print: render only the editor's HTML in a hidden iframe and trigger
  // the system print dialog. Keeps the sidebar / right panel out of the
  // printed page without needing global print CSS rules.
  const onPrint = useCallback(async () => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    const html = editor?.root?.innerHTML;
    if (!html) {
      toast({ title: 'Nothing to print', status: 'warning', duration: 2500 });
      return;
    }
    await recordDataOutEvent('PRINT', {
      resource_type: 'notes',
      resource_table: 'notes_c',
      patient_id: visit.patient?.id || visit.patient?.patient_id || null,
    });
    const title =
      [visit.patient?.patient_name, visit.patient?.date_].filter(Boolean).join(' — ') ||
      'Document';
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '-9999px';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const cleanup = () => {
      // Defer removal so the print dialog has a chance to grab the document.
      setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } }, 1000);
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; padding: 1in; color: #111; line-height: 1.45; }
  h1, h2, h3 { font-family: 'Helvetica Neue', Arial, sans-serif; }
  @page { margin: 0.75in; }
</style></head><body>${html}</body></html>`);
    doc.close();
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } finally {
        cleanup();
      }
    };
  }, [toast, visit.patient]);

  // ── Switch format (Vitals boxes / ROS cards ⇄ plain copyable text) ───────
  // The NP template renders Vitals as input boxes and ROS as cards (Quill
  // block embeds). They're contenteditable=false widgets, so selecting across
  // the note can't cleanly copy their text. "Switch format" flattens those two
  // sections into plain labeled lines (and back). The conversion is a pure
  // delta transform (switchSectionFormat.js); we apply it the same way Copy-
  // from-older does — YText for live collab, setContents otherwise — then queue
  // a save. The "positive/abnormal" ROS shading can't live in plain text, so we
  // stash it per-visit in localStorage and restore it on the way back.
  const sectionStashKey = uuid ? `medstudent-section-format-stash:${uuid}` : null;
  const [docFormat, setDocFormat] = useState('none'); // 'cards' | 'text' | 'none'

  const recomputeDocFormat = useCallback(() => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) { setDocFormat('none'); return; }
    try {
      const delta = documentBodyRef.current?.getFreshContents?.() || editor.getContents();
      setDocFormat(detectSectionFormat(delta));
    } catch {
      setDocFormat('none');
    }
  }, []);

  // Recompute after the editor mounts and whenever its contents settle. A short
  // debounce coalesces typing bursts and lets the collab WebSocket finish
  // hydrating (which arrives as api text-changes) before we read the format.
  useEffect(() => {
    if (!liveQuill) { setDocFormat('none'); return undefined; }
    let t;
    const onChange = () => { clearTimeout(t); t = setTimeout(recomputeDocFormat, 400); };
    recomputeDocFormat();
    liveQuill.on('text-change', onChange);
    return () => { clearTimeout(t); liveQuill.off('text-change', onChange); };
  }, [liveQuill, recomputeDocFormat]);

  const onSwitchFormat = useCallback(() => {
    const editor = documentBodyRef.current?.getEditor?.(0);
    if (!editor) {
      toast({ title: 'Editor not ready', status: 'warning', duration: 2500 });
      return;
    }
    let delta;
    try { delta = documentBodyRef.current?.getFreshContents?.() || editor.getContents(); }
    catch { delta = editor.getContents(); }

    let stash = null;
    try {
      const raw = sectionStashKey && localStorage.getItem(sectionStashKey);
      if (raw) stash = JSON.parse(raw);
    } catch (_) { /* fine */ }

    const result = switchSectionFormat(delta, { stash });
    if (!result.changed) {
      toast({
        title: 'Nothing to switch',
        description: 'This note has no Vitals or Review of Systems section to reformat.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newOps = result.delta.ops;
    const yText = collabActive ? collab?.getYText?.() : null;
    const yDoc = collabActive ? collab?.getYDoc?.() : null;
    try {
      if (yText && yDoc?.transact) {
        // Drive the change through the CRDT so it replicates to other viewers;
        // Quill.setContents would echo back through QuillBinding and duplicate.
        yDoc.transact(() => {
          if (yText.length > 0) yText.delete(0, yText.length);
          yText.applyDelta(newOps);
        });
      } else {
        editor.setContents({ ops: newOps }, 'api');
      }
      // Persist the captured embed values so switching back restores ROS
      // shading the plain text can't carry.
      try {
        if (result.format === 'text' && result.stash && sectionStashKey) {
          localStorage.setItem(sectionStashKey, JSON.stringify(result.stash));
        }
      } catch (_) { /* fine */ }
      setDocFormat(result.format);
      // Queue a save so the reformat lands in storage even without further edits.
      try {
        pendingDeltaRef.current = documentBodyRef.current?.getFreshContents?.() || editor.getContents();
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(persistDelta, 250);
      } catch (_) { /* fine */ }
      toast({
        title: result.format === 'text' ? 'Vitals & ROS switched to text' : 'Vitals & ROS switched to cards',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: 'Switch format failed',
        description: e?.message || String(e),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  // collab is read at click-time (declared above); listed-as-closure like onCopyOlder.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, sectionStashKey, toast, persistDelta]);

  // (documentPath is computed above for autosave; VersionHistoryModal reuses it)

  // Hold the first paint until the viewer's role is known.
  //
  // useUserInfo starts at { role: null, loading: true }, so patientReadOnly is
  // false on the initial render — which meant a patient briefly saw the FULL
  // clinician chart (Record Visit, Voice Type, the editor toolbar, the clinic
  // AI panel) before it flipped to the read-only summary. That flash is both
  // jarring and shows a patient controls they must never have.
  //
  // Placed immediately before the render and after every hook, so no hook is
  // ever skipped. The shell matches the clinic background so there is no white
  // flash either.
  if (userInfo.loading) {
    return (
      <div
        id="clinic-root"
        className={`${effectiveTheme === 'dark' ? 'dark' : ''} flex h-screen w-full items-center justify-center bg-slate-50 dark:bg-[#0a0a0f]`}
        aria-busy="true"
      >
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  return (
    <div id="clinic-root" className={`${effectiveTheme === 'dark' ? 'dark' : ''} flex flex-col h-screen w-full overflow-hidden bg-slate-50 dark:bg-[#0a0a0f] text-slate-900 dark:text-slate-100 antialiased font-sans`}>
      {/* Doctor portal command bar — sticky on top of the existing split-view
          chart. Hidden for read-only patients (Record Visit / Voice Type /
          ICD-10 / Share are clinician-only). */}
      {!patientReadOnly && <DoctorPortalBar />}
      <div className="flex flex-1 w-full overflow-hidden">
      {/* Small-viewport notice — lg layout is cramped below 1024px; dismiss per session */}
      {showNarrowGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center text-center p-6 bg-slate-50/95 dark:bg-[#0a0a0f]/95 text-slate-600 dark:text-slate-300 text-sm">
          <div className="flex flex-col gap-4 max-w-xs">
            <p className="font-medium text-slate-700 dark:text-slate-200">
              This workspace is optimized for wider screens (about 1024px or more across).
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              On smaller laptops panels may overlap or require horizontal scrolling—you can continue if you prefer.
            </p>
            <button
              type="button"
              onClick={dismissNarrowGate}
              className="rounded-lg bg-slate-800 dark:bg-slate-600 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 dark:hover:bg-slate-500"
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}
      {/* Left icon rail (patient profile / schedule / reports icons) is
          clinician-only — hidden for read-only patients. */}
      {!patientReadOnly && (
      <IconRail
        theme={effectiveTheme}
        onToggleTheme={toggleTheme}
        currentPatientUuid={uuid}
        scheduledDate={visit.patient?.date_ || scheduleDate || null}
        onOpenReports={() => setIsReportsOpen(true)}
        onOpenPriorVisits={() => setIsPriorVisitsOpen(true)}
        onOpenPatientProfile={() => setIsPatientProfileOpen(true)}
        userInitials={userInfo.initials}
        presenceUsers={presenceUsers}
        onAvatarClick={collabActive ? handleAvatarClick : undefined}
      />
      )}
      <PatientProfileDrawer
        patient={visit.patient}
        isOpen={isPatientProfileOpen}
        onClose={() => setIsPatientProfileOpen(false)}
      />
      {!patientReadOnly && !leftCollapsed && (
        <PatientListPanel
          date={date}
          schedules={schedule.schedules}
          isLoading={schedule.isLoading}
          error={schedule.error}
          onRetry={schedule.refetch}
          activeUuid={uuid}
          onCollapse={() => setLeftCollapsed(true)}
          providerName={userInfo.name}
          providerInitials={userInfo.initials}
          viewerTimezone={schedule.viewerTimezone}
        />
      )}
      {!patientReadOnly && leftCollapsed && (
        <button
          type="button"
          onClick={() => setLeftCollapsed(false)}
          aria-label="Back to patients"
          title="Back to patients"
          className="group w-10 shrink-0 border-r border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] hover:bg-slate-50 dark:hover:bg-slate-800/40 flex flex-col items-center pt-3 gap-2 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {/* Vertical label so the affordance is obvious in the rail. */}
          <span
            className="text-[10px] font-bold tracking-[0.2em] uppercase"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            Back to patients
          </span>
        </button>
      )}
      <main className="flex-1 flex flex-col min-w-0 min-h-0 bg-slate-50 dark:bg-[#0a0a0f] relative">
        <PatientHeaderBar
          patient={visit.patient}
          document={visit.document}
          // Where this chart's row actually came from. `chartDocSchema` is the
          // schema every read/write on this page is pinned to — for a chart
          // opened from a linked provider that is NOT the viewer's home schema,
          // which is exactly the case the badge exists to make visible. Table
          // follows the same split the rest of the page uses: visits live in
          // notes-c, roster charts (/patients/:uuid) in patients_with_ehrid.
          rowSchema={chartDocSchema}
          rowTable={chartTable(visit.patient)}
          // Where this note's .json lives in the `files` bucket — rendered as a
          // click-to-copy chip beside the owner name.
          documentPath={documentPath}
          // notes-c.user_id is the row OWNER — the account that inserted the
          // visit (shareAccess.js, AddPatientModal). patients_with_ehrid.user_id
          // is a different thing entirely: it links a roster row to the
          // PATIENT's portal account (2026-07-30 migration, "one roster row per
          // portal account"), so naming it "created by" on a roster chart would
          // credit the patient. Roster charts simply have no creator to show.
          rowCreatorId={
            chartTable(visit.patient) === 'patients_with_ehrid'
              ? null
              : visit.patient?.user_id
          }
          versionCount={versionCount}
          documentVersionStatus={documentVersionStatus}
          conciseNote={conciseNote}
          onToggleConcise={patientReadOnly ? undefined : toggleConciseNote}
          permissionLevel={
            // Same resolution EditableDocumentPage uses for tenant-schema
            // rows: viewer-id vs notes-c row's user_id + shared_users gives
            // owner / admin / editor / viewer / none. Independent of the
            // read/edit-mode toggle.
            resolveTenantRowAccess({
              viewerId: userInfo.userId,
              rowUserId: visit.patient?.user_id,
              sharedUsers: visit.patient?.shared_users,
            }).level
          }
          completed={isVisitCompleted(visit.patient)}
          readOnly={effectiveReadOnly}
          // Hide the entire action cluster (Edit / Restore / Add Problem /
          // Forms / Tools / Process / Master Note / Sign) for the patient
          // role. The summary panel that replaces the document body is
          // already non-actionable; the header shouldn't tease controls
          // the patient can't use.
          patientReadOnly={patientReadOnly}
          onToggleReadOnly={handleEditToggle}
          signedEditing={isSigned && signedEditGranted}
          onRestore={patientReadOnly ? undefined : onRestore}
          onAudios={patientReadOnly ? undefined : () => setIsAudiosOpen(true)}
          onVersionHistory={onVersionHistory}
          onForms={(patientReadOnly || !canFormsAction) ? undefined : onForms}
          onOpenClinicMeds={patientReadOnly ? undefined : onOpenClinicMeds}
          canOpenClinicMeds={canOpenClinicMeds}
          // Hidden for the patient role: these rows carry staff assignment
          // and completion state, which is clinician-facing.
          // Passing undefined REMOVES the button rather than disabling it —
          // a control the viewer may not use should not be on screen.
          onOpenClinicOrders={(patientReadOnly || !canOrdersAction) ? undefined : onOpenClinicOrders}
          canOpenClinicOrders={canOpenClinicOrders && canOrdersAction}
          onProcess={patientReadOnly ? undefined : onProcess}
          onMasterNote={(patientReadOnly || !canMasterNoteAction) ? undefined : (() => {
            // Master Note trigger — bypasses Diego's Parked Review flow
            // (which the header Process button now uses) and opens
            // ProcessModal directly. ProcessModal.generate() posts to
            // /api/master-note/ for whole-note rewrite (from 6be499a9).
            // The button was added because the merge with prd0701 silently
            // orphaned the Master Note frontend when Diego rewired the
            // Process button to Parked Review — this restores a reachable
            // trigger without disturbing his flow. Event 15.1 recurrence.
            // PHI guard (mirrors recorder #269): remember which patient this
            // Master Note is for, so a backgrounded generation can't land in
            // another chart if the user navigates before it finishes.
            masterNoteOriginRef.current = { id: uuid, name: visit?.patient?.patient_name || 'patient' };
            setProcessPinAudioSessionId(null);
            setProcessPinReportPath(null);
            setProcessAutoGenerate(false);
            setIsProcessOpen(true);
          })}
          unprocessedCount={unprocessedCount}
          hasSavedDiff={!!savedMasterDiff.savedDiff}
          onSign={(patientReadOnly || !canSignAction) ? undefined : onSign}
          signed={isSigned}
          signedBy={signedBy}
          signedAt={signedAt}
          isSigning={isSigning}
          canReveal={isSigned && isSigner}
          onReveal={onRevealPassword}
          visitInProgress={visitInProgress}
          onCopyOlder={(patientReadOnly || rosterMode) ? undefined : onCopyOlder}
          onRemoveDuplicates={patientReadOnly ? undefined : onRemoveDuplicates}
          onReloadTemplate={patientReadOnly ? undefined : onReloadTemplate}
          onSaveAsTemplate={patientReadOnly ? undefined : onSaveAsTemplate}
          onShare={patientReadOnly ? undefined : onShare}
          canShare={!patientReadOnly}
          onOpenAudioManager={patientReadOnly ? undefined : () => setIsAudioManagerOpen(true)}
          onCombine={rosterMode ? undefined : () => combineDocuments(date)}
          onSwitchFormat={onSwitchFormat}
          sectionFormat={docFormat}
          editorRef={documentBodyRef}
        />
        {foreignRecordingActive && (
          <div
            role="alert"
            className="flex items-center gap-2 border-y border-red-300 bg-red-100 px-6 py-2.5 text-sm font-semibold text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            <span aria-hidden>⛔</span>
            <span>
              The recorder is transcribing under a DIFFERENT patient&rsquo;s chart right now —
              this visit&rsquo;s audio is going to the wrong patient. In the recorder popup:
              Stop &amp; Save (it files under the patient it started on), then Sync Patient
              before recording here.
            </span>
            <button
              type="button"
              onClick={() => setForeignRecordingActive(false)}
              className="ml-auto shrink-0 rounded px-2 py-0.5 text-xs underline"
            >
              dismiss
            </button>
          </div>
        )}
        {collabBlocksEditing && (
          <div
            role="alert"
            data-testid="collab-typing-paused-banner"
            className="flex items-center gap-2 border-y border-red-300 bg-red-100 px-6 py-2.5 text-sm font-semibold text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" aria-hidden />
            <span>
              {collab.hasConnected
                ? 'Lost connection to the live document — typing is paused so nothing you write is lost. Reconnecting automatically; your existing text is safe.'
                : 'Still connecting to the live document — typing is paused until it syncs so your first edits aren’t overwritten.'}
            </span>
          </div>
        )}
        {effectiveReadOnly && (
          <div
            role="status"
            className="transition-opacity duration-200 bg-amber-50 dark:bg-amber-500/[0.08] border-y border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-200 text-sm px-6 py-2 flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>
              {patientReadOnly ? (
                'You are viewing this document in read-only patient portal mode.'
              ) : (
                <>
                  You&rsquo;re viewing this document in read mode. Click{' '}
                  <button
                    type="button"
                    onClick={handleEditToggle}
                    aria-label="Switch to edit mode"
                    className="font-semibold underline hover:text-amber-950 dark:hover:text-amber-50 cursor-pointer"
                  >
                    Edit
                  </button>{' '}
                  in the header to start editing.
                </>
              )}
            </span>
          </div>
        )}
        {/* Patient portal — patients see only the AI-generated, patient-safe
            visit summary (Task 9). The clinician editor (toolbar + document +
            audio manager) is swapped out entirely for read-only patient roles. */}
        {patientReadOnly ? (
          <div
            data-clinic-patient-summary
            style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '24px' }}
          >
            <div style={{ maxWidth: 860, margin: '0 auto', width: '100%' }}>
              <PatientSummaryPanel
                appointmentId={uuid}
                schema={userInfo.patientUserRow?.patient_verified_schema}
                userId={userInfo.userId}
                role="patient"
              />
            </div>
          </div>
        ) : (
          <>
        <DocumentToolbar
          editorRef={documentBodyRef}
          document={visit.document}
          autosaveStatus={{ savedAt }}
          templateName={visit.patient?.type || 'NP'}
          readOnly={inputBlocked}
          onNewDocument={onNewDocument}
          onOpenDocument={onOpenDocument}
          onSaveDocument={onSaveDocument}
          onShareDocument={patientReadOnly ? undefined : onShare}
          onVersionHistory={onVersionHistory}
          onSaveVersion={patientReadOnly ? undefined : onSaveVersion}
          onDownloadDocx={onDownloadDocx}
          onReformat={patientReadOnly ? undefined : onReformat}
          onPrint={onPrint}
          onCommentClick={handleToolbarCommentClick}
          onAssignTaskClick={canAssignTasks ? handleAssignTaskClick : undefined}
          safeShareMode={safeShareMode}
          safeShareMethod={safeShareMethod}
          isDeidentifying={isDeidentifying}
          onToggleSafeShare={toggleSafeShareMode}
          onSafeShareMethodChange={setSafeShareMethod}
        />

        {/* Doctor-facing patient summary — collapsible so it doesn't crowd the
            chart. Renders the same PatientSummaryPanel the patient sees, with
            clinician controls (Edit / Regenerate / Hide) and, later, patient
            comments (Task 12). Lazy-mounted: only fetches when expanded. */}
        <Accordion
          allowToggle
          flexShrink={0}
          mx="24px"
          mt={2}
          index={patientSummaryOpen ? 0 : -1}
          onChange={(i) => setPatientSummaryOpen(i === 0)}
        >
          <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
            <AccordionButton _expanded={{ bg: 'blackAlpha.50' }}>
              <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize="sm" color={effectiveTheme === 'dark' ? 'white' : undefined}>
                Patient summary
              </Box>
              {/* Unseen patient-comment count — same rose badge as the Process
                  button. Clears when this panel is opened (comments marked seen). */}
              {patientCommentsUnseen > 0 && (
                <Box
                  as="span"
                  aria-label={`${patientCommentsUnseen} unseen patient comments`}
                  mr={2}
                  minW="18px"
                  h="18px"
                  px="1"
                  borderRadius="full"
                  bg="#f43f5e"
                  color="white"
                  fontSize="11px"
                  fontWeight="bold"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {patientCommentsUnseen}
                </Box>
              )}
              {/* Expand to read the full summary (the panel below is height-capped). */}
              <Box
                as="span"
                role="button"
                tabIndex={0}
                aria-label="Expand full summary"
                title="Expand full summary"
                mr={2}
                px={2}
                py="1px"
                borderRadius="md"
                fontSize="14px"
                lineHeight="1.4"
                color={effectiveTheme === 'dark' ? 'white' : 'gray.600'}
                _hover={{ bg: effectiveTheme === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.100' }}
                onClick={(e) => { e.stopPropagation(); setPatientSummaryModalOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setPatientSummaryModalOpen(true); } }}
              >
                ⛶
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} maxH="40vh" overflowY="auto">
              {patientSummaryOpen && (
                <PatientSummaryPanel
                  appointmentId={uuid}
                  schema={chartDocSchema}
                  userId={userInfo.userId}
                  role="clinician"
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* Expanded, uncapped full-summary view for the doctor (opened by the ⛶
            button on the panel header). Carries the `dark` class so the summary's
            dark-mode styles apply inside the portal. */}
        <Modal
          isOpen={patientSummaryModalOpen}
          onClose={() => setPatientSummaryModalOpen(false)}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent
            className={effectiveTheme === 'dark' ? 'dark' : ''}
            bg={effectiveTheme === 'dark' ? '#0f1117' : 'white'}
            color={effectiveTheme === 'dark' ? 'white' : undefined}
          >
            <ModalHeader>Patient summary</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {patientSummaryModalOpen && (
                <PatientSummaryPanel
                  appointmentId={uuid}
                  schema={chartDocSchema}
                  userId={userInfo.userId}
                  role="clinician"
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>


        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: '24px', flex: 1, overflow: 'visible', padding: '0 24px', minHeight: 0 }}>
          <SectionsPanel
            quill={liveQuill}
            collapsed={sectionsCollapsed}
            onToggle={() => setSectionsCollapsed((prev) => !prev)}
          />
          <div className="ms-note-pane" style={{ flex: '1 1 auto', minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <DocumentBody
              ref={documentBodyRef}
              document={visit.document}
              readOnly={inputBlocked}
              onChange={onDocumentChange}
              bindingActive={collabActive && !safeShareMode}
            />

            {/* Portal comment bubbles and active comment cards next to the editor */}
            {liveQuill && liveQuill.container && liveQuill.container.parentNode && commentBubblePositions.map((pos) => {
              const targetContainer = liveQuill.container.parentNode;
              const isActive = activeCommentId === pos.id;
              
              return createPortal(
                isActive ? (
                  <div
                    key={pos.id}
                    id={`comment-card-${pos.id}`}
                    style={{
                      position: 'absolute',
                      top: `${pos.cardTop}px`,
                      left: `${pos.cardLeft}px`,
                      zIndex: 100,
                      width: '260px',
                      background: '#fff',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #ced4da',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#495057' }}>
                        {pos.comment.user_name || 'User'}
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolveComment(pos.comment);
                          }}
                          style={{
                            fontSize: '9px',
                            background: '#ebfbee',
                            color: '#2b8a3e',
                            border: '1px solid #b2f2bb',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            cursor: 'pointer',
                          }}
                        >
                          Resolve
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveCommentId(null);
                          }}
                          style={{
                            fontSize: '10px',
                            background: 'none',
                            color: '#868e96',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px 4px',
                          }}
                          title="Minimize"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <p style={{ fontSize: '12.5px', color: '#212529', whiteSpace: 'pre-wrap', margin: 0 }}>
                      {pos.comment.content}
                    </p>
                  </div>
                ) : (
                  <button
                    key={pos.id}
                    onClick={() => {
                      setActiveCommentId(pos.id);
                    }}
                    style={{
                      position: 'absolute',
                      top: `${pos.top}px`,
                      left: `${pos.left}px`,
                      zIndex: 10,
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: pos.comment.highlight_color || '#FEF08A',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                      border: 'none',
                      transition: 'transform 0.2s, background-color 0.2s',
                    }}
                    title={pos.comment.content}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1.0)'; }}
                  >
                    <FontAwesomeIcon icon={faComment} style={{ fontSize: '10px' }} />
                  </button>
                ),
                targetContainer
              );
            })}

            {/* Task link card — same portal target as the comment bubbles so it
                stays inside #clinic-root and scrolls with the note. */}
            {openTaskLink && taskLinksInteractive && liveQuill && liveQuill.container && liveQuill.container.parentNode && createPortal(
              <TaskLinkCard
                key={openTaskLink.taskId}
                taskId={openTaskLink.taskId}
                anchorRect={openTaskLink.rect}
                hostWidth={liveQuill.container.parentNode.clientWidth}
                currentUserId={userInfo.userId}
                onClose={() => setOpenTaskLink(null)}
              />,
              liveQuill.container.parentNode
            )}

            {/* "📋 Assign task" chip beside a highlight. onMouseDown preventDefault
                keeps the editor selection alive so the click still sees it. */}
            {canAssignTasks && taskFloatPosition && !isAddingComment && !taskAssign && liveQuill && liveQuill.container && liveQuill.container.parentNode && createPortal(
              <button
                type="button"
                className="task-float-btn"
                aria-label="Assign task from selection"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setTaskFloatPosition(null); handleAssignTaskClick(); }}
                style={{
                  position: "absolute",
                  top: `${taskFloatPosition.top}px`,
                  left: `${taskFloatPosition.left}px`,
                  zIndex: 100,
                  width: "130px",
                  padding: "4px 10px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#1d4ed8",
                  background: "#fff",
                  border: "1px solid #93c5fd",
                  borderRadius: "999px",
                  boxShadow: "0 2px 8px rgba(29, 78, 216, 0.15)",
                  cursor: "pointer",
                }}
              >
                📋 Assign task
              </button>,
              liveQuill.container.parentNode
            )}

            {/* Portal new comment input window directly to editor parent wrapper */}

            {isAddingComment && commentFloatPosition && liveQuill && liveQuill.container && liveQuill.container.parentNode && (() => {
              const targetContainer = liveQuill.container.parentNode;
              return createPortal(
                <div
                  className="comment-float-input"
                  style={{
                    position: "absolute",
                    top: `${commentFloatPosition.top}px`,
                    left: `${commentFloatPosition.left}px`,
                    zIndex: 100,
                    width: "260px",
                    background: "#fff",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #2196f3",
                    boxShadow: "0 4px 12px rgba(33, 150, 243, 0.2)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <p style={{ fontSize: "10px", color: "#6c757d", margin: 0 }}>
                    New Comment on: "{selectedText.length > 25 ? selectedText.substring(0, 22) + "..." : selectedText}"
                  </p>
                  <textarea
                    placeholder="Add a comment..."
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    autoFocus
                    style={{
                      width: "100%",
                      fontSize: "12px",
                      padding: "6px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      minHeight: "50px",
                      resize: "none",
                      outline: "none",
                    }}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                    <span style={{ fontSize: "10px", color: "#6c757d" }}>Highlight:</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {HIGHLIGHT_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedHighlightColor(color)}
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            background: color,
                            border: selectedHighlightColor === color ? "2px solid #2196f3" : "1px solid #ced4da",
                            cursor: "pointer",
                            padding: 0,
                          }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                    <button
                      onClick={() => {
                        setIsAddingComment(false);
                        setNewCommentText("");
                        setSelectedHighlightColor('#FEF08A');
                      }}
                      style={{
                        fontSize: "10px",
                        background: "none",
                        border: "none",
                        color: "#868e96",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveComment}
                      style={{
                        fontSize: "10px",
                        background: "#2196f3",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        cursor: "pointer",
                      }}
                    >
                      Comment
                    </button>
                  </div>
                </div>,
                targetContainer
              );
            })()}
          </div>
          
          {/* Document Comments Side Panel */}
          {showCommentsSidebar && (
            <div
              className="comments-sidebar shrink-0"
              style={{
                width: "300px",
                padding: "16px",
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                maxHeight: "calc(100vh - 240px)",
                position: "sticky",
                top: "20px",
                overflowY: "auto",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <h3
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#495057",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <FontAwesomeIcon icon={faComment} /> Comments
                </h3>
                <button
                  onClick={() => setShowCommentsSidebar(false)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: "14px",
                    padding: "4px"
                  }}
                  title="Hide comments"
                >
                  ✕
                </button>
              </div>

              {(() => {
                const openComments = comments.filter(c => c.status === 'open');
                const resolvedComments = comments.filter(c => c.status === 'resolved');

                const renderCommentCard = (comment) => (
                  <div
                    key={comment.id}
                    id={`comment-card-${comment.id}`}
                    onClick={() => {
                      if (comment.selection_range && liveQuill) {
                        const { index, length } = comment.selection_range;
                        liveQuill.focus();
                        let targetIndex = index;
                        let targetLength = length;
                        if (comment.highlight_text) {
                          const text = liveQuill.getText();
                          const sub = text.substring(targetIndex, targetIndex + targetLength);
                          if (sub !== comment.highlight_text) {
                            let foundIdx = text.indexOf(comment.highlight_text);
                            while (foundIdx !== -1) {
                              const format = liveQuill.getFormat(foundIdx, 1);
                              if (format && format.background) {
                                targetIndex = foundIdx;
                                targetLength = comment.highlight_text.length;
                                break;
                              }
                              foundIdx = text.indexOf(comment.highlight_text, foundIdx + 1);
                            }
                          }
                        }
                        // Scroll and center in the page viewport
                        const leaf = liveQuill.getLeaf(targetIndex);
                        if (leaf && leaf[0]) {
                          let domNode = leaf[0].domNode;
                          if (domNode.nodeType === Node.TEXT_NODE) {
                            domNode = domNode.parentElement;
                          }
                          if (domNode && typeof domNode.scrollIntoView === 'function') {
                            domNode.scrollIntoView({
                              behavior: 'smooth',
                              block: 'center',
                              inline: 'nearest'
                            });
                          }
                        }
                        setActiveCommentId(comment.id);
                      }
                    }}
                    style={{
                      background:
                        comment.status === "resolved"
                          ? "#f8f9fa"
                          : comment.id === activeCommentId
                          ? "#e7f5ff"
                          : "#fff",
                      padding: "10px",
                      borderRadius: "6px",
                      border: comment.id === activeCommentId ? "1px solid #74c0fc" : "1px solid #e9ecef",
                      opacity: comment.status === "resolved" ? 0.65 : 1,
                      boxShadow: comment.id === activeCommentId ? "0 2px 6px rgba(34, 139, 230, 0.15)" : "0 1px 2px rgba(0,0,0,0.01)",
                      position: "relative",
                      transition: "background-color 0.2s, border-color 0.2s",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "4px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: comment.status === "resolved" ? "#f1f3f5" : "#e7f5ff",
                            color: comment.status === "resolved" ? "#868e96" : "#228be6",
                            fontSize: "10px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {(comment.user_name || "A")
                            .substring(0, 1)
                            .toUpperCase()}
                        </div>
                        <div>
                          <p
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: comment.status === "resolved" ? "#868e96" : "#495057",
                            }}
                          >
                            {comment.user_name || "User"}
                          </p>
                        </div>
                        {comment.status === "resolved" && (
                          <span
                            style={{
                              fontSize: "8.5px",
                              background: "#d3f9d8",
                              color: "#2f9e44",
                              borderRadius: "10px",
                              padding: "1px 6px",
                              fontWeight: "600",
                              letterSpacing: "0.3px",
                            }}
                          >
                            ✓ Resolved
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: "2px" }}>
                        {comment.status === "open" && (
                          <ChakraButton
                            size="xs"
                            variant="outline"
                            colorScheme="green"
                            h="18px"
                            px="4px"
                            fontSize="8px"
                            onClick={(e) => { e.stopPropagation(); handleResolveComment(comment); }}
                          >
                            Resolve
                          </ChakraButton>
                        )}
                        <IconButton
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          h="18px"
                          w="18px"
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={(e) => { e.stopPropagation(); handleDeleteComment(comment); }}
                        />
                      </div>
                    </div>
                    <p
                      style={{
                        fontSize: "11.5px",
                        color: comment.status === "resolved" ? "#868e96" : "#343a40",
                        marginTop: "4px",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {comment.content}
                    </p>
                    {comment.highlight_text && (
                      <p
                        style={{
                          fontSize: "9.5px",
                          color: "#868e96",
                          fontStyle: "italic",
                          background: comment.status === "resolved" ? "#f1f3f5" : (comment.highlight_color ? `${comment.highlight_color}40` : "#f8f9fa"),
                          padding: "3px 6px",
                          borderRadius: "4px",
                          marginTop: "4px",
                          borderLeft: `2px solid ${comment.status === "resolved" ? "#ced4da" : "#ced4da"}`,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={comment.highlight_text}
                      >
                        "{comment.highlight_text}"
                      </p>
                    )}
                  </div>
                );

                if (comments.length === 0) {
                  return (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#868e96",
                        textAlign: "center",
                        marginTop: "16px",
                        lineHeight: "1.4",
                      }}
                    >
                      No comments yet.<br />Highlight text and click the comment icon in the toolbar to add one.
                    </p>
                  );
                }

                return (
                  <>
                    {/* Open comments */}
                    {openComments.length === 0 && !showResolvedHistory && (
                      <p
                        style={{
                          fontSize: "11px",
                          color: "#868e96",
                          textAlign: "center",
                          marginTop: "8px",
                          lineHeight: "1.4",
                        }}
                      >
                        No open comments.
                      </p>
                    )}
                    {openComments.map(renderCommentCard)}

                    {/* Show History toggle button */}
                    {resolvedComments.length > 0 && (
                      <button
                        onClick={() => setShowResolvedHistory(prev => !prev)}
                        style={{
                          width: "100%",
                          marginTop: "4px",
                          padding: "6px 10px",
                          background: showResolvedHistory ? "#f1f3f5" : "transparent",
                          border: "1px dashed #ced4da",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "11px",
                          color: "#868e96",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "background 0.15s, color 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#f1f3f5"; e.currentTarget.style.color = "#495057"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = showResolvedHistory ? "#f1f3f5" : "transparent"; e.currentTarget.style.color = "#868e96"; }}
                      >
                        <span style={{ fontSize: "10px" }}>
                          {showResolvedHistory ? "▲" : "▼"}
                        </span>
                        {showResolvedHistory
                          ? `Hide history (${resolvedComments.length})`
                          : `Show history (${resolvedComments.length} resolved)`
                        }
                      </button>
                    )}

                    {/* Resolved comments — shown only when history is toggled on */}
                    {showResolvedHistory && resolvedComments.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          borderTop: "1px solid #e9ecef",
                          paddingTop: "8px",
                          marginTop: "2px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "10px",
                            color: "#adb5bd",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: "600",
                            marginBottom: "2px",
                          }}
                        >
                          Resolved
                        </p>
                        {resolvedComments.map(renderCommentCard)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
        {collabActive && !safeShareMode && (
          <div className="absolute top-3 right-3 z-30 pointer-events-none">
            <CollabStatusBadge
              connected={!!collab.isConnected}
              hasConnected={!!collab.hasConnected}
              typingLocked={collabBlocksEditing}
              downMs={collab.downMs || 0}
            />
          </div>
        )}
        </>
        )}
      </main>
      {/* Both floating bubbles always sit OUTSIDE (to the left of) the
          right panel — their right offsets are computed from the current
          panel width (or the 32px collapse rail) so a drag-resize keeps
          them on the doc side. Layout from right→left:
            <panel|rail> · 16px gap · [recording bubble] · 16px gap · [AI bubble] */}
      {!patientReadOnly && (() => {
        const panelOffset = rightCollapsed ? 32 : rightWidth;      // panel/rail width in px
        const BUBBLE_SIZE = 50;                                    // match RecordingBubble's 50px
        const PANEL_GAP = 34;                                      // breathing room between mic + sidebar
        // Leejoe 2026-06-29 — Record + voice mics moved to the top white
        // bar. AI bubble now lives in the slot the red mic used to occupy
        // (RECORDING_RIGHT) so the bottom-right corner has ONE floating
        // affordance instead of two competing for attention. Future:
        // replace this AI bubble with corner expand/collapse arrows for
        // the right + left panels per Leejoe (the bubble's real job is
        // "open AI tab", not "minimize right panel"; the side-effect
        // currently overloads it).
        const RECORDING_RIGHT = panelOffset + PANEL_GAP;
        return (
          <>
            {/* RecordingBubble still mounts (hidden) so its window/popup
                event listeners keep firing for the top-bar Record Visit
                chip. The visible mic is gone. */}
            <div className="absolute bottom-6 z-30" style={{ right: `${RECORDING_RIGHT}px`, width: 0, height: 0, pointerEvents: 'none' }}>
              <RecordingBubble
                currentPatientId={visit.patient?.id || uuid}
                currentPatientData={visit.patient}
                // Same schema every other write on this chart uses. A
                // cross-schema chart's audio has to land on ITS row, not on
                // the viewer's home schema where the uuid doesn't exist.
                chartSchema={chartDocSchema}
                hidden
              />
            </div>
            {/* Floating AI bubble — moved to where the red mic used to be
                (RECORDING_RIGHT) so it doesn't crowd the note text. Same
                click behavior as before:
                  - panel collapsed OR a non-AI tab is active → expand + open AI
                  - AI tab already open → collapse the panel */}
            <button
              type="button"
              onClick={onAIBubbleClick}
              aria-label={`${rightCollapsed || rightTab !== 'ai' ? 'Open AI chat' : 'Collapse AI chat'}${agentsUnseenLabel}`}
              title={rightCollapsed || rightTab !== 'ai' ? 'Open AI chat' : 'Collapse AI chat'}
              className="absolute bottom-6 z-30 flex items-center justify-center text-white"
              style={{
                right: `${RECORDING_RIGHT}px`,
                width: `${BUBBLE_SIZE}px`,
                height: `${BUBBLE_SIZE}px`,
                borderRadius: '9999px',
                background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 50%, #4f46e5 100%)',
                boxShadow: '0 12px 32px -8px rgba(59,130,246,0.55), 0 0 0 6px rgba(59,130,246,0.10)',
                border: 'none',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6 10l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zM5 14l.8 2.4L8 17l-2.2.6L5 20l-.8-2.4L2 17l2.2-.6L5 14z" />
              </svg>
              {/* Unseen agent updates (the count is in the bubble's label too). */}
              {agentsUnseen > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 dark:bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center"
                >
                  {agentsUnseenText}
                </span>
              )}
            </button>
          </>
        );
      })()}
      {/* Drag-to-resize splitter between the document and the right panel.
          Hidden while the panel is collapsed (there's nothing to resize). */}
      {!patientReadOnly && !rightCollapsed && !isNarrow && (
        <ResizeSplitter
          side="right"
          minWidth={280}
          // Two caps, whichever's tighter:
          //   1. windowWidth − 320 — keeps the doc area visible at all.
          //   2. maxRightWidth — set by the overflow detector above the
          //      moment a drag pushes header/toolbar past its natural width.
          //      This is the hard wall the user feels while dragging; the
          //      splitter clamps to it before calling onResize, so further
          //      drag motion just doesn't translate to a width change.
          //      Cap auto-resets on patient switch / window resize / panel
          //      collapse so a new layout can find its own ceiling.
          maxWidth={Math.min(
            typeof window !== 'undefined' ? window.innerWidth - 320 : 1600,
            maxRightWidth,
          )}
          onResize={(w) => {
            // First manual drag takes over from the auto-fit: release the cap
            // so the user can size freely within the windowWidth−320 bound, and
            // stop the overflow detector from snapping the panel back.
            if (!userResizedRef.current) {
              userResizedRef.current = true;
              setMaxRightWidth(Infinity);
            }
            setRightWidth(w);
          }}
        />
      )}
      {/* Backdrop for narrow screen right sidebar overlays */}
      {!patientReadOnly && isNarrow && !rightCollapsed && (
        <div
          onClick={() => setRightCollapsed(true)}
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-xs transition-opacity duration-200"
          style={{ backdropFilter: 'blur(2px)' }}
        />
      )}
      {/* Always mount the right panel so AI chat history / source-chip
          toggles / pinned bar survive a collapse-expand cycle. The panel
          self-hides via the `hidden` prop (display:none) while the rail
          (below) takes its place visually. */}
      {!patientReadOnly && (
        <MedStudentRightPanel
          patient={visit.patient}
          patientId={uuid}
          schema={chartDocSchema}
          documentContent={documentToText(visit.document)}
          documentPlainRef={aiDocumentPlainRef}
          userRole={userInfo.role || "medstudent"}
          scheduledDate={visit.patient?.date_ || date}
          userInitials={userInfo.initials}
          onCollapse={() => setRightCollapsed(true)}
          hidden={rightCollapsed}
          width={rightWidth}
          activeTab={rightTab}
          onTabChange={setRightTab}
          injectPrompt={injectPrompt}
          isNarrow={isNarrow}
          onSettingsSaved={handleAiSettingsSaved}
          skillSync={skillSync}
          activeSkill={aiChatSettings.activeSkill}
          skillIntensity={aiChatSettings.skillIntensity}
          onSkillChange={handleAiSkillChange}
          processDiffPanelSlotRef={processDiffPanelSlotRef}
          // A collapsed panel is only hidden (display:none), not unmounted, so
          // a doctor who chose Copilot and then collapsed the panel still has it
          // mounted. Asking it for the diff slot then would portal Process into
          // a box nobody can see; with no slot, ProcessModal uses its floating
          // window instead.
          processDiffInPanelMode={isProcessOpen && !rightCollapsed}
          parkedReview={
            // PHI guard — a parked Master Note only shows in the chart it was
            // generated for; hidden (not applied) when viewing another patient.
            parkedReview && parkedReview.originPatientId != null
            && String(parkedReview.originPatientId) !== String(uuid)
              ? null
              : parkedReview
          }
          parkedApplying={parkedApplying}
          onParkedApprove={parkedOnApprove}
          onParkedDeny={parkedOnDeny}
          onParkedEditSave={parkedOnEditSave}
          onParkedApproveRest={parkedOnApproveRest}
          onParkedRejectAllClose={parkedOnRejectAllClose}
          onParkedApply={parkedOnApply}
          onParkedDismiss={parkedOnDismiss}
          onParkedUnpark={handleUnparkReview}
          onParkedToggleItem={parkedToggleItem}
          onParkedToggleGroup={parkedToggleGroup}
          onParkedToggleExpand={parkedToggleExpand}
          onParkedToggleTemplate={parkedToggleTemplate}
          onParkedResetSelection={parkedResetSelection}
          onParkedSelectOnlyNew={parkedSelectOnlyNew}
          onParkedClearAll={parkedClearAllSelection}
          onParkedGenerate={parkedGenerate}
          sourcesLoading={sourcesLoading}
          isGenerating={isGenerating}
          generationStartedAt={generationStartedAt}
          userId={userInfo.userId}
          agentsState={agentsAllowed ? agentsState : null}
        />
      )}
      {/* Patient portal right panel — the patient-facing counterpart of the
          clinic panel above: Reports + an AI assistant that runs over the
          patient-safe SUMMARIES, never the raw note or transcripts. Mounted
          HERE and not in PatientPage.js because /visit/:uuid routes to this
          page for every role, patients included (PatientPage is not on that
          route at all). `provideClinicRoot={false}` — this page already
          carries id="clinic-root", and a nested duplicate would be invalid
          HTML and shadow the portal target the clinic modals look up. */}
      {patientReadOnly && (
        <PatientRightPanel
          appointmentId={uuid}
          patientFirstName={
            userInfo.patientUserRow?.patient_first_name ||
            String(userInfo.name || '').trim().split(/\s+/)[0] ||
            ''
          }
          patientName={
            userInfo.patientUserRow?.patient_full_name ||
            userInfo.name ||
            visit.patient?.patient_name ||
            ''
          }
          userInitials={userInfo.initials}
          role={userInfo.role}
          provideClinicRoot={false}
        />
      )}
      {!patientReadOnly && rightCollapsed && (
        <button
          type="button"
          onClick={() => setRightCollapsed(false)}
          aria-label={`Expand AI panel${agentsUnseenLabel}`}
          title="Expand AI panel"
          className="w-8 shrink-0 border-l border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] hover:bg-slate-50 dark:hover:bg-slate-800/40 flex flex-col items-center pt-4 gap-2 text-slate-400 dark:text-slate-500"
        >
          {/* Left-pointing chevron — re-expands the right panel */}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {/* Unseen agent updates (the count is in the rail's label too). */}
          {agentsUnseen > 0 && (
            <span
              aria-hidden="true"
              className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 dark:bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {agentsUnseenText}
            </span>
          )}
        </button>
      )}

      {/* Forms modal — fully wired */}
      {!patientReadOnly && isFormsOpen && (
        <FormsModal
          isOpen={isFormsOpen}
          onClose={() => setIsFormsOpen(false)}
          patientId={visit.patient?.id}
          patientData={visit.patient}
        />
      )}

      {/* Doctor-side medication manager. Mirrors the patient's /meds page
          so the doctor's manual add/remove and the "Add meds from latest
          visit" LLM flow both write to the same `patients_with_ehrid.meds`
          store the patient reads from. `currentVisitOverride` carries the
          path/schema/visit of the chart on screen + a getter for the live
          Quill instance so a scan that races the autosave debounce still
          sees the just-typed meds. The endpoint has its own
          in-memory-YDoc fallback (via the collab rooms registry) but the
          live override is cheaper and always wins when no other viewer is
          connected, so we send it. */}
      {!patientReadOnly && isClinicMedsOpen && visit.patient?.id && (
        <ClinicPatientMedsModal
          patient={visit.patient}
          onClose={() => setIsClinicMedsOpen(false)}
          // Opening the 💊 Meds button in the chart header now also fires
          // "Add meds from visits" automatically — same backend call, same
          // dedup, same per-med source-date attribution. The doctor no
          // longer has to click twice; if they want to re-scan they press
          // the in-modal button (no-op on a fresh list, dedup on repeats).
          autoScan={true}
          currentVisitOverride={{
            // The notes-c row uuid. `usePatientDocument` returns
            // `{ patient, document, isLoading, error, refetch }` — there is
            // no `.id` on `visit` itself; the row's uuid is `visit.patient.id`
            // (= the URL param `uuid`) and that's the same value the
            // scan endpoint's `_find_visits_for_chart` returns for the
            // current visit. The previous version passed `visit.id` which
            // was `undefined`, so the backend silently dropped the override
            // and the scan read the stale `.json` / `.yjs` only — which is
            // exactly why "I just typed this and the scan doesn't see it"
            // was the symptom.
            visitId: visit.patient.id || uuid,
            path: documentPath,
            schema: chartDocSchema,
            getQuill: () => liveQuill,
          }}
        />
      )}

      {/* Assign task from a note selection (toolbar button). On success the
          selected text becomes a task link (TASK_LINK_FORMAT). */}
      {taskAssign && (
        <AssignModal
          isOpen
          onClose={() => setTaskAssign(null)}
          currentUser={{
            id: userInfo.userId,
            name: userInfo.name,
            email: userInfo.email,
            role: userInfo.role,
            schema: chartDocSchema,
          }}
          defaultTab={0}
          initialTitle={taskAssign.prefill.title}
          initialDescription={taskAssign.prefill.description}
          taskSource={{
            source_doc_path: documentPath,
            source_doc_schema: chartDocSchema,
            source_patient_name: visit.patient?.patient_name || null,
            source_visit_date: (String(visit.patient?.date_ || '').match(/^\d{4}-\d{2}-\d{2}/) || [null])[0],
          }}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {/* Orders & followups — read-only. Rows come from
          `clinical_action_items`, which the copilot already fills on every
          recording save. `chartDocSchema` is passed explicitly because the
          orders endpoints default to `public` when no schema is supplied,
          which is why existing rows skew to that schema. */}
      {!patientReadOnly && canOrdersAction && isClinicOrdersOpen && visit.patient?.id && (
        <ClinicPatientOrdersModal
          patient={visit.patient}
          currentUserId={userInfo.userId}
          schema={chartDocSchema}
          visitDate={visit.patient?.date_}
          // Printed under the mark on anything signed here. Same fallback
          // chain the collab presence name uses.
          signerName={userInfo.name || userInfo.email || ''}
          currentUserName={userInfo.name || userInfo.email || ''}
          onClose={() => {
            setIsClinicOrdersOpen(false);
            clearOrdersLink();
          }}
          // A new link while Orders is open starts it afresh on that form.
          key={ordersLink?.key || 'orders'}
          initialTab={ordersLink?.tab || null}
          openPaperworkId={ordersLink?.paperworkId || null}
          // Opening the button also reads the note's ORDERS block, so orders
          // the clinician just typed land on the queue without a second click.
          // The scan dedups server-side, so reopening the chart is cheap in
          // rows even though it costs an LLM call.
          autoScan={true}
          currentVisitOverride={{
            visitId: visit.patient.id || uuid,
            path: documentPath,
            schema: chartDocSchema,
            getQuill: () => liveQuill,
          }}
        />
      )}
      {/* A paperwork task's link, for someone without the Orders button: the
          form they were handed, in the editor, to fill in and save. Closing it
          leaves the chart too — they came for the form, not the visit note. */}
      {!patientReadOnly && !canOrdersAction && ordersLink?.paperworkId && visit.patient?.id && (
        <PaperworkLinkEditor
          patientId={visit.patient.id}
          paperworkId={ordersLink.paperworkId}
          onClose={() => {
            setIsClinicOrdersOpen(false);
            leaveOrdersLink();
          }}
        />
      )}

      {/* Copy-from-older confirm dialog. Mirrors EditableDocumentPage's
          search → confirm flow; the search itself runs in onCopyOlder and
          stores its result in copyOlderMatch. */}
      {!patientReadOnly && copyOlderOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {copyOlderAuto ? 'Copy from your last visit?' : 'Copy from older session'}
              </h2>
            </div>
            {copyOlderLoading ? (
              <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Searching prior visits…
              </div>
            ) : copyOlderMatch ? (
              <>
                <p className="text-sm text-slate-700 dark:text-slate-200 mb-1">
                  Last populated document found on{' '}
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-50">
                    {copyOlderMatch.row.date_}
                  </span>
                  {copyOlderMatch.row.type ? (
                    <span className="ml-1 text-slate-500 dark:text-slate-400">
                      ({copyOlderMatch.row.type})
                    </span>
                  ) : null}
                  .
                </p>
                {/* Who the copy is actually from. The search now requires a
                    name match (and an EHR ID match when both rows carry a real
                    one), but the user still gets to see which chart it landed
                    on before overwriting anything. */}
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
                  {formatPatientLabel(copyOlderMatch.row)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {copyOlderAuto
                    ? 'This note is still the blank template, so nothing you have written will be lost. You can edit the copied content afterwards.'
                    : 'Replacing will overwrite the current document with the contents of that visit. This cannot be undone (use Version History to recover).'}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeCopyOlder}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    {copyOlderAuto ? 'Start with a new template' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyOlderConfirm}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 hover:bg-blue-500 text-white shadow-sm"
                  >
                    {copyOlderAuto ? 'Copy it over' : 'Replace with older'}
                  </button>
                </div>
              </>
            ) : copyOlderAuto ? null : (
              <>
                <p className="text-sm text-slate-700 dark:text-slate-200 mb-4">
                  No previous visit document was found for{' '}
                  <span className="font-semibold">{visit.patient?.patient_name || 'this patient'}</span>.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeCopyOlder}
                    className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 hover:bg-blue-500 text-white shadow-sm"
                  >
                    OK
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reload-template picker. Rendered INSIDE the page tree, never portaled
          to document.body — Tailwind utilities are scoped to #clinic-root and
          a portaled copy would render invisible. */}
      {!patientReadOnly && reloadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-2xl p-5">
            <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-3">Reload template</h2>
            <label
              htmlFor="reload-template-select"
              className="block text-xs text-slate-500 dark:text-slate-400 mb-1"
            >
              Template
            </label>
            <select
              id="reload-template-select"
              aria-label="Template"
              value={reloadCode}
              onChange={(e) => setReloadCode(e.target.value)}
              className="w-full mb-3 px-2 py-1.5 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            >
              {withStaleOption(reloadOptions, reloadCode).map((o) => (
                <option key={o.code} value={o.code}>{o.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              This replaces the current document and saves the choice on this visit.
              Use Version History to recover the previous content.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReloadOpen(false)}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReloadConfirm}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 hover:bg-blue-500 text-white shadow-sm"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history modal — mirrors EditableDocumentPage/DocumentNavBar wiring.
          onVersionRestored receives a Quill delta and pushes it into our editor
          via the same documentBodyRef the toolbar uses, then queues a save. */}
      {isVersionsOpen && (
        <VersionHistoryModal
          isOpen={isVersionsOpen}
          onClose={() => { setIsVersionsOpen(false); refreshVersionCount(); }}
          documentPath={documentPath}
          userRole={userInfo.role || "medstudent"}
          documentReadOnly={effectiveReadOnly}
          versioningSystem={versioningSystemRef.current}
          onVersionRestored={patientReadOnly ? undefined : async (content, envelope) => {
            const editor = documentBodyRef.current?.getEditor?.(0);
            if (!editor || !content) return;
            // Yjs-aware apply — see performRestoreLatest comment for why
            // plain setContents double-stacks when collab is on.
            const yText = collabActive ? collab?.getYText?.() : null;
            const yDoc = collabActive ? collab?.getYDoc?.() : null;
            const ops = Array.isArray(content) ? content : (content.ops || []);
            if (yText && yDoc?.transact) {
              yDoc.transact(() => {
                if (yText.length > 0) yText.delete(0, yText.length);
                yText.applyDelta(ops);
              });
            } else {
              editor.setContents([{ insert: '\n' }], 'api');
              editor.setContents(content, 'api');
            }
            // Mirror performRestoreLatest — time-travel the
            // processedSources ledger to whatever was active when this
            // version was saved. The modal now passes the full envelope
            // as a second arg (the legacy single-arg shape would skip
            // this branch and leave the in-memory ledger untouched,
            // which is also acceptable — at worst the badge is stale).
            if (envelope && Array.isArray(envelope.processedSources)) {
              processedSourcesRef.current = envelope.processedSources;
              setProcessedLedger(envelope.processedSources);
            }
            pendingDeltaRef.current = editor.getContents();
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(persistDelta, 250);
            refreshVersionCount();
          }}
        />
      )}

      {/* Restore-latest confirmation — fired from the header "↻ Restore"
          button. Quick yes/no path; the full version list is still reachable
          from the Tools menu's "Version history" entry (onVersionHistory). */}
      {!patientReadOnly && isRestoreConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isRestoring) {
              setIsRestoreConfirmOpen(false);
            }
          }}
        >
          <div className="bg-white dark:bg-[#0a0a0f] rounded-xl shadow-xl border border-slate-200 dark:border-slate-800/60 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Restore last saved version?
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
              This rolls the document back to its most recent saved version.
              Any unsaved edits made since then will be replaced.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsRestoreConfirmOpen(false)}
                disabled={isRestoring}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={performRestoreLatest}
                disabled={isRestoring}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white bg-gradient-to-br from-cyan-500 to-sky-600 hover:from-cyan-400 hover:to-sky-500 shadow-sm shadow-cyan-500/25 disabled:opacity-50"
              >
                {isRestoring ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reports modal — fired from the IconRail "Reports" (document) icon */}
      <MedStudentReportsModal
        isOpen={isReportsOpen}
        onClose={() => setIsReportsOpen(false)}
        patient={visit.patient}
        readOnly={patientReadOnly}
      />

      {/* Audios modal — opened from the header "🎧 Audios" button. Rendered
          inline (NOT portalled) so Tailwind's #clinic-root scope reaches it. */}
      {!patientReadOnly && (
        <ClinicAudiosModal
          isOpen={isAudiosOpen}
          onClose={() => setIsAudiosOpen(false)}
          patient={visit.patient}
          schema={chartDocSchema}
          theme={effectiveTheme}
          onOpenAudioManager={() => setIsAudioManagerOpen(true)}
        />
      )}

      {/* Prior visits modal — fired from the IconRail clock icon. Lists every
          earlier notes-c row for this patient (matched on EHR_ID first, then
          patient_name) and lets the clinician preview the stored note inline
          or jump straight to that day's chart. */}
      <PriorVisitsModal
        isOpen={isPriorVisitsOpen}
        onClose={() => setIsPriorVisitsOpen(false)}
        patient={visit.patient}
        currentUuid={uuid}
        schema={chartDocSchema}
      />

      {/* Audio Manager modal — opened from the Tools dropdown in PatientHeaderBar */}
      <AudioManagerModal
        isOpen={isAudioManagerOpen}
        onClose={() => setIsAudioManagerOpen(false)}
        patientData={{ ...(visit.patient || {}), more_audios: visit.patient?.more_audios || [] }}
        patientId={visit.patient?.id}
        onDeleteAudio={patientReadOnly ? undefined : handleDeleteAudio}
        onRenameAudio={(patientReadOnly || rosterMode) ? undefined : handleRenameAudio}
        onRestoreFromBackupSuccess={visit.refetch || (() => {})}
        readOnly={patientReadOnly}
      />

      {/* Task #178 — Send copy to PCP? modal, opened from Sign & Save.
          v1 stub: AI-drafts a referral-back letter + "send" toast. Backend
          wiring (Twilio fax + SendGrid email + audit log) is Task #178 v2. */}
      <SendToPcpModal
        isOpen={isPcpModalOpen}
        onClose={() => setIsPcpModalOpen(false)}
        patient={visit.patient}
        getNoteText={() => {
          const editor = documentBodyRef.current?.getEditor?.(0);
          return editor?.getText?.() || '';
        }}
      />

      <SignAndSaveModal
        isOpen={isSignModalOpen}
        onClose={onSignModalClose}
        mode={signModalMode}
        defaultSignature={userInfo.name || ''}
        password={revealPassword}
        signatureText={revealSignatureText}
        signerName={signedBy || userInfo.name || ''}
        isBusy={isSigning}
        onConfirmSign={performSignAndLock}
      />

      <UnlockNoteModal
        isOpen={isUnlockOpen}
        onClose={() => { setIsUnlockOpen(false); setUnlockError(''); }}
        onSubmit={tryUnlock}
        error={unlockError}
      />

      {/* Process modal — sources → AI structured edits → inline diff → apply.
          Fired from the "Process" button in PatientHeaderBar. The
          review-screen header carries a "Park in sidebar" button that calls
          onParkReview; the modal closes and the diff surfaces as its own
          "Review" tab in the right sidebar (alongside AI Chat / Reports /
          Live Transcript / Copilot). */}
      <ProcessModal
        isOpen={isProcessOpen}
        onClose={() => {
          setIsProcessOpen(false);
          // Clear the pin + auto-generate flag so the next open (e.g. via top-bar
          // Process button) resumes default unprocessed-auto-select behavior.
          // Without this, a stale pin would silently override defaults on
          // subsequent opens.
          setProcessPinAudioSessionId(null);
          setProcessAutoGenerate(false);
        }}
        patient={visit.patient}
        patientId={uuid}
        userRole="medstudent"
        documentText={collabActive ? (aiDocumentPlainRef.current || '') : (documentToText(visit.document) || '')}
        liveTranscript={liveTranscriptText}
        groups={sourceGroups}
        loading={sourcesLoading}
        onApplyEdits={handleApplyProcessEdits}
        onReplaceNote={handleReplaceNoteFromMasterNote}
        onTemplateOff={handleTemplateOff}
        aiRequestSettings={processAiRequestSettings}
        activeSkill={aiChatSettings.activeSkill}
        skillIntensity={aiChatSettings.skillIntensity}
        onSkillChange={handleAiSkillChange}
        pinAudioSessionId={processPinAudioSessionId}
        pinReportPath={processPinReportPath}
        autoGenerate={processAutoGenerate}
        diffPanelSlotRef={processDiffPanelSlotRef}
        onParkReview={handleParkReview}
        unparkState={unparkState}
        onUnparkConsumed={handleUnparkConsumed}
        hasParkedReview={!!parkedReview}
        parkedPhase={parkedReview?.phase || null}
        isGenerating={isGenerating}
        generationStartedAt={generationStartedAt}
        onGenerationBegin={beginGeneration}
        onGenerationEnd={endGeneration}
        documentPath={patientReadOnly ? null : documentPath}
        savedDiff={savedMasterDiff.savedDiff}
        runningRun={savedMasterDiff.runningRun}
        failedRun={savedMasterDiff.failedRun}
        onSavedRunRefresh={savedMasterDiff.refresh}
        onAcceptedMasterNote={handleAcceptedMasterNote}
        onOpenSavedDiff={() => {
          // Same stamp the Generate Master Note button makes. A saved diff is
          // read from THIS document's own folder, so the open chart is its
          // origin by construction — without re-stamping, a Master Note
          // generated earlier in another chart would still own the ref and
          // masterNoteChartOk would refuse to apply this one.
          masterNoteOriginRef.current = { id: uuid, name: visit?.patient?.patient_name || 'patient' };
        }}
        onDiscardSavedDiff={savedMasterDiff.discard}
        savedDiffError={savedMasterDiff.error}
      />

      </div>
    </div>
  );
}
